import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  MessageSquare, LogOut, Shield, ShieldCheck,
  HardDrive, ChevronRight, Upload, X, Loader2,
  CheckCircle2, AlertTriangle, Menu, Smartphone, Link2,
  ExternalLink, Settings, KeyRound, Eye, EyeOff, Fingerprint, Trash2, Plus,
  Headphones, Download, Clock, FolderOpen, MessageCircle,
  Send, FilePenLine, Cloud, Info, FileSignature, FileSpreadsheet,
  PanelRightClose, PanelRightOpen, Maximize2, Minimize2,
} from 'lucide-react';
import { CustomerRecord } from './CustomerLogin';
import {
  ARKO_CONTRACT_GENERATE_URL,
  isContractGeneratorEnabled,
  ARKO_SCHREIBEN_PDF_URL,
  ONLYOFFICE_DOCUMENT_SERVER_URL,
  ONLYOFFICE_ISSUE_JWT_URL,
  ONLYOFFICE_JWT_BRIDGE_TOKEN,
  ONLYOFFICE_CALLBACK_URL,
  buildOnlyofficeEditorCustomization,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  portalAuthzSidecarExpected,
  PORTAL_DOCUMENT_UPLOAD_URL,
  PORTAL_DOCUMENT_FOLDER_URL,
  PORTAL_NEXTCLOUD_SHARE_IMPORT_URL,
  PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL,
  PORTAL_SYNTHESIS_APPEND_URL,
  PORTAL_PLAINTEXT_FETCH_URL,
  getPortalNextcloudShareAllowedHosts,
  validateNextcloudSharePasteUrl,
  getPortalArkoWebhook,
  getMemcoreCuratorUrl,
  getPortalNcMandantenRoot,
  getMandantenRootFromNextcloudPath,
  getPortalTenantN8nExtras,
  isIsolatedDemoPortalTenant,
  isMemcoreEnabled,
  getPortalPocketBaseUrl,
  persistPortalPbSession,
  getPortalPbToken,
  isPbAuthRefreshNonRenewable,
  isLaborPortalHostname,
  PORTAL_LITIGATION_UPLOAD_URL,
  PORTAL_PROTOKOLLE_COLLECTION,
  resolveOnlyofficeIssueJwtUrl,
  BRAND_PORTAL,
} from '../constants';
import PortalArkoChatWorkspace, { PORTAL_ARKO_BACKGROUND_ANALYSIS_TIMEOUT_MS } from './PortalArkoChatWorkspace';
import { ARKO_OPEN_CONTRACT_CREATOR_EVENT } from './contract/contractChatFlow';
import { getContractTemplate } from './contract/contractTemplates';
import { PORTAL_SHELL } from './portal/portalTheme';
import {
  type PortalOnlyOfficeTheme,
  readPortalOnlyOfficeTheme,
  resolveOnlyofficeUiTheme,
  writePortalOnlyOfficeTheme,
} from './portal/portalOnlyofficeTheme';
import { OnlyofficeThemeSwitch } from './portal/OnlyofficeThemeSwitch';
import { PortalShell } from './portal/PortalShell';
import { GLOCKE_SPRUNG_EVENT, type GlockeSprung } from './portal/portalGlockeSprung';
import { PortalLightSurface } from './portal/portalAppearanceContext';
import { portalFeedbackSurfaceClass, portalTastenSurfaceClass } from './portal/portalAppearance';
import { PortalShortcutBelegung } from './portal/PortalShortcutBelegung';
import { HomeView } from './portal/views/HomeView';
import { KalenderView } from './portal/views/KalenderView';
import { SuchePaneBoundary, SucheView } from './portal/views/SucheView';
import type { ArkoHubMode } from './portal/arkoHubModes';
import { DiktatPaneBoundary, DiktatView } from './portal/views/DiktatView';
import { portalMatrixHomeserverUrl } from './portal/portalMatrixOrigins';
import { DiktatAnalysisIsland, type DiktatAnalysisStart } from './portal/DiktatAnalysisIsland';
import { PortalBackgroundJobsProvider } from './portal/portalBackgroundJobsContext';
import { readableAnalysisDocument } from './portal/portalDocumentRowAnalysis';
import {
  assessElementDiktatLoad,
  probeMatrixHomeserverReachable,
  attachDiktatPersistFromProtokoll,
  buildDiktatAnalysisDocumentContext,
  buildDiktatArchiveEntry,
  DIKTAT_UPLOAD_NO_TEXT,
  diktatAnalysisStartDecision,
  diktatArchiveHasFileHandle,
  diktatDokumenteCtaKind,
  diktatFileKind,
  diktatSuccessArchiveMessage,
  diktatSurfaceCopyBlocks,
  diktatUsableTranscript,
  diktatIntoRememberedAkteId,
  diktatIntoRememberedAkteFolder,
  hydrateDiktatArchiveFromProtokolle,
  mergeDiktatArchive,
  pickDiktatTranscript,
  resolveDiktatOutputTranscript,
  type DiktatArchiveEntry,
  type DiktatOutputFormat,
} from './portal/portalDiktatFlow';
import { DokumenteView } from './portal/views/DokumenteView';
import { PortalArkoChatPane } from './portal/PortalArkoChatPane';
import { FristApprovalProvider } from './portal/verfahrensmotor/FristApprovalContext';
import { FristApprovalDocumentMirror } from './portal/verfahrensmotor/FristApprovalDocumentMirror';
import { decidePortalOnlyofficeSurface } from './portal/portalOnlyofficeSurface';
import { deriveAkteNcPathFromDocumentNcPath } from './portal/verfahrensmotor/portalFristen';
import { buildAnalysisDocumentBody, fetchStoredDiktatTranscript, materializeDiktatOutputFormat, prepareAudioAnalyzeDocumentContext } from './portal/portalAudioAnalyze';
import { buildDocumentExtractFingerprint, markDocumentAnalyzeDone } from './portal/portalDocumentAnalyzeGuard';
import { PORTAL_ARKO_LLM_WINDOW_CHARS } from './portal/portalBackgroundAnalysisChunks';
import {
  appendParagraphMapToDocumentContext,
  buildParagraphIndexMap,
} from './portal/portalParagraphMap';
import { MatrixView } from './portal/views/MatrixView';
import { AkteArkoChatMount, AkteArkoHostProvider } from './portal/portalAkteArkoHost';
import { aktenNavShouldShowFallliste, portalAkteHasArko, recallPortalAkteRahmenOpen } from './portal/portalAkteRahmen';
import { endElementBrowserSession } from './portal/portalMatrixOrigins';
import { clearSeatLoginTokens } from './portal/elementSeatInvite';
import { DokumenteAreaTabs } from './portal/views/DokumenteAreaTabs';
import { DocumentTabsBar } from './portal/views/DocumentTabsBar';
import { SettingsPanel } from './portal/views/SettingsPanel';
import { PdfToolsView } from './portal/views/PdfToolsView';
import { BausteinView } from './portal/views/BausteinView';
import {
  bausteinAnfrageBleibt,
  bausteinAnfrageBrief,
  bausteinZeileAnfrage,
  bausteinDarfBestellen,
  bausteinKategorienNachBestellung,
  bausteinKategorienNachKuendigung,
  bausteinKuendigungDarfSchreiben,
  sendeBausteinAnfrageMail,
  filterNavItemsForSeat,
  portalViewAllowed,
  seatEntersBaustein,
  seatInputFromCustomer,
} from './portal/portalBausteinSitz';
import {
  type PortalView,
  type DiktatTab,
  type DokumenteAreaTab,
  type PortalHomeRecentItem,
  loadPortalSessionState,
  savePortalSessionState,
  clearPortalSessionState,
  canRestorePortalView,
  buildPortalHomeRecents,
  getPrimaryNavItems,
} from './portal/portalNavigation';
import {
  applyPortalViewHistory,
  canonicalizePortalView,
  portalHistoryRecordMode,
  readPortalViewFromLocation,
} from './portal/portalViewHistory';
import {
  formatConnectionError,
  formatHttpStatusForUser,
  formatOnlyofficeJwtErrorForUser,
  formatPortalUploadError,
  logPortalError,
  logPortalExtractOutcome,
} from './portal/portalErrorMessages';
import {
  portalOnlyofficeRateLimitBlocked,
  portalOnlyofficeRateLimitClearInsufficient,
  portalOnlyofficeRateLimitInsufficientBlocked,
  portalOnlyofficeRateLimitKey,
  portalOnlyofficeRateLimitMark429,
  portalOnlyofficeRateLimitMarkInsufficient,
  portalOnlyofficeRateLimitRemainingMs,
} from './portal/portalOnlyofficeRateLimit';
import {
  type PortalProtokoll,
  type PortalRecentDocument,
  loadRecentPortalDocuments,
  saveRecentPortalDocument,
  clearRecentPortalDocumentsWhenServerEmpty,
  mapPbRecordToProtokoll,
  resolveProtokollDocumentUrl,
  isPortalDocxForEditor,
  isActivePortalDocumentRecord,
  buildPortalDocumentsPbFilter,
  PORTAL_DOCUMENTS_PB_LIST_SORT,
  PORTAL_DOCUMENTS_PB_LIST_PER_PAGE,
  shouldFetchNextPortalDocumentPage,
  fetchPortalProtokollById,
  bustPortalDocumentDownloadUrl,
  mergeProtokollIntoList,
  protokollFromIngestFields,
  type PortalDocumentOpenFields,
  type PortalOpenDocumentMeta,
  type PortalSelectedDocumentPayload,
  buildAkteInventoryDocumentContext,
  boundAkteFocusExtractIfSelected,
  PORTAL_AKTE_INVENTORY_MAX_DOCS,
  PORTAL_MAX_OPEN_DOCUMENTS,
  PORTAL_SYNTHESIS_MAX_SELECTED_DOCS,
  parsePortalDocumentN8nResponse,
  portalDocumentRelativeFolder,
  canOpenDocumentInPortal,
  resolvePortalExtractFileType,
  isLikelyBlankPortalDocumentName,
  ensurePortalDocxFileName,
  pbRecordIdFromPortalDocumentId,
  portalDocumentOpenFieldsFromProtokoll,
  portalSelectedDocumentFromMeta,
  renamePortalDocumentViaFolderApi,
  movePortalDocumentViaFolderApi,
  createPortalFolderViaApi,
  sanitizePortalFileName,
  applyPortalDocumentRenameToProtokolle,
  applyPortalDocumentRenameToRecent,
  persistPortalDocumentRenameShortcuts,
} from './portal/portalDocuments';
import {
  listPortalAktenForCustomer,
  loadPortalBoundAkte,
  effectivePortalAkteNcPath,
  portalDokumentBelongsToAkte,
  portalOpenMetaBelongsToAkte,
  savePortalBoundAkte,
  toPortalBoundAkte,
  resolveAkteFolder,
  akteExistingFolder,
  updatePortalAkteNcPath,
} from './portal/portalAkten';
import type { PortalAkteRecord, PortalBoundAkte } from './portal/portalAktenTypes';
import { portalAkteAssignTargetFolder, portalAkteNcPathFromMovedDocument } from './portal/portalAkteDocumentActions';
import {
  capAkteExtractDocumentContext,
  akteSelectedIdsNeedingExtract,
  buildAkteDocumentRevFingerprint,
  buildAkteLazyExtractDocumentContext,
  buildFailedAkteLazyExtractContext,
  buildPendingAkteLazyExtractContext,
  buildRecognizingAkteLazyExtractContext,
  dropAkteExtractCacheEntries,
  fetchAkteDocumentExtractText,
  hasUsableAkteExtract,
  hydrateAkteExtractCacheFromPersisted,
  isAkteDocumentRevFingerprintReady,
  isFailedAkteLazyExtractContext,
  isAkteLazyExtractHttpFailure,
  isPendingAkteExtractHttpResult,
  isPendingAkteLazyExtractContext,
  isRecognizingAkteLazyExtractContext,
  isSoftAkteExtractHttpStatus,
  loadAkteExtractCacheStore,
  preferRicherAkteDocumentContext,
  preferLongerAkteExtract,
  akteExtractSourceLabel,
  saveUsableAkteExtractToCache,
  waitForAkteDocumentExtractJob,
  expireStaleRecognizingAkteDocumentContext,
  isStaleRecognizingAkteLazyExtractContext,
  PORTAL_AKTE_LAZY_EXTRACT_TIMEOUT_MS,
  PORTAL_AKTE_OCR_POLL_MAX_MS,
} from './portal/portalAkteLazyExtract';
import { applySynthesisWithTarget } from './portal/portalSynthesisApply';
import { createBlankDocxFile } from './portal/blankDocx';
import ContractGeneratorView from './contract/ContractGeneratorView';
import MemoryView from './portal/views/MemoryView';
import { MemoryOnboardingDialog } from './portal/MemoryDialogs';
import { isMemcoreOnboardingDone, setMemcoreOnboardingDone } from './portal/memcore';
import { WEBAUTHN_URL, portalAuthHeaders, registerPasskey } from './portal/webauthnClient';
import {
  fetchIdAustriaStatus,
  fetchIdAustriaLink,
  startIdAustriaLink,
  unlinkIdAustria,
} from './portal/idAustriaClient';
import { MustChangePasswordOverlay } from './portal/MustChangePasswordOverlay';
import { KanzleiZugaengeView } from './portal/views/KanzleiZugaengeView';
import { LitigationView, type LitigationSummary } from './portal/views/LitigationView';
import { VertragsQAView, type VertragQAResult } from './portal/views/VertragsQAView';
import { canManageTeamAccess, canWritePortalDocuments, buildCustomerRecord, persistPortalCustomer } from './portal/portalCustomerAccess';
import {
  FEEDBACK_KATEGORIEN,
  FEEDBACK_SYMPTOMS,
  FEEDBACK_SYMPTOM_LABELS,
  nowClientSubmittedAtIso,
  submitCustomerFeedback,
  type FeedbackKategorie,
  type FeedbackSymptom,
} from './portal/portalCustomerFeedback';
import { FeedbackDiagCodeLine } from './portal/FeedbackDiagCodeLine';

/** Öffentliche Demo-DOCX — der Document Server lädt diese URL serverseitig. Nextcloud als kanonische Quelle später. */
const ONLYOFFICE_ALPHA_DEMO_DOCUMENT_URL =
  'https://file-examples.com/storage/fe1017f14df9e33ec500cc8/2017/02/file-sample_100kB.docx';

function isPortalAlphaDemoDocumentUrl(url: string): boolean {
  return url.trim() === ONLYOFFICE_ALPHA_DEMO_DOCUMENT_URL;
}

/**
 * Frontend-Längenlimit für den Auszug, bevor er an ARKO/LEXCORE geht (Token-Budget Mistral/Llama).
 * Server-Pendant: `Portal Body Builder` / `Portal Renderer Builder` in `01_n8n_Workflows/ARKO_AGENT.json`
 * setzen den documentContext vor den User-Prompt — Limit hier knapp, damit Verlauf + Persona + LEXCORE-Block bleiben.
 */
const PORTAL_ARKO_DOCUMENT_CONTEXT_MAX_TEXT_CHARS = PORTAL_ARKO_LLM_WINDOW_CHARS;
/** Whitespace-stripped Mindestlänge — darunter gilt H2-Extraktion als „noch nicht bereit“ (kein H1-Fallback). */
const PORTAL_ARKO_MIN_MEANINGFUL_EXTRACT_CHARS = 20;

function isMeaningfulPortalExtractText(text: string): boolean {
  return text.replace(/\s+/g, '').length >= PORTAL_ARKO_MIN_MEANINGFUL_EXTRACT_CHARS;
}

const PORTAL_ONLYOFFICE_EXTRACT_OPEN_DEBOUNCE_MS = 1800;
/** Tab-Wechsel: JWT erst nach kurzer Ruhe — verhindert Burst bei schnellem Multi-Tab-Switch. */
const PORTAL_ONLYOFFICE_JWT_SWITCH_DEBOUNCE_MS = 450;

/** OnlyOffice-Embed: zusammengeführte Quelle (Demo oder PocketBase `portal_documents`). */
interface AlphaOnlyofficeDocumentSource {
  url: string;
  title: string;
  key: string;
  /** Wie PB `nextcloud_path` — WebDAV unter /Mandanten/.../portal-documents/... (Klartext-Fetch in n8n). */
  nextcloudWebdavPath?: string;
  /** Gesetzt, wenn PB `nextcloud_path` geliefert hat — für deterministischen Klartext-POST (Priorität vor Alias/Inferenz). */
  nextcloudPathFromPb?: string;
  /**
   * Roh-`display_name` aus PB bzw. Upload-Response (= echter NC-Dateiname inkl. Zeitstempel).
   * Für WebDAV-Inferenz, wenn `nextcloud_path` fehlt — nicht `title` nutzen (`title` kann künstlich `.docx` anhängen).
   */
  portalDocumentDisplayName?: string;
  /** PocketBase `portal_documents` id — optional für ONLYOFFICE_SAVE_CALLBACK (document_key sync). */
  pbRecordId?: string;
}

/**
 * Stabile Tab-Identitaet fuer mehrere gleichzeitig geoeffnete Dokumente.
 * Bewusst NICHT `key` (enthaelt `Date.now()`-Nonce → bei Re-Open neu), sondern
 * fachliche Identitaet: PB-Record, sonst NC-Pfad, sonst URL. So erkennt der
 * Tab-Manager dasselbe Dokument auch nach erneutem Oeffnen aus der Liste wieder.
 */
function documentTabId(src: AlphaOnlyofficeDocumentSource): string {
  const pb = src.pbRecordId?.trim();
  if (pb) return `pb:${pb}`;
  const nc = src.nextcloudPathFromPb?.trim() || src.nextcloudWebdavPath?.trim();
  if (nc) return `nc:${nc}`;
  return `url:${src.url}`;
}

function documentTabTitle(src: AlphaOnlyofficeDocumentSource): string {
  return src.portalDocumentDisplayName?.trim() || src.title?.trim() || 'Dokument';
}

function buildPortalOpenDocumentsMeta(
  docs: AlphaOnlyofficeDocumentSource[],
  activeId: string | null,
): PortalOpenDocumentMeta[] {
  return docs.map((d) => {
    const id = documentTabId(d);
    return {
      documentId: id,
      title: documentTabTitle(d),
      nc_path: d.nextcloudPathFromPb?.trim() || d.nextcloudWebdavPath?.trim() || null,
      active: id === activeId,
    };
  });
}

/** Frisch erzeugtes leeres Word — Extract nicht per Autosave (ConvertService 422). */
function isLikelyBlankPortalDoc(source: AlphaOnlyofficeDocumentSource): boolean {
  const name = source.portalDocumentDisplayName || source.title || '';
  return isLikelyBlankPortalDocumentName(name);
}

function escapePbFilterLiteral(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function pickFirstHttpUrlFromPortalDocument(record: Record<string, unknown>): string | null {
  const keys = ['document_url', 'url', 'onlyoffice_document_url', 'onlyoffice_url', 'source_url'];
  for (const k of keys) {
    const v = record[k];
    if (typeof v === 'string') {
      const t = v.trim();
      if (/^https?:\/\//i.test(t)) return t;
    }
  }
  const dk = record.document_key;
  if (typeof dk === 'string') {
    const t = dk.trim();
    if (/^https?:\/\//i.test(t)) return t;
  }
  return null;
}

function titleFromPortalDocumentRecord(record: Record<string, unknown>): string {
  const dn = record.display_name;
  if (typeof dn === 'string' && dn.trim()) {
    const s = dn.trim();
    return /\.[a-z0-9]+$/i.test(s) ? s : `${s}.docx`;
  }
  return 'Dokument.docx';
}

/** OnlyOffice: document.key nur [0-9a-zA-Z=._-], max. 128 (sonst Co-Editing /socket.io oft 400). */
function sanitizeOnlyofficeDocumentKey(raw: string): string {
  const cleaned = raw
    .replace(/[^a-zA-Z0-9=._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  const out = cleaned.slice(0, 128);
  return out.length > 0 ? out : `portal-doc-${Date.now()}`;
}

/** Nextcloud-Freigabe: direkter Download ohne Dateiendung in der URL → Typ aus Titel (`display_name`). */
function isNextcloudShareDirectDownloadUrl(pathOrUrl: string): boolean {
  const p = pathOrUrl.split('?')[0].split('#')[0].toLowerCase();
  return /\/s\/[^/]+\/download$/i.test(p);
}

function fileExtensionFromName(name: string): string {
  const m = name.trim().toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

/** Dateiname ohne Query/Fragment — für WebDAV-Pfade und Download-URLs. */
function basenamePortable(pathOrUrl: string): string {
  const p = pathOrUrl.trim().split('?')[0].split('#')[0];
  const parts = p.replace(/\\/g, '/').split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1]! : '';
}

/**
 * Endung für Wechsel Klartext vs. OnlyOffice: zuerst kanonischer NC-Pfad aus PB, dann WebDAV-Auflösung,
 * Display-Name, Titel, URL. Upload-Fälle: `display_name` kann von der echten Datei am `nextcloud_path` abweichen.
 */
function resolveAlphaDocRoutingExtension(src: AlphaOnlyofficeDocumentSource): string {
  const parts: string[] = [];
  const ncp = src.nextcloudPathFromPb?.trim();
  const ncw = src.nextcloudWebdavPath?.trim();
  if (ncp) parts.push(basenamePortable(ncp));
  if (ncw) parts.push(basenamePortable(ncw));
  const pd = src.portalDocumentDisplayName?.trim();
  if (pd) parts.push(pd);
  parts.push(src.title.trim());
  parts.push(basenamePortable(src.url));
  for (const seg of parts) {
    const ext = fileExtensionFromName(seg);
    if (ext) return ext;
  }
  return '';
}

/**
 * Wort-/Zeichen-Schnitt für den ARKO-Dokumentkontext. Schneidet wenn möglich am letzten
 * Whitespace nahe dem Limit, sonst hart; markiert das Ende mit `…[hier abgeschnitten]`,
 * damit das Modell die Verkürzung erkennt.
 */
function truncatePortalDocumentContextText(
  raw: string,
  limit: number,
): { text: string; truncated: boolean } {
  if (raw.length <= limit) return { text: raw, truncated: false };
  const slice = raw.slice(0, limit);
  const lastWs = slice.search(/\s\S*$/);
  const cut = lastWs > limit * 0.7 ? slice.slice(0, lastWs) : slice;
  return { text: `${cut.trimEnd()}\n…[hier abgeschnitten]`, truncated: true };
}

/**
 * Baut den `documentContext`-String für n8n `ARKO_AGENT` (Portal-Zweig).
 * - Plaintext-Pfad (.txt/.md/.csv): vollständiger UTF-8-Auszug, hart begrenzt auf `PORTAL_ARKO_DOCUMENT_CONTEXT_MAX_TEXT_CHARS`.
 * - OnlyOffice-Pfad (.docx/.xlsx/…): wenn H2-Server-Extraktion (`PORTAL_ONLYOFFICE_EXTRACT_TEXT`) Volltext geliefert hat,
 *   gleiches Cap wie Plaintext + Quellen-Label `(via OnlyOffice ConvertService)`; sonst weiterhin Metadaten + Hinweis
 *   (H1-Verhalten), damit n8n keine erfundenen Quellen baut.
 * - Kein Dokument bzw. leerer Text in einem Plaintext-Ladefehler → `null`, n8n behandelt das wie kein Kontext.
 */
function buildPortalArkoDocumentContext(args: {
  source: AlphaOnlyofficeDocumentSource | null;
  plainText: string | null;
  isPlainTextRoute: boolean;
  /** Optional: serverseitig extrahierter Volltext (H2) für OnlyOffice-Pfad — nur genutzt, wenn `isPlainTextRoute=false`. */
  extractedText?: string | null;
  extractSource?: string | null;
}): string | null {
  const src = args.source;
  if (!src) return null;
  const title = (src.title || '').trim() || 'Dokument';
  const ncPath = (src.nextcloudPathFromPb || src.nextcloudWebdavPath || '').trim();
  const sourceLine = ncPath || src.url || '(unbekannt)';
  const ext = resolveAlphaDocRoutingExtension(src) || 'unbekannt';

  const lines = [`Dokument: ${title}`, `Quelle: ${sourceLine}`, `Typ: ${ext}`];

  if (args.isPlainTextRoute) {
    const raw = (args.plainText || '').trim();
    if (raw.length === 0) {
      return null;
    }
    const { text, truncated } = truncatePortalDocumentContextText(
      raw,
      PORTAL_ARKO_DOCUMENT_CONTEXT_MAX_TEXT_CHARS,
    );
    lines.push(
      '',
      `Auszug (UTF-8${truncated ? `, gekürzt auf ${PORTAL_ARKO_DOCUMENT_CONTEXT_MAX_TEXT_CHARS} Zeichen` : ''}):`,
      text,
    );
    return lines.join('\n');
  }

  const extracted = (args.extractedText || '').trim();
  if (extracted.length > 0) {
    const { text, truncated } = truncatePortalDocumentContextText(
      extracted,
      PORTAL_ARKO_DOCUMENT_CONTEXT_MAX_TEXT_CHARS,
    );
    const via = akteExtractSourceLabel(args.extractSource, true);
    lines.push(
      '',
      `Auszug (UTF-8${via}${truncated ? `, gekürzt auf ${PORTAL_ARKO_DOCUMENT_CONTEXT_MAX_TEXT_CHARS} Zeichen` : ''}):`,
      text,
    );
    return lines.join('\n');
  }

  lines.push(
    '',
    'Hinweis: In dieser Portal-Version wird der OnlyOffice-Live-Text noch nicht automatisch extrahiert. ' +
      'Bitte den relevanten Auszug aus dem Editor bei Bedarf direkt in den Chat kopieren.',
  );
  return lines.join('\n');
}

/** Dateiname-Hinweis für OnlyOffice `documentType` / `fileType` (Extension vorrangig aus Mandanten-Pfad). */
function onlyofficeFilenameHintForMeta(src: AlphaOnlyofficeDocumentSource): string {
  const routed = resolveAlphaDocRoutingExtension(src);
  const fromNc = basenamePortable(src.nextcloudPathFromPb || src.nextcloudWebdavPath || '');
  const fromDisplay = src.portalDocumentDisplayName?.trim() ?? '';
  let hint = fromNc || fromDisplay || src.title.trim() || basenamePortable(src.url);
  if (routed && !fileExtensionFromName(hint)) {
    hint = `${hint}.${routed}`;
  }
  return hint.trim() || src.title.trim() || 'Dokument.docx';
}

/** OnlyOffice DocsAPI: documentType + fileType aus URL; bei NC-Share-/download ohne Endung aus `titleHint` (Anzeigename). */
function onlyofficeDocumentMetaFromUrl(
  url: string,
  titleHint?: string | null,
): { documentType: string; fileType: string } {
  const pathOnly = url.split('?')[0].split('#')[0].toLowerCase();
  const m = pathOnly.match(/\.([a-z0-9]+)$/);
  let ex = m ? m[1] : '';
  const fromTitle = titleHint ? fileExtensionFromName(titleHint) : '';
  if ((!ex || isNextcloudShareDirectDownloadUrl(pathOnly)) && fromTitle) {
    ex = fromTitle;
  }
  if (!ex) ex = 'docx';
  if (ex === 'pdf') return { documentType: 'pdf', fileType: 'pdf' };
  if (ex === 'xls') return { documentType: 'cell', fileType: 'xls' };
  if (ex === 'xlsx' || ex === 'ods') return { documentType: 'cell', fileType: ex };
  if (ex === 'csv') return { documentType: 'cell', fileType: 'csv' };
  if (ex === 'ppt') return { documentType: 'slide', fileType: 'ppt' };
  if (ex === 'pptx' || ex === 'odp') return { documentType: 'slide', fileType: ex };
  if (ex === 'doc') return { documentType: 'word', fileType: 'doc' };
  if (ex === 'md' || ex === 'markdown') {
    // Labor-DS (ältere Builds): `fileType: 'md'` → DocsAPI „document.fileType … invalid“. Als Plaintext öffnen.
    return { documentType: 'word', fileType: 'txt' };
  }
  if (
    ex === 'docx' ||
    ex === 'odt' ||
    ex === 'rtf' ||
    ex === 'txt' ||
    ex === 'html' ||
    ex === 'htm'
  ) {
    const ft = ex === 'htm' ? 'html' : ex;
    return { documentType: 'word', fileType: ft };
  }
  return { documentType: 'word', fileType: 'docx' };
}

/**
 * Lifted-State-Typen fuer den Dokument-Auszug, der in den `documentContext`
 * der Anwalts-ARKO-Webhook-Payload fliesst. Definition auf Modul-Ebene, damit
 * sowohl der `DocumentEditorAlphaView` als auch der `CustomerPortal`-Outer-Scope
 * (lifted state seit 27.05.2026) denselben Typ teilen — der Outer-Scope haelt
 * den Wert ueber View-Wechsel hinweg, damit der normale ARKO-Chat (`view === 'chat'`)
 * den geoeffneten Dokumentenstand sieht (Andreas-Vorgabe „documentContext in
 * BOTH document tab chat AND normal ARKO assistant chat“).
 */
type NativeTextPaneUi =
  | { kind: 'off' }
  | { kind: 'loading' }
  | { kind: 'ready'; documentId: string; text: string }
  | { kind: 'error'; message: string; documentUrl: string };

type OnlyofficeExtractTextUi =
  | { kind: 'off' }
  | { kind: 'loading' }
  | { kind: 'ready'; documentId: string; text: string; rawBytes?: number; truncated?: boolean; pdfPages?: number; source?: string }
  /** OnlyOffice lieferte nur Leerzeichen — typisch vor erstem Save oder bei leerer Vorlage. */
  | { kind: 'insufficient' }
  | { kind: 'error'; message: string };

/** Scope-Ref für stabilen `DocumentEditorAlphaView` (kein Remount bei Parent-Re-Renders). */
type PortalDocumentEditorScope = {
  customer: CustomerRecord;
  alphaDocReady: boolean;
  alphaDocSource: AlphaOnlyofficeDocumentSource | null;
  plainTextDocumentChrome: boolean;
  nativeTextUi: NativeTextPaneUi;
  pinAlphaDocSource: (source: AlphaOnlyofficeDocumentSource) => boolean;
  mandantenRoot: string;
  setOnlyofficeTabDirty: (tabId: string, dirty: boolean) => void;
  scheduleOnlyofficeExtractRefresh: () => void;
  portalArkoDocumentContext: string | null;
  portalParagraphIndexMap: import('./portal/portalParagraphMap').ParagraphIndexEntry[];
  handleMarkPortalDocumentAnalyzed: () => void;
  portalDocumentDisplayName: string | null;
  defaultDocumentContextEnabled: boolean;
  portalDocumentExtractFingerprint: string | null;
  onlyofficeExtractRevision: number;
  portalDocumentNcPath: string | null;
  portalDocumentAkteNcPath: string | null;
  portalArkoWebhook: string;
  portalArkoInitialBotMessage: string;
  portalWebhookExtraBody: Record<string, unknown>;
  isPremium: boolean;
  contractGeneratorEnabled: boolean;
  generateContractFromChat: (args: {
    templateType: string;
    fields: Record<string, unknown>;
    displayName: string;
  }) => Promise<{ ok: boolean; message: string }>;
  exportSchreibenPdf: (args: {
    text: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    message: string;
    portalDocument?: PortalDocumentOpenFields;
  }>;
  exportSchreibenDocx: (args: {
    text: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    message: string;
    portalDocument?: PortalDocumentOpenFields;
  }>;
  applySynthesisToDocument: (args: {
    text: string;
    target: 'existing' | 'new';
  }) => Promise<{ ok: boolean; message: string }>;
  onlyofficeEditorInstanceRef: React.MutableRefObject<OnlyofficeDocEditorHandle | null>;
  akteDocSource: AlphaOnlyofficeDocumentSource | null;
  akteDocReady: boolean;
  akteOnlyofficeEditorInstanceRef: React.MutableRefObject<OnlyofficeDocEditorHandle | null>;
  closeAkteOnlyoffice: () => void;
  memcoreChat: { curatorUrl: string; kanzleiId: string; userId: string } | null;
  activeDocumentId: string | null;
  portalOpenDocumentsMeta: PortalOpenDocumentMeta[];
  portalAvailableDocuments: PortalSelectedDocumentPayload[];
  portalArkoDocumentContextForChat: string | null;
  portalArkoOpenDocumentsMeta: PortalOpenDocumentMeta[];
  portalArkoAvailableDocuments: PortalSelectedDocumentPayload[];
  portalActiveDocumentExtract: {
    text: string;
    pages?: number;
    rawBytes?: number;
    truncated?: boolean;
    source?: string;
  } | null;
  arkoActiveDocumentId: string | null;
  boundAkte: PortalBoundAkte | null;
  boundAkteOptions: PortalBoundAkte[];
  akteDocumentCount: number;
  handleBoundAkteChange: (akte: PortalBoundAkte | PortalAkteRecord | null) => void;
  akteDocsPanelOpenNonce: number;
  onOpenAkteDocument: (documentId: string) => void;
  onRenameAkteDocument?: (documentId: string, newName: string) => Promise<void>;
  canWriteAkteDocuments: boolean;
  onDocumentSelectionControllerChange: (
    controller: ((documentId: string) => void) | null,
  ) => void;
  onSelectedDocumentIdsChange: (selectedDocumentIds: string[]) => void;
  extractingDocumentIds: string[];
  onlyOfficeTheme: PortalOnlyOfficeTheme;
  setOnlyOfficeTheme: (theme: PortalOnlyOfficeTheme) => void;
  registerPortalDocumentFromFields: (fields: PortalDocumentOpenFields) => Promise<void>;
};

const portalDocumentEditorScopeRef: { current: PortalDocumentEditorScope } = {
  current: {} as PortalDocumentEditorScope,
};

/** Klartext im Portal statt OnlyOffice (DS-Textpfad + NC-CORS sind zu fehleranfällig). */
function isPortalNativePlainTextExtension(ext: string): boolean {
  return ext === 'txt' || ext === 'md' || ext === 'markdown' || ext === 'csv';
}

function stripLeadingUtf8BomBytes(u8: Uint8Array): Uint8Array {
  if (u8.byteLength >= 3 && u8[0] === 0xef && u8[1] === 0xbb && u8[2] === 0xbf) {
    return u8.subarray(3);
  }
  return u8;
}

/** Leseansicht: UTF-8, ungueltige Bytes als Ersatzzeichen (nicht fatal). */
function decodePortalPlainTextUtf8(ab: ArrayBuffer): string {
  const u8 = new Uint8Array(ab);
  const body = stripLeadingUtf8BomBytes(u8);
  return new TextDecoder('utf-8', { fatal: false }).decode(body);
}

function portalPlaintextBase64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function onlyofficeKeyFromPortalDocumentRecord(record: Record<string, unknown>): string {
  const id = typeof record.id === 'string' ? record.id : 'doc';
  const sessionNonce = Date.now();
  const revRaw = record.last_revision_id;
  const revNum = revRaw != null ? Number(revRaw) : NaN;
  if (Number.isFinite(revNum) && revNum >= 0) {
    return sanitizeOnlyofficeDocumentKey(`portal-doc-${id}-r${Math.floor(revNum)}-${sessionNonce}`);
  }
  const savedAt = record.last_save_callback_at;
  if (typeof savedAt === 'string' && savedAt.trim()) {
    const t = new Date(savedAt.trim()).getTime();
    if (Number.isFinite(t)) {
      return sanitizeOnlyofficeDocumentKey(`portal-doc-${id}-${t}-${sessionNonce}`);
    }
  }
  const upd = record.updated != null ? String(record.updated) : String(record.created ?? sessionNonce);
  return sanitizeOnlyofficeDocumentKey(`portal-doc-${id}-${upd}-${sessionNonce}`);
}

function portalDocumentCacheBustToken(record: Record<string, unknown>): string | number | undefined {
  const revRaw = record.last_revision_id;
  const revNum = revRaw != null ? Number(revRaw) : NaN;
  if (Number.isFinite(revNum) && revNum >= 0) return Math.floor(revNum);
  const savedAt = record.last_save_callback_at;
  if (typeof savedAt === 'string' && savedAt.trim()) {
    const t = new Date(savedAt.trim()).getTime();
    if (Number.isFinite(t)) return t;
  }
  if (record.updated != null) return String(record.updated);
  return undefined;
}

type OnlyofficeDocEditorHandle = {
  destroyEditor?: () => void;
  requestClose?: () => void;
  createConnector?: () => { callCommand?: (command: () => void, callback?: () => void) => void };
};

/**
 * Doc-Wechsel/Unmount.
 * - `preferDestroy`: schnelle Tab-Switches / Aborts — kein Save-Callback-Sturm.
 * - sonst `requestClose` (status 2/6) wenn dirty; destroyEditor allein oft status 4.
 */
function closeOnlyofficeEditorInstance(
  inst: OnlyofficeDocEditorHandle | null | undefined,
  opts?: { preferDestroy?: boolean },
): void {
  if (!inst) return;
  if (opts?.preferDestroy) {
    try {
      inst.destroyEditor?.();
      return;
    } catch {
      /* fallback unten */
    }
  }
  if (typeof inst.requestClose === 'function') {
    try {
      inst.requestClose();
      return;
    } catch {
      /* fallback */
    }
  }
  inst.destroyEditor?.();
}

/** Wie n8n PORTAL_DOCUMENT_UPLOAD: NC-Segment = kanzlei_id (Org), Legacy-Fallback customer.id. */
function portalNcCustomerSegment(customerId: string, kanzleiId?: string): string {
  const raw = String(kanzleiId || customerId || '');
  return raw.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 80);
}

/**
 * Wenn PB `nextcloud_path` nicht mitgeliefert wird (Rules/API), gleicher Pfad wie Upload:
 * `/Mandanten/<segment>/portal-documents/<display_name>` — display_name = Dateiname inkl. Zeitstempel.
 */
function inferNextcloudPortalDocumentsPath(
  customerId: string,
  fileLeaf: string,
  mandantenRoot = 'Mandanten',
  kanzleiId?: string,
): string | undefined {
  const seg = portalNcCustomerSegment(customerId, kanzleiId);
  const leaf = String(fileLeaf || '').trim();
  if (!seg || !leaf || /[\\/]/.test(leaf)) return undefined;
  return `/${mandantenRoot}/${seg}/portal-documents/${leaf}`;
}

/** PB liefert logischen DAV-Pfad; ohne führendes `/` ist Mandanten/... trotzdem eindeutig. */
function normalizePortalWebdavPathFromPb(raw: string): string {
  const t = raw.trim();
  if (!t) return t;
  if (t.startsWith('/')) return t;
  if (/^Mandanten(-field)?\//i.test(t)) return `/${t}`;
  return t;
}

/** n8n Upload/Share: tenant=field|demo → isolierter NC-Prefix. */
function portalTenantN8nExtras(
  customerStatus?: string,
  customerEmail?: string,
): Record<string, string> {
  return getPortalTenantN8nExtras(customerStatus, customerEmail);
}

/**
 * Kanonische WebDAV-Relativpfad-Quelle für n8n **PORTAL_PLAINTEXT_FETCH**:
 * `nextcloud_path` (PB) vor Alias-Feldern; Inferenz aus Dateiname nur **Labor-Fallback**, wenn PB keinen Pfad liefert.
 */
function resolveNextcloudWebdavPathFromPortalDocumentRecord(
  record: Record<string, unknown>,
  customerId: string,
  mandantenRoot = 'Mandanten',
  kanzleiId?: string,
): string | undefined {
  const ncp = record.nextcloud_path;
  const ncw = record.nextcloud_webdav_path;
  const ncc = record.nextcloudWebdavPath;
  if (typeof ncp === 'string' && ncp.trim()) {
    return normalizePortalWebdavPathFromPb(ncp);
  }
  if (typeof ncw === 'string' && ncw.trim()) {
    return normalizePortalWebdavPathFromPb(ncw);
  }
  if (typeof ncc === 'string' && ncc.trim()) {
    return normalizePortalWebdavPathFromPb(ncc);
  }
  const leaf =
    (typeof record.display_name === 'string' && record.display_name.trim()) ||
    (typeof record.portalDocumentDisplayName === 'string' && record.portalDocumentDisplayName.trim()) ||
    '';
  return inferNextcloudPortalDocumentsPath(customerId, leaf, mandantenRoot, kanzleiId);
}

/**
 * Deterministischer POST-Body für **PORTAL_PLAINTEXT_FETCH**: immer `document_url`; `nextcloud_webdav_path` nur aus
 * PB-Pfad bzw. eindeutigem Alias — Inferenz nur wenn Pfad-Felder wirklich leer (z. B. frischer Upload mit nur `display_name`).
 */
function buildPlaintextFetchBody(
  record: Record<string, unknown>,
  customerId: string,
  customerStatus?: string,
  customerEmail?: string,
): { document_url: string; nextcloud_webdav_path?: string; tenant?: string } {
  const document_url =
    pickFirstHttpUrlFromPortalDocument(record) ||
    (typeof record.document_url === 'string' ? record.document_url.trim() : '') ||
    (typeof record.url === 'string' ? record.url.trim() : '');
  const mandantenRoot = getPortalNcMandantenRoot(customerStatus, customerEmail);
  const nextcloud_webdav_path = resolveNextcloudWebdavPathFromPortalDocumentRecord(
    record,
    customerId,
    mandantenRoot,
  );
  const out: { document_url: string; nextcloud_webdav_path?: string; tenant?: string } = {
    document_url,
    ...portalTenantN8nExtras(customerStatus, customerEmail),
  };
  if (nextcloud_webdav_path) {
    out.nextcloud_webdav_path = nextcloud_webdav_path;
  }
  return out;
}

/**
 * `editorConfig.userdata` (JSON-String) für n8n **ONLYOFFICE_SAVE_CALLBACK**:
 * `customerId` im Userdata = NC-Segment (`kanzlei_id` bevorzugt, sonst Sitz-ID);
 * `nextcloud_path` muss `/Mandanten/<segment>/...` sein (Path-Guard im Workflow).
 */
function buildOnlyofficeEditorUserdataJson(args: {
  portalCustomerId: string;
  kanzleiId?: string;
  nextcloudWebdavPath?: string;
  pbRecordId?: string;
  mandantenRoot?: string;
}): string | undefined {
  const segment = portalNcCustomerSegment(args.portalCustomerId, args.kanzleiId);
  if (!segment) return undefined;
  const rawPath = args.nextcloudWebdavPath?.trim();
  if (!rawPath) return undefined;
  const pathNorm = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  const root = getMandantenRootFromNextcloudPath(pathNorm, undefined);
  const pathGuard = `/${root}/${segment}/`;
  if (!pathNorm.startsWith(pathGuard)) return undefined;
  const o: Record<string, string> = {
    customerId: segment,
    nextcloud_path: pathNorm,
  };
  const rid = args.pbRecordId?.trim();
  if (rid) o.pb_record_id = rid;
  return JSON.stringify(o);
}

/**
 * Optional: aktives Dokument aus PocketBase `portal_documents` (gleiche Bearer-Session wie Portal-Login).
 * Schema: create-portal-documents-collection.ps1 — Zuordnung über `customer_id`, Titel `display_name`,
 * kanonische öffentliche Download-URL im PB-Feld `document_url` (Legacy-Schlüssel siehe pickFirstHttpUrlFromPortalDocument).
 * Collection fehlt / leer / keine URL → null (Caller: Demo-DOCX).
 */
async function fetchAlphaDocumentSourceFromPb(
  customerId: string,
  authToken: string | null,
  customerStatus?: string,
  customerEmail?: string,
): Promise<AlphaOnlyofficeDocumentSource | null> {
  if (!authToken?.trim()) return null;
  const base = getPortalPocketBaseUrl().replace(/\/$/, '');
  const cid = escapePbFilterLiteral(customerId);
  const filter = `(customer_id='${cid}')`;
  const listUrl = `${base}/api/collections/${PORTAL_PROTOKOLLE_COLLECTION}/records?${new URLSearchParams({
    filter,
    sort: PORTAL_DOCUMENTS_PB_LIST_SORT,
    perPage: '25',
  }).toString()}`;

  let res: Response;
  try {
    res = await fetch(listUrl, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
  } catch {
    return null;
  }

  if (res.status === 404) return null;
  if (!res.ok) return null;

  let data: { items?: Record<string, unknown>[] };
  try {
    data = (await res.json()) as { items?: Record<string, unknown>[] };
  } catch {
    return null;
  }

  const items = data.items ?? [];
  const preferred = items.filter(isActivePortalDocumentRecord);
  const scan = preferred.length > 0 ? preferred : items;

  for (const rec of scan) {
    const url = pickFirstHttpUrlFromPortalDocument(rec);
    if (!url) continue;
    const rawDisplay = typeof rec.display_name === 'string' ? rec.display_name.trim() : '';
    const ncpRaw = typeof rec.nextcloud_path === 'string' ? rec.nextcloud_path.trim() : '';
    const nextcloudPathFromPb = ncpRaw ? ncpRaw : undefined;
    const nextcloudWebdavPath = resolveNextcloudWebdavPathFromPortalDocumentRecord(
      rec,
      customerId,
      getPortalNcMandantenRoot(customerStatus, customerEmail),
    );
    const pbId = typeof rec.id === 'string' ? rec.id.trim() : '';
    const cacheBust = portalDocumentCacheBustToken(rec);
    return {
      url: bustPortalDocumentDownloadUrl(url, cacheBust),
      title: titleFromPortalDocumentRecord(rec),
      key: onlyofficeKeyFromPortalDocumentRecord(rec),
      nextcloudWebdavPath,
      nextcloudPathFromPb,
      portalDocumentDisplayName: rawDisplay || undefined,
      pbRecordId: pbId || undefined,
    };
  }

  return null;
}

/**
 * Baut aus einer n8n-JSON-Antwort (PORTAL_DOCUMENT_UPLOAD-Format: `document_url`,
 * `nextcloud_path`, `display_name`, `pb_record_id`) eine `AlphaOnlyofficeDocumentSource`.
 * Gemeinsame Quelle für Upload/NC-Import (innerer View) UND den Vertragsgenerator
 * (Stream C/D) — gleicher Editor-Pfad, keine Duplizierung. Wirft bei Fehler.
 */
function buildAlphaDocSourceFromN8nJson(
  data: Record<string, unknown>,
  res: Response,
  customerId: string,
  keyPrefix: string,
): { source: AlphaOnlyofficeDocumentSource; displayName: string } {
  const docUrl = typeof data.document_url === 'string' ? data.document_url.trim() : '';
  const payloadOk = data.ok === true || data.ok === 'true';
  if (!res.ok || (!payloadOk && !docUrl)) {
    throw new Error(formatPortalUploadError(res, data, 'upload'));
  }
  if (!docUrl) {
    throw new Error('Die Datei wurde gespeichert, aber die Server-Antwort fehlt. Bitte erneut versuchen.');
  }
  const dn = typeof data.display_name === 'string' ? data.display_name : 'Dokument';
  const pbUploadId =
    (typeof data.pb_record_id === 'string' && data.pb_record_id.trim()) ||
    (typeof data.pb_id === 'string' && data.pb_id.trim()) ||
    (typeof data.record_id === 'string' && data.record_id.trim()) ||
    undefined;
  const uploadRec: Record<string, unknown> = {
    nextcloud_path: data.nextcloud_path,
    display_name: dn,
  };
  const ncpResolved = resolveNextcloudWebdavPathFromPortalDocumentRecord(uploadRec, customerId);
  const source: AlphaOnlyofficeDocumentSource = {
    url: docUrl,
    title: dn,
    key: sanitizeOnlyofficeDocumentKey(`${keyPrefix}-${Date.now()}-${dn.replace(/[^a-zA-Z0-9._-]/g, '-')}`),
    nextcloudWebdavPath: ncpResolved,
    nextcloudPathFromPb:
      typeof data.nextcloud_path === 'string' && data.nextcloud_path.trim()
        ? data.nextcloud_path.trim()
        : undefined,
    portalDocumentDisplayName: dn,
    pbRecordId: pbUploadId,
  };
  return { source, displayName: dn };
}

/** Wartet auf Editor-Mount (Layout/Split-Hydration), bevor DocsAPI initialisiert wird. */
async function waitForOnlyofficeEditorMount(
  getMount: () => HTMLDivElement | null,
  maxFrames = 24,
): Promise<HTMLDivElement> {
  for (let i = 0; i < maxFrames; i++) {
    const el = getMount();
    if (el) return el;
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }
  throw new Error('Editor-Bereich nicht im DOM (Layout noch nicht bereit — bitte erneut öffnen)');
}

/** Bestätigt den aktiven, unverdeckten Ziel-Split statt nur irgendeinen positiven DOM-Rect. */
async function waitForVisibleDocumentSplit(
  documentId: string,
  timeoutMs = 30000,
): Promise<HTMLElement | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const split = Array.from(
      document.querySelectorAll<HTMLElement>('[data-portal-document-editor-active="true"]'),
    ).find((el) => el.dataset.portalDocumentSplitId === documentId);
    if (split) {
      const style = window.getComputedStyle(split);
      const rect = split.getBoundingClientRect();
      const visible =
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity || '1') > 0 &&
        !split.closest('[aria-hidden="true"]') &&
        rect.width > 0 &&
        rect.height > 0 &&
        rect.right > 0 &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.top < window.innerHeight;
      if (visible) return split;
    }
    await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
  }
  return null;
}

function formatOnlyofficeJwtFetchError(res: Response, errStr: string): string {
  return formatOnlyofficeJwtErrorForUser(res, errStr);
}

async function loadOnlyOfficeDocsApiScript(dsBase: string): Promise<void> {
  if (typeof window === 'undefined') return;
  if (window.DocsAPI?.DocEditor) return;

  const src = `${dsBase.replace(/\/$/, '')}/web-apps/apps/api/documents/api.js`;
  const existing = document.querySelector<HTMLScriptElement>(
    'script[data-arkorbit-onlyoffice-docsapi="1"]',
  );

  if (existing) {
    await new Promise<void>((resolve, reject) => {
      if (window.DocsAPI?.DocEditor) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('OnlyOffice DocsAPI Skript')), {
        once: true,
      });
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.dataset.arkorbitOnlyofficeDocsapi = '1';
    s.onload = () => resolve();
    s.onerror = () =>
      reject(new Error(`OnlyOffice DocsAPI konnte nicht geladen werden (${src})`));
    document.head.appendChild(s);
  });

  if (!window.DocsAPI?.DocEditor) {
    throw new Error('DocsAPI nach dem Laden nicht verfügbar');
  }
}

// ── Typen ─────────────────────────────────────────────────────────────────────

type Protokoll = PortalProtokoll;

function buildAlphaDocSourceFromPortalDocumentFields(
  customerId: string,
  fields: {
    display_name: string;
    document_url: string;
    pb_record_id?: string;
    nextcloud_path?: string;
    updated?: string;
    last_revision_id?: number;
    last_save_callback_at?: string;
  },
  customerStatus?: string,
  customerEmail?: string,
): AlphaOnlyofficeDocumentSource | null {
  const document_url = fields.document_url.trim();
  if (!/^https?:\/\//i.test(document_url)) return null;
  const mandantenRoot = getPortalNcMandantenRoot(customerStatus, customerEmail);
  const rec: Record<string, unknown> = {
    id: fields.pb_record_id,
    display_name: fields.display_name,
    document_url,
    nextcloud_path: fields.nextcloud_path,
    updated: fields.updated,
    last_revision_id: fields.last_revision_id,
    last_save_callback_at: fields.last_save_callback_at,
  };
  const ncpRaw = fields.nextcloud_path?.trim();
  const cacheBust = portalDocumentCacheBustToken(rec);
  return {
    url: bustPortalDocumentDownloadUrl(document_url, cacheBust),
    title: titleFromPortalDocumentRecord(rec),
    key: fields.pb_record_id
      ? onlyofficeKeyFromPortalDocumentRecord(rec)
      : sanitizeOnlyofficeDocumentKey(`portal-open-${Date.now()}-${fields.display_name}`),
    nextcloudWebdavPath: resolveNextcloudWebdavPathFromPortalDocumentRecord(
      rec,
      customerId,
      mandantenRoot,
    ),
    nextcloudPathFromPb: ncpRaw || undefined,
    portalDocumentDisplayName: fields.display_name.trim() || undefined,
    pbRecordId: fields.pb_record_id?.trim() || undefined,
  };
}

interface PasskeyInfo {
  id: string;
  deviceName: string;
  created: string;
}

// ── WebAuthn: siehe portal/webauthnClient.ts ─────────────────────────────────

// ── Haupt-Komponente ───────────────────────────────────────────────────────────

interface CustomerPortalProps {
  customer: CustomerRecord;
  onLogout: () => void;
  onCustomerUpdate?: (customer: CustomerRecord) => void;
}

const CustomerPortal: React.FC<CustomerPortalProps> = ({ customer: customerProp, onLogout, onCustomerUpdate }) => {
  const [customer, setCustomer] = useState(customerProp);
  useEffect(() => {
    setCustomer(customerProp);
  }, [customerProp]);

  const bausteinSeat = useMemo(() => seatInputFromCustomer(customer), [customer]);
  const [bausteinBestellName, setBausteinBestellName] = useState('');
  const [bausteinBestellHinweis, setBausteinBestellHinweis] = useState('');
  const bausteinAnfrageEinmal = useRef<Set<string>>(new Set());
  const schreibeBaustein = useCallback(async (name: string, freischalten: boolean) => {
    if (bausteinBestellName) return;
    if (bausteinZeileAnfrage(name, bausteinSeat)) return;
    const einmal = (freischalten ? 'F:' : 'K:') + name;
    if (bausteinAnfrageEinmal.current.has(einmal)) return;
    if (!bausteinDarfBestellen(bausteinSeat)) return;
    const vorher = bausteinSeat;
    const nextMap = freischalten
      ? bausteinKategorienNachBestellung(customer.baustein_kategorien, name)
      : bausteinKategorienNachKuendigung(customer.baustein_kategorien, name);
    if (!nextMap) return;
    const hinweis = 'Die Anfrage ist nicht gespeichert.';
    const token = getPortalPbToken();
    if (!token) {
      setBausteinBestellHinweis(hinweis);
      return;
    }
    bausteinAnfrageEinmal.current.add(einmal);
    setBausteinBestellHinweis('');
    setBausteinBestellName(name);
    try {
      const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
      const url = `${pbBase}/api/collections/customers/records/${customer.id}`;
      const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token };
      const res = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ baustein_kategorien: JSON.stringify(nextMap) }),
      });
      if (!res.ok) {
        bausteinAnfrageEinmal.current.delete(einmal);
        setBausteinBestellHinweis(hinweis);
        return;
      }
      const reload = await fetch(url, { headers: { Authorization: 'Bearer ' + token } });
      if (!reload.ok) {
        bausteinAnfrageEinmal.current.delete(einmal);
        setBausteinBestellHinweis(hinweis);
        return;
      }
      const record = (await reload.json()) as Record<string, unknown>;
      if (!record?.id) {
        bausteinAnfrageEinmal.current.delete(einmal);
        setBausteinBestellHinweis(hinweis);
        return;
      }
      const refreshed = buildCustomerRecord(record);
      const refreshedSeat = seatInputFromCustomer(refreshed);
      if (!bausteinAnfrageBleibt(name, vorher, refreshedSeat)) {
        bausteinAnfrageEinmal.current.delete(einmal);
        setBausteinBestellHinweis(hinweis);
        return;
      }
      setCustomer(refreshed);
      persistPortalCustomer(refreshed);
      onCustomerUpdate?.(refreshed);
      const brief = bausteinAnfrageBrief({
        kanzlei: customer.kanzlei_id || customer.kanzlei,
        sitz: customer.id,
        modul: name,
        art: freischalten ? 'Freischalten' : 'Kündigung',
      });
      if (brief) void sendeBausteinAnfrageMail(brief);
    } catch {
      bausteinAnfrageEinmal.current.delete(einmal);
      setBausteinBestellHinweis(hinweis);
    } finally {
      setBausteinBestellName('');
    }
  }, [bausteinBestellName, bausteinSeat, customer.baustein_kategorien, customer.id, customer.kanzlei, customer.kanzlei_id, onCustomerUpdate]);
  const bestelleBaustein = useCallback((name: string) => {
    void schreibeBaustein(name, true);
  }, [schreibeBaustein]);
  const kuendigeBaustein = useCallback((name: string, zugestimmt?: boolean) => {
    if (!bausteinKuendigungDarfSchreiben(zugestimmt === true)) return;
    void schreibeBaustein(name, false);
  }, [schreibeBaustein]);
  const arkoOpen = seatEntersBaustein('arko', bausteinSeat);
  const diktatOpen = seatEntersBaustein('diktat', bausteinSeat);
  const sucheOpen = seatEntersBaustein('suche', bausteinSeat);
  const fallanalyseOpen = seatEntersBaustein('fallanalyse', bausteinSeat);
  const judicatureOpen = seatEntersBaustein('judikatur', bausteinSeat);
  const hintergrundOpen = seatEntersBaustein('hintergrundanalyse', bausteinSeat);
  const mustChangePassword = customer.must_change_password === true;
  const teamAccessEnabled = canManageTeamAccess(customer);
  const portalArkoInitialBotMessage = useMemo(() => {
    const who =
      (customer.name && String(customer.name).trim()) ? String(customer.name).trim() : customer.email;
    return `Guten Tag, ${who}. Ich bin ARKO, Ihr Kanzlei-Assistent. Ich unterstütze beim Formulieren, Erklären, Zusammenfassen und beim Festhalten von Notizen – keine Rechtsberatung. Was darf ich für Sie tun?`;
  }, [customer.name, customer.email]);

  const [view, setView] = useState<PortalView>(() => {
    const navCtx = {
      isPremium: customer.tier === 'premium',
      contractGeneratorEnabled: isContractGeneratorEnabled(),
      memcoreEnabled: isMemcoreEnabled(customer.status),
      teamAccessEnabled: canManageTeamAccess(customer),
      elementSeat: customer.elementSeat === true,
      seat: seatInputFromCustomer(customer),
    };
    if (typeof window !== 'undefined') {
      const fromLoc = readPortalViewFromLocation(window.location.hash, window.history.state);
      if (fromLoc && canRestorePortalView(fromLoc, navCtx)) {
        return fromLoc;
      }
    }
    const saved = loadPortalSessionState(customer.id);
    if (saved && canRestorePortalView(saved.view, navCtx)) {
      return saved.view;
    }
    return 'home';
  });
  useEffect(() => {
    if (view === 'matrix' && customer.elementSeat !== true) {
      setView('home');
    }
  }, [view, customer.elementSeat]);
  useEffect(() => {
    if (!portalViewAllowed(view, bausteinSeat)) setView('home');
  }, [view, bausteinSeat]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Hub-Layout: ARKO-Rail rechts (Jace-Pattern) — unabhängig vom Dokument-Split. */
  const [diktatTab, setDiktatTab]   = useState<DiktatTab>('record');
  const [dokumenteAreaTab, setDokumenteAreaTab] = useState<DokumenteAreaTab>(() => {
    const saved = loadPortalSessionState(customer.id);
    return saved?.view === 'document_editor' ? 'editor' : 'hub';
  });
  const [lastArkoIntent, setLastArkoIntent] = useState<string | null>(
    () => loadPortalSessionState(customer.id)?.lastArkoIntent ?? null,
  );
  const isPremium = customer.tier === 'premium';
  const [aktenRahmenFill, setAktenRahmenFill] = useState(false);
  const [contractCreatorTemplateId, setContractCreatorTemplateId] = useState<string | null>(null);
  const contractGeneratorEnabled = isContractGeneratorEnabled();
  useEffect(() => {
    const onOpenCreator = (event: Event) => {
      const templateId = String(
        (event as CustomEvent<{ templateId?: string }>).detail?.templateId || '',
      ).trim();
      if (!templateId || !getContractTemplate(templateId)) return;
      if (!isPremium || !contractGeneratorEnabled) return;
      setContractCreatorTemplateId(templateId);
      setView('contract_generator');
    };
    window.addEventListener(ARKO_OPEN_CONTRACT_CREATOR_EVENT, onOpenCreator);
    return () => window.removeEventListener(ARKO_OPEN_CONTRACT_CREATOR_EVENT, onOpenCreator);
  }, [isPremium, contractGeneratorEnabled]);
  const mandantenRoot = getPortalNcMandantenRoot(customer.status, customer.email);
  const portalArkoWebhook = useMemo(
    () => getPortalArkoWebhook(customer.status),
    [customer.status],
  );
  const memcoreCuratorUrl = useMemo(
    () => getMemcoreCuratorUrl(customer.status),
    [customer.status],
  );

  // ── MEMCORE Modul B (Labor/Feld, Premium): Anwalts-Gedächtnis ─────────────
  const memcoreEnabled = isPremium && isMemcoreEnabled(customer.status);
  const arkoBooked = portalAkteHasArko(
    filterNavItemsForSeat(
      getPrimaryNavItems({
        elementSeat: customer.elementSeat === true,
      }),
      bausteinSeat,
    ).map((item) => item.id),
  );
  const memcoreCtx = useMemo(
    () => ({ kanzleiId: customer.kanzlei, userId: customer.id }),
    [customer.kanzlei, customer.id],
  );
  const memcoreChat = useMemo(
    () =>
      memcoreEnabled
        ? { curatorUrl: memcoreCuratorUrl, kanzleiId: customer.kanzlei, userId: customer.id }
        : null,
    [memcoreEnabled, memcoreCuratorUrl, customer.kanzlei, customer.id],
  );
  const [boundAkte, setBoundAkte] = useState<PortalBoundAkte | null>(() =>
    loadPortalBoundAkte(customer.id),
  );
  const [portalAktenList, setPortalAktenList] = useState<PortalAkteRecord[]>([]);

  const handleBoundAkteChange = useCallback((akte: PortalBoundAkte | PortalAkteRecord | null) => {
    const next = akte?.id?.trim() ? toPortalBoundAkte(akte) : null;
    setBoundAkte((prev) => {
      if (
        (prev?.id || '') === (next?.id || '')
        && (prev?.title || '') === (next?.title || '')
        && (prev?.nc_path || '') === (next?.nc_path || '')
      ) {
        return prev;
      }
      return next;
    });
    savePortalBoundAkte(customer.id, next);
  }, [customer.id]);
  const [akteDocsPanelOpenNonce, setAkteDocsPanelOpenNonce] = useState(0);

  const openAkteInHub = useCallback((akte: PortalBoundAkte | PortalAkteRecord) => {
    handleBoundAkteChange(akte);
    setView('akten');
  }, [handleBoundAkteChange]);

  const openAkteInArkoChat = useCallback((akte: PortalBoundAkte | PortalAkteRecord) => {
    handleBoundAkteChange(akte);
    if (!seatEntersBaustein('arko', bausteinSeat)) return;
    setAkteDocsPanelOpenNonce((n) => n + 1);
    setView('chat');
  }, [bausteinSeat, handleBoundAkteChange]);

  const handleAktenListChange = useCallback((rows: PortalAkteRecord[]) => {
    setPortalAktenList(rows);
  }, []);

  useEffect(() => {
    setBoundAkte(loadPortalBoundAkte(customer.id));
  }, [customer.id]);

  useEffect(() => {
    if (!boundAkte?.id) return;
    const row = portalAktenList.find((akte) => akte.id === boundAkte.id);
    if (!row) return;
    if ((row.title || '') === (boundAkte.title || '') && (row.nc_path || '') === (boundAkte.nc_path || '')) {
      return;
    }
    handleBoundAkteChange(row);
  }, [boundAkte, handleBoundAkteChange, portalAktenList]);

  useEffect(() => {
    const token = getPortalPbToken();
    if (!token) {
      setPortalAktenList([]);
      return;
    }
    let cancelled = false;
    void listPortalAktenForCustomer(customer.id, token, customer.kanzlei_id).then((rows) => {
      if (!cancelled) setPortalAktenList(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [customer.id, customer.kanzlei_id]);

  const portalWebhookExtraBody = useMemo(
    () => ({
      customerId: customer.id,
      kanzlei: customer.kanzlei,
      email: customer.email,
      tier: customer.tier,
      ...portalTenantN8nExtras(customer.status, customer.email),
      ...(boundAkte?.id
        ? {
            akteId: boundAkte.id,
            akteTitle: boundAkte.title,
            ...(boundAkte.nc_path ? { akte_nc_path: boundAkte.nc_path } : {}),
          }
        : {}),
    }),
    [customer.id, customer.kanzlei, customer.email, customer.tier, customer.status, boundAkte],
  );
  const [memOnboardingOpen, setMemOnboardingOpen] = useState(false);
  // Erststart-Interview (§7.7 Cold-Start): einmal pro Kunde, nur bei aktivem MEMCORE.
  useEffect(() => {
    if (memcoreEnabled && !isMemcoreOnboardingDone(customer.id)) {
      setMemOnboardingOpen(true);
    }
  }, [memcoreEnabled, customer.id]);

  // Logout: nur Auth + sessionStorage-Nav clearen. ARKO-Chat bleibt in localStorage
  // (`arkorbit_portal_arko_threads_v1__<customerId>`) — Verlauf weg nur per UI-Löschen.
  const handleLogout = useCallback(() => {
    endElementBrowserSession();
    clearSeatLoginTokens();
    clearPortalSessionState(customer.id);
    onLogout();
  }, [customer.id, onLogout]);
  const handleLogoutRef = useRef(handleLogout);
  handleLogoutRef.current = handleLogout;
  const onCustomerUpdateRef = useRef(onCustomerUpdate);
  onCustomerUpdateRef.current = onCustomerUpdate;

  // ── Lifted State: geoeffnetes Dokument & abgeleiteter `documentContext` ──
  // Andreas 27.05.2026 (Branch `pka/arko-document-context-unified`):
  // Wenn ein Portal-Dokument im Editor geoeffnet ist, soll der `documentContext`
  // an JEDEN ARKO-Chat-Request gehaengt werden — sowohl im Editor-Pane (rechts)
  // als auch im normalen ARKO-Chat-Tab (`view === 'chat'`). Dafuer muessen
  // `alphaDocSource`, `alphaDocReady`, der Plaintext-/OnlyOffice-Auszug und
  // der gebaute `documentContext`-String hier (`CustomerPortal`-Scope) leben,
  // nicht im inneren `DocumentEditorAlphaView` — sonst gehen sie beim
  // View-Wechsel verloren. Der innere View greift via Closure auf Setter zu.
  const [alphaDocSource, setAlphaDocSource] = useState<AlphaOnlyofficeDocumentSource | null>(null);
  const [alphaDocReady, setAlphaDocReady] = useState(false);
  const [akteDocSource, setAkteDocSource] = useState<AlphaOnlyofficeDocumentSource | null>(null);
  const [akteDocReady, setAkteDocReady] = useState(false);
  const [akteEditorAkteId, setAkteEditorAkteId] = useState<string | null>(null);
  const [akteOpenDocs, setAkteOpenDocs] = useState<AlphaOnlyofficeDocumentSource[]>([]);
  const [akteSaveFocus, setAkteSaveFocus] = useState<{
    akteId: string;
    docId: string;
    tick: number;
  } | null>(null);
  const [aktenHomeTick, setAktenHomeTick] = useState(0);
  const akteOnlyofficeEditorInstanceRef = useRef<OnlyofficeDocEditorHandle | null>(null);
  // Mehrere gleichzeitig geoeffnete OnlyOffice-Dokumente (Tab-Leiste). `alphaDocSource`
  // bleibt die einzige Wahrheit fuer den AKTIVEN Tab (Editor-Embed, Plaintext-Loader,
  // documentContext). `openDocuments` ist nur die Liste der offenen Tabs; der aktive
  // Tab wird aus `alphaDocSource` abgeleitet (kein zweiter State, kein Desync).
  const [openDocuments, setOpenDocuments] = useState<AlphaOnlyofficeDocumentSource[]>([]);
  const [openDocumentsLimitMsg, setOpenDocumentsLimitMsg] = useState<string | null>(null);
  /** Spiegel der aktiven Thread-Auswahl; Quelle der Wahrheit lebt im ARKO-Thread. */
  const [activeThreadSelectedDocIds, setActiveThreadSelectedDocIds] = useState<string[]>([]);
  const documentSelectionControllerRef = useRef<((documentId: string) => void) | null>(null);
  const handleDocumentSelectionControllerChange = useCallback(
    (controller: ((documentId: string) => void) | null) => {
      documentSelectionControllerRef.current = controller;
    },
    [],
  );
  const handleSelectedDocumentIdsChange = useCallback((selectedDocumentIds: string[]) => {
    setActiveThreadSelectedDocIds((current) => {
      if (
        current.length === selectedDocumentIds.length
        && current.every((id, index) => id === selectedDocumentIds[index])
      ) {
        return current;
      }
      return selectedDocumentIds;
    });
  }, []);
  /** Slice 1.3b: gecachter documentContext pro besuchtem Tab (nach Extract). */
  const [docContextCache, setDocContextCache] = useState<
    Record<
      string,
      {
        documentContext: string | null;
        documentExtractFingerprint: string | null;
        title: string;
        nc_path: string | null;
        fullExtractText?: string | null;
        extractPages?: number | null;
        extractRawChars?: number | null;
        extractTruncated?: boolean;
        extractSource?: string | null;
      }
    >
  >({});
  const [akteLazyExtractBusyIds, setAkteLazyExtractBusyIds] = useState<string[]>([]);
  const akteLazyExtractInFlightRef = useRef(new Set<string>());
  const akteLazyExtractAbortRef = useRef(new Map<string, AbortController>());
  const akteLazyExtractWatchdogRef = useRef(new Map<string, number>());
  const [nativeTextUi, setNativeTextUi] = useState<NativeTextPaneUi>({ kind: 'off' });
  const [onlyofficeExtractTextUi, setOnlyofficeExtractTextUi] =
    useState<OnlyofficeExtractTextUi>({ kind: 'off' });
  /** Bump nach OnlyOffice-Save → H2-Extraktion erneut (NC-Datei war beim ersten Open oft noch leer). */
  const [onlyofficeExtractRevision, setOnlyofficeExtractRevision] = useState(0);
  const onlyofficeExtractSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleOnlyofficeExtractRefresh = useCallback(() => {
    if (alphaDocSource && isLikelyBlankPortalDoc(alphaDocSource)) {
      /* Autosave-Stub: leeres/neues DOCX nicht extracten — sonst F12 POST 422. */
      return;
    }
    if (onlyofficeExtractSaveDebounceRef.current) {
      clearTimeout(onlyofficeExtractSaveDebounceRef.current);
    }
    onlyofficeExtractSaveDebounceRef.current = setTimeout(() => {
      onlyofficeExtractSaveDebounceRef.current = null;
      if (alphaDocSource) {
        portalOnlyofficeRateLimitClearInsufficient(
          portalOnlyofficeRateLimitKey('extract', documentTabId(alphaDocSource)),
        );
      }
      setOnlyofficeExtractRevision((v) => v + 1);
    }, 1200);
  }, [alphaDocSource]);
  /** Verhindert, dass der PB-Start-Pull ein manuell geöffnetes Dokument überschreibt. */
  const userPinnedAlphaDocRef = useRef(false);
  /** Slice 1.3b V2: OnlyOffice-Instanz des aktiven Tabs (für Synthese-Einfügen). */
  const onlyofficeEditorInstanceRef = useRef<OnlyofficeDocEditorHandle | null>(null);
  const closeAkteOnlyoffice = useCallback(() => {
    setAkteDocSource(null);
    setAkteDocReady(false);
    setAkteEditorAkteId(null);
    setAkteOpenDocs([]);
    akteOnlyofficeEditorInstanceRef.current = null;
  }, []);
  /** `onAppReady` gehört exakt zu diesem Dokument-Tab; State allein gilt nicht als ready. */
  const onlyofficeAppReadyDocumentIdRef = useRef<string | null>(null);

  // ── Unsaved-Guard: Browser-/Tab-Schließen warnt bei ungespeicherten OnlyOffice-
  // Änderungen. OnlyOffice meldet den modified-State via events.onDocumentStateChange
  // (data === true). Es lebt immer nur der aktive Editor; bei Tab-Wechsel/Unmount
  // speichert `requestClose` (status 2/6, siehe closeOnlyofficeEditorInstance), darum
  // wird das Flag im Editor-Cleanup geleert — stale-true bei inaktiven Tabs vermeiden.
  //
  // WICHTIG (Re-Init-Bug 06/2026): Der Dirty-Zustand lebt bewusst in einem `ref`,
  // NICHT in React-State. `setOnlyofficeTabDirty` wird bei jedem Tastendruck via
  // onDocumentStateChange aufgerufen; ein State-Update würde `CustomerPortal`
  // re-rendern und damit den inline definierten `DocumentEditorAlphaView` neu
  // mounten → Editor-Reinit bei jedem Buchstaben. Der ref-Ansatz löst keinen
  // Render aus; der beforeunload-Handler liest den ref erst beim Schließen.
  const dirtyOnlyofficeTabsRef = useRef<Set<string>>(new Set());
  const setOnlyofficeTabDirty = useCallback((tabId: string, dirty: boolean) => {
    if (dirty) dirtyOnlyofficeTabsRef.current.add(tabId);
    else dirtyOnlyofficeTabsRef.current.delete(tabId);
  }, []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyOnlyofficeTabsRef.current.size === 0) return;
      // Browser-nativer Bestätigungsdialog. Text ist aus Sicherheitsgründen nicht
      // anpassbar; Chrome verlangt das Setzen von returnValue.
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Protokoll-/Dokumente-Hub-State vor Effects, die `protokolle` in deps/body nutzen (TDZ).
  const [protokolle, setProtokolle] = useState<Protokoll[]>([]);
  const protokolleRef = useRef<Protokoll[]>([]);
  protokolleRef.current = protokolle;
  const [protokolleLoading, setProtokolleLoading] = useState(false);
  const protokolleLoadSeqRef = useRef(0);
  const [protokolleError, setProtokolleError] = useState('');
  const [recentDocs, setRecentDocs] = useState<PortalRecentDocument[]>([]);
  /** Letzter Ordner im Dokument-Hub; bleibt beim Wechsel in Chat/Editor erhalten. */
  const [documentHubCurrentFolder, setDocumentHubCurrentFolder] = useState('');

  /**
   * Oeffnet/aktiviert ein Dokument: in die Tab-Liste aufnehmen (oder bestehenden
   * Tab mit frischer Quelle ersetzen) und als aktiven Editor-Tab setzen.
   * `pin` markiert eine bewusste Nutzeraktion, damit der initiale PB-Pull sie
   * nicht ueberschreibt.
   */
  const activateOpenDocument = useCallback(
    (source: AlphaOnlyofficeDocumentSource, opts?: { pin?: boolean }): boolean => {
      const id = documentTabId(source);
      let accepted = true;
      setOpenDocuments((prev) => {
        const idx = prev.findIndex((d) => documentTabId(d) === id);
        if (idx >= 0) {
          const next = prev.slice();
          next[idx] = source;
          return next;
        }
        if (prev.length >= PORTAL_MAX_OPEN_DOCUMENTS) {
          accepted = false;
          return prev;
        }
        return [...prev, source];
      });
      if (!accepted) {
        setOpenDocumentsLimitMsg(
          `Maximal ${PORTAL_MAX_OPEN_DOCUMENTS} Dokumente gleichzeitig geöffnet. Bitte einen Tab schließen, dann erneut öffnen.`,
        );
        return false;
      }
      setOpenDocumentsLimitMsg(null);
      setAlphaDocSource(source);
      setAlphaDocReady(true);
      if (opts?.pin) userPinnedAlphaDocRef.current = true;
      return true;
    },
    [],
  );

  const pinAlphaDocSource = useCallback(
    (source: AlphaOnlyofficeDocumentSource): boolean => {
      return activateOpenDocument(source, { pin: true });
    },
    [activateOpenDocument],
  );

  /** Tab-Wechsel: gespeicherte Quelle (stabiler `document.key`) reaktivieren → DS-Cache. */
  const selectDocumentTab = useCallback(
    (id: string) => {
      const doc = openDocuments.find((d) => documentTabId(d) === id);
      if (!doc) return;
      userPinnedAlphaDocRef.current = true;
      setAlphaDocSource(doc);
      setAlphaDocReady(true);
      setDokumenteAreaTab('editor');
      setView('document_editor');
    },
    [openDocuments],
  );

  /** Tab schliessen: bei aktivem Tab Nachbarn aktivieren, sonst zurueck in die Liste. */
  const closeDocumentTab = useCallback(
    (id: string) => {
      const remaining = openDocuments.filter((d) => documentTabId(d) !== id);
      setOpenDocuments(remaining);
      if (remaining.length < PORTAL_MAX_OPEN_DOCUMENTS) {
        setOpenDocumentsLimitMsg(null);
      }
      setOnlyofficeTabDirty(id, false);
      const wasActive = alphaDocSource != null && documentTabId(alphaDocSource) === id;
      if (!wasActive) return;
      if (remaining.length > 0) {
        const fallback = remaining[remaining.length - 1]!;
        userPinnedAlphaDocRef.current = true;
        setAlphaDocSource(fallback);
        setAlphaDocReady(true);
      } else {
        userPinnedAlphaDocRef.current = false;
        setAlphaDocSource(null);
        setAlphaDocReady(false);
        setNativeTextUi({ kind: 'off' });
        setOnlyofficeExtractTextUi({ kind: 'off' });
        setView('dokumente');
        setDokumenteAreaTab('hub');
      }
    },
    [openDocuments, alphaDocSource, setOnlyofficeTabDirty],
  );

  const hasActivePortalDocument = Boolean(isPremium && alphaDocReady && alphaDocSource);
  const isDokumenteEditorPaneVisible =
    view === 'document_editor' && isPremium && dokumenteAreaTab === 'editor';
  const [docWorkModeOpen, setDocWorkModeOpen] = useState(false);
  const [onlyOfficeTheme, setOnlyOfficeThemeState] = useState<PortalOnlyOfficeTheme>(() =>
    readPortalOnlyOfficeTheme(),
  );
  const setOnlyOfficeTheme = useCallback((theme: PortalOnlyOfficeTheme) => {
    writePortalOnlyOfficeTheme(theme);
    setOnlyOfficeThemeState(theme);
  }, []);
  const isDokumenteHubMode =
    view === 'dokumente' || (view === 'document_editor' && dokumenteAreaTab === 'hub');
  const isDokumenteWorkspaceVisible =
    isDokumenteHubMode || (view === 'document_editor' && isPremium);

  const portalDocumentTabTitle = useMemo(() => {
    if (!alphaDocSource) return null;
    return (
      alphaDocSource.portalDocumentDisplayName?.trim() ||
      alphaDocSource.title?.trim() ||
      null
    );
  }, [alphaDocSource]);

  const activeDocumentId = useMemo(
    () => (alphaDocSource ? documentTabId(alphaDocSource) : null),
    [alphaDocSource],
  );

  /**
   * Aktiviert den Ziel-Tab, mountet den Dokument-Workspace und wartet auf den
   * tatsächlich sichtbaren Split. Damit ist `alphaDocSource` nicht länger mit
   * „Editor geöffnet“ gleichgesetzt.
   */
  const openDocumentInSplit = useCallback(
    async (source?: AlphaOnlyofficeDocumentSource): Promise<boolean> => {
      const target = source ?? alphaDocSource;
      if (!target) return false;
      const targetDocumentId = documentTabId(target);
      if (import.meta.env.DEV) console.debug('[synthesis-apply]', 'activate-editor');
      // Der Klick kommt aus dem ARKO-Chat. Alle UI-Schalter gemeinsam und synchron
      // committen, damit der folgende Wait nicht nur logischen Dokument-State sieht.
      let activated = false;
      flushSync(() => {
        activated = activateOpenDocument(target, { pin: true });
        if (!activated) return;
        setDokumenteAreaTab('editor');
        setDocWorkModeOpen(false);
        setMobileOpen(false);
        setSettingsOpen(false);
        setView('document_editor');
      });
      if (!activated) return false;
      const split = await waitForVisibleDocumentSplit(targetDocumentId);
      if (split) {
        split.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        split.focus({ preventScroll: true });
      }
      if (import.meta.env.DEV) {
        console.debug('[synthesis-apply]', split ? 'editor-visible' : 'editor-timeout');
      }
      return Boolean(split);
    },
    [activateOpenDocument, alphaDocSource],
  );

  useEffect(() => {
    setOnlyofficeExtractRevision(0);
    setOnlyofficeExtractTextUi({ kind: 'off' });
  }, [activeDocumentId]);

  const documentTabsItems = useMemo(
    () =>
      openDocuments.map((d) => {
        const id = documentTabId(d);
        return {
          id,
          title: documentTabTitle(d),
          active: id === activeDocumentId,
          synthesisSelected: activeThreadSelectedDocIds.includes(id),
        };
      }),
    [openDocuments, activeDocumentId, activeThreadSelectedDocIds],
  );

  const toggleSynthesisDocument = useCallback((id: string) => {
    documentSelectionControllerRef.current?.(id);
  }, []);

  const portalOpenDocumentsMeta = useMemo(
    () => buildPortalOpenDocumentsMeta(openDocuments, activeDocumentId),
    [openDocuments, activeDocumentId],
  );

  const synthesisSelectAtMax =
    activeThreadSelectedDocIds.length >= PORTAL_SYNTHESIS_MAX_SELECTED_DOCS;

  const handlePortalNavigate = useCallback(
    (next: PortalView) => {
      if (!portalViewAllowed(next, bausteinSeat)) {
        setView('home');
        return;
      }
      if (next === 'dokumente') {
        if (hasActivePortalDocument) {
          // Aktives Dokument offen: zurueck in den Dokument-Editor-Bereich, ohne
          // den zuletzt gewaehlten Unter-Reiter (Editor/Liste) zu verwerfen.
          // Sonst landet der Nutzer nach einem Abstecher (z. B. Diktat) auf der
          // Liste statt am offenen OnlyOffice-Dokument ("Editor weg").
          setView('document_editor');
        } else {
          setView('dokumente');
          setDokumenteAreaTab('hub');
        }
        return;
      }
      if (next === 'chat') {
        setView('chat');
        return;
      }
      if (next === 'akten') {
        // Nur wenn man schon in Akten ist: Fallliste (bewusster Home-Klick).
        // Rückkehr von Suche/Diktat/anderen Reitern: Position wiederherstellen.
        if (aktenNavShouldShowFallliste(view, next)) {
          setAktenHomeTick((n) => n + 1);
        }
        setView('akten');
        return;
      }
      if (next === 'matrix' && customer.elementSeat !== true) {
        setView('home');
        return;
      }
      if (next === 'contract_generator') {
        setContractCreatorTemplateId(null);
      }
      setView(next);
    },
    [bausteinSeat, hasActivePortalDocument, customer.elementSeat, view],
  );

  const viewRef = useRef(view);
  viewRef.current = view;
  const recordedPortalViewRef = useRef<PortalView | null>(null);
  const portalHistorySourceRef = useRef<'init' | 'navigate' | 'pop'>('init');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const canonical = canonicalizePortalView(view);
    if (!canonical) return;
    const mode = portalHistoryRecordMode({
      previousRecorded: recordedPortalViewRef.current,
      nextView: canonical,
      source: portalHistorySourceRef.current,
    });
    recordedPortalViewRef.current = canonical;
    if (portalHistorySourceRef.current === 'init' || portalHistorySourceRef.current === 'pop') {
      portalHistorySourceRef.current = 'navigate';
    }
    if (mode === 'skip') return;
    applyPortalViewHistory(mode, canonical);
  }, [view]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const next = readPortalViewFromLocation(window.location.hash, window.history.state);
      if (!next) return;
      const canonical = canonicalizePortalView(next);
      if (!canonical) return;
      portalHistorySourceRef.current = 'pop';
      recordedPortalViewRef.current = canonical;
      if (canonical === viewRef.current) return;
      setView(canonical);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (!isDokumenteEditorPaneVisible) return;
    const timer = window.setTimeout(() => window.dispatchEvent(new Event('resize')), 80);
    return () => window.clearTimeout(timer);
  }, [isDokumenteEditorPaneVisible]);

  useEffect(() => {
    if (isDokumenteEditorPaneVisible) return;
    setDocWorkModeOpen(false);
  }, [isDokumenteEditorPaneVisible]);

  useEffect(() => {
    if (!docWorkModeOpen) return;
    const onEsc = () => setDocWorkModeOpen(false);
    window.addEventListener('arkorbit:portal-escape', onEsc);
    return () => window.removeEventListener('arkorbit:portal-escape', onEsc);
  }, [docWorkModeOpen]);

  useEffect(() => {
    if (!docWorkModeOpen) return;
    const timer = window.setTimeout(() => window.dispatchEvent(new Event('resize')), 120);
    return () => window.clearTimeout(timer);
  }, [docWorkModeOpen]);

  // Initialer Doc-Pull aus PB (oder Demo-Fallback). Nur wenn Editor-View aktiv —
  // Session-Restore separat nach PB-Listen-Validierung (stale Shares → Extract-502).
  useEffect(() => {
    let cancelled = false;
    if (view !== 'document_editor' || !isPremium) {
      return () => {
        cancelled = true;
      };
    }
    if (userPinnedAlphaDocRef.current || alphaDocReady) {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      let token: string | null = null;
      try {
        token = getPortalPbToken();
      } catch {
        token = null;
      }

      const fromPb = await fetchAlphaDocumentSourceFromPb(customer.id, token, customer.status, customer.email);
      if (cancelled || userPinnedAlphaDocRef.current) return;

      if (fromPb) {
        activateOpenDocument(fromPb);
        return;
      }

      if (isIsolatedDemoPortalTenant(undefined, customer.status, customer.email)) {
        setAlphaDocReady(false);
        setAlphaDocSource(null);
        return;
      }

      activateOpenDocument({
        url: ONLYOFFICE_ALPHA_DEMO_DOCUMENT_URL,
        title: 'Demo.docx',
        key: sanitizeOnlyofficeDocumentKey(`alpha-${customer.id}-${Date.now()}`),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [customer.id, customer.status, isPremium, view, alphaDocReady, activateOpenDocument]);

  useEffect(() => {
    if (protokolleLoading || view !== 'document_editor' || !isPremium) return;
    if (userPinnedAlphaDocRef.current || alphaDocReady) return;

    const session = loadPortalSessionState(customer.id);
    if (!session?.document) return;

    const pbId = session.document.pb_record_id?.trim();
    if (protokolle.length === 0) {
      savePortalSessionState(customer.id, { document: null });
      setView('dokumente');
      return;
    }
    if (pbId && !protokolle.some((p) => p.id === pbId)) {
      savePortalSessionState(customer.id, { document: null });
      setView('dokumente');
      return;
    }

    const restored = buildAlphaDocSourceFromPortalDocumentFields(
      customer.id,
      session.document,
      customer.status,
      customer.email,
    );
    if (!restored) return;
    activateOpenDocument(restored, { pin: true });
  }, [
    protokolle,
    protokolleLoading,
    view,
    isPremium,
    alphaDocReady,
    customer.id,
    customer.status,
    activateOpenDocument,
  ]);

  useEffect(() => {
    const doc =
      view === 'document_editor' && alphaDocSource?.url
        ? {
            display_name:
              alphaDocSource.portalDocumentDisplayName?.trim() ||
              alphaDocSource.title?.trim() ||
              'Dokument',
            document_url: alphaDocSource.url,
            pb_record_id: alphaDocSource.pbRecordId,
            nextcloud_path: alphaDocSource.nextcloudPathFromPb,
          }
        : null;
    savePortalSessionState(customer.id, { view, document: doc, lastArkoIntent });
  }, [customer.id, view, alphaDocSource, lastArkoIntent]);

  // Plaintext-Pfad: laedt UTF-8-Auszug fuer .txt/.md/.csv via NC oder Proxy.
  useEffect(() => {
    if (!alphaDocReady || !alphaDocSource) {
      setNativeTextUi({ kind: 'off' });
      return;
    }
    const ext = resolveAlphaDocRoutingExtension(alphaDocSource);
    if (!isPortalNativePlainTextExtension(ext)) {
      setNativeTextUi({ kind: 'off' });
      return;
    }

    let cancelled = false;
    setNativeTextUi({ kind: 'loading' });

    (async () => {
      try {
        const usePlaintextProxy = Boolean(PORTAL_PLAINTEXT_FETCH_URL);
        let ab: ArrayBuffer;
        if (usePlaintextProxy) {
          const proxyUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_PLAINTEXT_FETCH_URL);
          const plainRec: Record<string, unknown> = {
            document_url: alphaDocSource.url,
            url: alphaDocSource.url,
            nextcloud_path: alphaDocSource.nextcloudPathFromPb,
            nextcloud_webdav_path: alphaDocSource.nextcloudWebdavPath,
            nextcloudWebdavPath: alphaDocSource.nextcloudWebdavPath,
            display_name: alphaDocSource.portalDocumentDisplayName ?? alphaDocSource.title,
            portalDocumentDisplayName: alphaDocSource.portalDocumentDisplayName,
          };
          const plainBody = buildPlaintextFetchBody(plainRec, customer.id, customer.status, customer.email);
          const res = await fetch(proxyUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...portalAuthzHeaders(proxyUrl),
            },
            credentials: 'omit',
            body: JSON.stringify(plainBody),
          });
          const data = (await res.json()) as {
            ok?: boolean;
            text_utf8_base64?: string;
            error?: string;
          };
          if (!res.ok || !data.ok || typeof data.text_utf8_base64 !== 'string') {
            logPortalError('Klartext-Proxy', { status: res.status, error: data.error });
            throw new Error(
              data.error?.trim() || formatHttpStatusForUser(res.status, 'download'),
            );
          }
          ab = portalPlaintextBase64ToArrayBuffer(data.text_utf8_base64);
        } else {
          const res = await fetch(alphaDocSource.url, { credentials: 'omit', mode: 'cors' });
          if (!res.ok) {
            logPortalError('Dokument-Download', res.status);
            throw new Error(formatHttpStatusForUser(res.status, 'download'));
          }
          ab = await res.arrayBuffer();
        }
        const text = decodePortalPlainTextUtf8(ab);
        if (!cancelled) {
          setNativeTextUi({
            kind: 'ready',
            documentId: documentTabId(alphaDocSource),
            text,
          });
        }
      } catch (e) {
        const raw = e instanceof Error ? e.message : String(e);
        const friendly = /NetworkError|Failed to fetch|fetch|CORS|blocked|network/i.test(raw)
          ? formatConnectionError(raw)
          : raw;
        if (!cancelled) {
          setNativeTextUi({
            kind: 'error',
            message: friendly,
            documentUrl: alphaDocSource.url,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [alphaDocReady, alphaDocSource, customer.id]);

  // OnlyOffice-Pfad: serverseitiger Volltext (PORTAL_ONLYOFFICE_EXTRACT_TEXT).
  // Lifted state wie nativeTextUi — bleibt über View-Wechsel (Editor-Pane ↔ ARKO-Tab) erhalten.
  useEffect(() => {
    if (!alphaDocReady || !alphaDocSource) {
      setOnlyofficeExtractTextUi({ kind: 'off' });
      return;
    }
    if (isPortalAlphaDemoDocumentUrl(alphaDocSource.url)) {
      setOnlyofficeExtractTextUi({ kind: 'off' });
      return;
    }
    const ext = resolveAlphaDocRoutingExtension(alphaDocSource);
    if (isPortalNativePlainTextExtension(ext)) {
      setOnlyofficeExtractTextUi({ kind: 'off' });
      return;
    }
    if (!PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL) {
      setOnlyofficeExtractTextUi({ kind: 'off' });
      return;
    }
    // Firma-nginx ohne portal-authz: Extract-Gate braucht Sidecar-Header oder Upload-Secret.
    // Secret-Cutover-FE sendet nur Bearer → 401 + rote Konsole; Editor (JWT-Bridge) bleibt ok.
    if (!portalAuthzSidecarExpected()) {
      setOnlyofficeExtractTextUi({ kind: 'off' });
      return;
    }

    const extractRateKey = portalOnlyofficeRateLimitKey('extract', activeDocumentId ?? 'unknown');
    if (portalOnlyofficeRateLimitBlocked(extractRateKey)) {
      const remainSec = Math.ceil(portalOnlyofficeRateLimitRemainingMs(extractRateKey) / 1000);
      setOnlyofficeExtractTextUi({
        kind: 'error',
        message: `Zu viele Anfragen in kurzer Zeit. Bitte ${remainSec} Sekunden warten und erneut versuchen.`,
      });
      return;
    }
    if (portalOnlyofficeRateLimitInsufficientBlocked(extractRateKey)) {
      setOnlyofficeExtractTextUi({ kind: 'insufficient' });
      return;
    }
    if (onlyofficeExtractRevision === 0 && isLikelyBlankPortalDoc(alphaDocSource)) {
      setOnlyofficeExtractTextUi({ kind: 'insufficient' });
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();
    const debounceMs =
      onlyofficeExtractRevision > 0 ? 400 : PORTAL_ONLYOFFICE_EXTRACT_OPEN_DEBOUNCE_MS;
    const debounceTimer = window.setTimeout(() => {
      if (cancelled) return;
      setOnlyofficeExtractTextUi((prev) =>
        prev.kind === 'loading' ? prev : { kind: 'loading' },
      );

      void (async () => {
        try {
          const extractUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL);
          const ncPathFromPb =
            typeof alphaDocSource.nextcloudPathFromPb === 'string'
              ? alphaDocSource.nextcloudPathFromPb.trim()
              : '';
          const displayName =
            alphaDocSource.portalDocumentDisplayName?.trim() ||
            alphaDocSource.title?.trim() ||
            'Dokument';
          const pbId = alphaDocSource.pbRecordId?.trim();
          const hubList = protokolleRef.current;
          let documentUrlForExtract = resolveProtokollDocumentUrl(hubList, {
            pb_record_id: pbId,
            document_url: alphaDocSource.url,
          });
          const pbKnownInHub = pbId ? hubList.some((p) => p.id === pbId) : false;
          if (pbId && pbKnownInHub && documentUrlForExtract === alphaDocSource.url) {
            const token = getPortalPbToken();
            if (token) {
              const fresh = await fetchPortalProtokollById(
                getPortalPocketBaseUrl(),
                PORTAL_PROTOKOLLE_COLLECTION,
                pbId,
                token,
              );
              if (fresh?.nc_url) documentUrlForExtract = fresh.nc_url;
            }
          }
          if (cancelled || abortController.signal.aborted) return;
          const ncPathForExtract =
            ncPathFromPb ||
            alphaDocSource.nextcloudWebdavPath?.trim() ||
            inferNextcloudPortalDocumentsPath(
              customer.id,
              displayName,
              getPortalNcMandantenRoot(customer.status, customer.email),
              customer.kanzlei_id,
            );
          const hubMime =
            pbId
              ? hubList.find((p) => p.id === pbId)?.mime_type
              : undefined;
          const extractFileType = resolvePortalExtractFileType({
            displayName,
            nextcloudPath: ncPathForExtract,
            documentUrl: documentUrlForExtract,
            mimeType: hubMime,
          });
          if (!extractFileType) {
            if (!cancelled) setOnlyofficeExtractTextUi({ kind: 'off' });
            return;
          }
          const displayNameForExtract =
            !fileExtensionFromName(displayName)
              ? `${displayName}.${extractFileType}`
              : displayName;
          const reqBody: Record<string, string> = {
            document_url: documentUrlForExtract,
            display_name: displayNameForExtract,
            customerId: customer.id,
            filetype: extractFileType,
            ...portalTenantN8nExtras(customer.status, customer.email),
          };
          if (extractFileType === 'pdf') reqBody.prefer_local = '1';
          if (ncPathForExtract) {
            reqBody.nextcloud_path = normalizePortalWebdavPathFromPb(ncPathForExtract);
          }
          if (hubMime) reqBody.mime_type = hubMime;
          const result = await fetchAkteDocumentExtractText({
            fetchFn: fetch,
            url: extractUrl,
            body: reqBody,
            headers: portalAuthzHeaders(extractUrl),
            signal: abortController.signal,
            timeoutMs: PORTAL_AKTE_LAZY_EXTRACT_TIMEOUT_MS,
          });
          if (cancelled || abortController.signal.aborted) return;
          let settled = result;
          if (isPendingAkteExtractHttpResult(result)) {
            settled = await waitForAkteDocumentExtractJob({
              fetchFn: fetch,
              url: extractUrl,
              jobId: result.jobId,
              customerId: customer.id,
              extraBody: portalTenantN8nExtras(customer.status, customer.email),
              headers: portalAuthzHeaders(extractUrl),
              signal: abortController.signal,
            });
          }
          if (cancelled || abortController.signal.aborted) return;
          if (isAkteLazyExtractHttpFailure(settled)) {
            if (settled.status === 429) {
              portalOnlyofficeRateLimitMark429(extractRateKey);
              logPortalExtractOutcome({ status: settled.status, error: settled.error });
              throw new Error(
                settled.error?.trim() || formatHttpStatusForUser(settled.status, 'editor'),
              );
            }
            if (settled.status === 422 || isSoftAkteExtractHttpStatus(settled.status)) {
              portalOnlyofficeRateLimitMarkInsufficient(extractRateKey);
              logPortalExtractOutcome({
                status: settled.status,
                error: settled.error,
                pending: settled.pending === true,
              });
              if (!cancelled) setOnlyofficeExtractTextUi({ kind: 'insufficient' });
              return;
            }
            const missingExt =
              settled.status === 415 &&
              /fileType nicht ermittelbar/i.test(String(settled.error || ''));
            if (settled.status === 401 || settled.status === 403 || settled.status === 400 || missingExt) {
              if (!cancelled) setOnlyofficeExtractTextUi({ kind: 'off' });
              return;
            }
            logPortalExtractOutcome({ status: settled.status, error: settled.error });
            throw new Error(
              settled.error?.trim() || formatHttpStatusForUser(settled.status, 'editor'),
            );
          }
          const text = decodePortalPlainTextUtf8(
            portalPlaintextBase64ToArrayBuffer(settled.textBase64),
          );
          if (cancelled) return;
          if (!isMeaningfulPortalExtractText(text)) {
            portalOnlyofficeRateLimitMarkInsufficient(extractRateKey);
            setOnlyofficeExtractTextUi({ kind: 'insufficient' });
            return;
          }
          portalOnlyofficeRateLimitClearInsufficient(extractRateKey);
          setOnlyofficeExtractTextUi({
            kind: 'ready',
            documentId: activeDocumentId ?? documentTabId(alphaDocSource),
            text,
            rawBytes: settled.ok ? settled.rawBytes : undefined,
            truncated: settled.ok ? settled.truncated : undefined,
            pdfPages: settled.ok ? settled.pdfPages : undefined,
            source: settled.ok ? settled.source : undefined,
          });
        } catch (e) {
          if (abortController.signal.aborted || cancelled) return;
          if (e instanceof DOMException && e.name === 'AbortError') return;
          const raw = e instanceof Error ? e.message : String(e);
          setOnlyofficeExtractTextUi({ kind: 'error', message: raw });
        }
      })();
    }, debounceMs);

    return () => {
      cancelled = true;
      abortController.abort();
      window.clearTimeout(debounceTimer);
    };
  }, [
    alphaDocReady,
    activeDocumentId,
    alphaDocSource,
    customer.id,
    customer.status,
    customer.email,
    onlyofficeExtractRevision,
  ]);

  const plainTextDocumentChrome =
    alphaDocSource != null &&
    isPortalNativePlainTextExtension(resolveAlphaDocRoutingExtension(alphaDocSource));

  /**
   * Echter `documentContext` fuer n8n `ARKO_AGENT` (Portal-Zweig).
   * - Plaintext-Pfad: gekappter UTF-8-Auszug aus `nativeTextUi`.
   * - OnlyOffice-Pfad mit H2-Extraktion ready: gekappter Volltext aus `onlyofficeExtractTextUi`.
   * - OnlyOffice-Pfad ohne H2 (Endpoint leer / Fehler / unsupported / loading): H1-Fallback.
   * Fehlerfall / leer / Source noch nicht geladen -> `null` (Workspace laesst Feld weg).
   */
  const portalArkoDocumentContextRaw = useMemo<string | null>(() => {
    if (!alphaDocReady || !alphaDocSource) return null;
    const plainText =
      plainTextDocumentChrome
      && nativeTextUi.kind === 'ready'
      && nativeTextUi.documentId === activeDocumentId
        ? nativeTextUi.text
        : null;
    if (
      !plainTextDocumentChrome &&
      (onlyofficeExtractTextUi.kind === 'loading' || onlyofficeExtractTextUi.kind === 'insufficient')
    ) {
      // H2 lädt noch oder NC/DS noch ohne Inhalt — kein irreführender H1-Hinweis an ARKO.
      return null;
    }
    if (!plainTextDocumentChrome && onlyofficeExtractTextUi.kind === 'error') {
      return null;
    }
    const extractedText =
      !plainTextDocumentChrome
      && onlyofficeExtractTextUi.kind === 'ready'
      && onlyofficeExtractTextUi.documentId === activeDocumentId
        ? onlyofficeExtractTextUi.text
        : null;
    return buildPortalArkoDocumentContext({
      source: alphaDocSource,
      plainText,
      isPlainTextRoute: plainTextDocumentChrome,
      extractedText,
      extractSource: extractedText && onlyofficeExtractTextUi.kind === 'ready'
        ? onlyofficeExtractTextUi.source
        : null,
    });
  }, [alphaDocReady, alphaDocSource, plainTextDocumentChrome, nativeTextUi, onlyofficeExtractTextUi]);

  const portalParagraphIndexMap = useMemo(() => {
    if (!portalArkoDocumentContextRaw) return [];
    return buildParagraphIndexMap(portalArkoDocumentContextRaw);
  }, [portalArkoDocumentContextRaw]);

  const portalArkoDocumentContext = useMemo<string | null>(() => {
    if (!portalArkoDocumentContextRaw) return null;
    if (!portalParagraphIndexMap.length) return portalArkoDocumentContextRaw;
    return appendParagraphMapToDocumentContext(portalArkoDocumentContextRaw, portalParagraphIndexMap);
  }, [portalArkoDocumentContextRaw, portalParagraphIndexMap]);

  const portalDocumentDisplayName = useMemo(() => {
    if (!alphaDocSource) return null;
    return (
      alphaDocSource.portalDocumentDisplayName?.trim() ||
      alphaDocSource.title?.trim() ||
      null
    );
  }, [alphaDocSource]);

  const defaultDocumentContextEnabled = useMemo(() => {
    if (boundAkte?.id) return true;
    if (!portalArkoDocumentContext) return false;
    // Editor-Split: immer an; ARKO-Tab: an wenn Dokument geöffnet (Smoke I — gleicher Kontext).
    return isDokumenteEditorPaneVisible || (view === 'chat' && hasActivePortalDocument);
  }, [boundAkte?.id, portalArkoDocumentContext, isDokumenteEditorPaneVisible, view, hasActivePortalDocument]);

  const portalDocumentNcPath = useMemo(() => {
    if (!alphaDocSource) return null;
    return (
      alphaDocSource.nextcloudPathFromPb?.trim() ||
      alphaDocSource.nextcloudWebdavPath?.trim() ||
      null
    );
  }, [alphaDocSource]);

  const portalDocumentExtractFingerprint = useMemo(() => {
    if (!alphaDocReady || !alphaDocSource || !portalArkoDocumentContext) return null;
    return buildDocumentExtractFingerprint({
      documentId: documentTabId(alphaDocSource),
      extractRevision: onlyofficeExtractRevision,
      contextCharCount: portalArkoDocumentContext.length,
    });
  }, [
    alphaDocReady,
    alphaDocSource,
    portalArkoDocumentContext,
    onlyofficeExtractRevision,
  ]);

  const handleMarkPortalDocumentAnalyzed = useCallback(() => {
    const fp = portalDocumentExtractFingerprint;
    if (!fp) return;
    markDocumentAnalyzeDone(customer.id, fp, onlyofficeExtractRevision);
  }, [customer.id, portalDocumentExtractFingerprint, onlyofficeExtractRevision]);

  useEffect(() => {
    if (!activeDocumentId || !alphaDocSource) return;
    const cappedContext = capAkteExtractDocumentContext(portalArkoDocumentContext);
    const ncPath =
      alphaDocSource.nextcloudPathFromPb?.trim() ||
      alphaDocSource.nextcloudWebdavPath?.trim() ||
      null;
    const title = documentTabTitle(alphaDocSource);
    const rawFull =
      (plainTextDocumentChrome && nativeTextUi.kind === 'ready' && nativeTextUi.documentId === activeDocumentId
        ? nativeTextUi.text
        : null)
      || (onlyofficeExtractTextUi.kind === 'ready' && onlyofficeExtractTextUi.documentId === activeDocumentId
        ? onlyofficeExtractTextUi.text
        : null);
    const extractMeta = onlyofficeExtractTextUi.kind === 'ready' && onlyofficeExtractTextUi.documentId === activeDocumentId
      ? {
        extractPages: onlyofficeExtractTextUi.pdfPages,
        extractRawChars: onlyofficeExtractTextUi.rawBytes,
        extractTruncated: onlyofficeExtractTextUi.truncated,
        extractSource: onlyofficeExtractTextUi.source,
      }
      : {};
    setDocContextCache((prev) => ({
      ...prev,
      [activeDocumentId]: {
        documentContext: preferRicherAkteDocumentContext(
          prev[activeDocumentId]?.documentContext,
          cappedContext,
        ),
        documentExtractFingerprint: portalDocumentExtractFingerprint,
        title,
        nc_path: ncPath,
        fullExtractText: preferLongerAkteExtract(
          prev[activeDocumentId]?.fullExtractText,
          rawFull,
        ),
        extractPages: extractMeta.extractPages || prev[activeDocumentId]?.extractPages || null,
        extractRawChars: extractMeta.extractRawChars || prev[activeDocumentId]?.extractRawChars || null,
        extractTruncated: extractMeta.extractTruncated ?? prev[activeDocumentId]?.extractTruncated,
        extractSource: extractMeta.extractSource || prev[activeDocumentId]?.extractSource || null,
      },
    }));
    const pbId = pbRecordIdFromPortalDocumentId(activeDocumentId);
    const proto = pbId ? protokolle.find((row) => row.id === pbId) : undefined;
    const revFp = buildAkteDocumentRevFingerprint({
      updated: proto?.updated,
      fileSize: proto?.file_size,
      ncPath: proto?.nextcloud_path || ncPath,
      lastRevisionId: proto?.last_revision_id,
    });
    if (hasUsableAkteExtract(cappedContext) && isAkteDocumentRevFingerprintReady(revFp)) {
      saveUsableAkteExtractToCache(customer.id, {
        documentId: activeDocumentId,
        fingerprint: revFp,
        documentContext: cappedContext!,
        title,
        nc_path: ncPath,
        fullExtractText: rawFull?.trim() || undefined,
        extractPages: extractMeta.extractPages,
        extractRawChars: extractMeta.extractRawChars,
        extractTruncated: extractMeta.extractTruncated,
        extractSource: extractMeta.extractSource,
      });
    }
  }, [
    activeDocumentId,
    alphaDocSource,
    customer.id,
    portalArkoDocumentContext,
    portalDocumentExtractFingerprint,
    protokolle,
    plainTextDocumentChrome,
    nativeTextUi,
    onlyofficeExtractTextUi,
  ]);

  const portalAvailableDocuments = useMemo((): PortalSelectedDocumentPayload[] => {
    return portalOpenDocumentsMeta.map((meta) =>
      portalSelectedDocumentFromMeta(meta, docContextCache[meta.documentId]),
    );
  }, [docContextCache, portalOpenDocumentsMeta]);

  const portalDocumentAkteNcPath = useMemo(() => {
    const own = effectivePortalAkteNcPath(boundAkte);
    if (own) return own;
    if (boundAkte?.id) return null;
    if (!portalDocumentNcPath) return null;
    return deriveAkteNcPathFromDocumentNcPath(portalDocumentNcPath);
  }, [boundAkte, portalDocumentNcPath]);

  const akteDocumentsForBinding = useMemo(() => {
    if (!boundAkte) return [];
    return protokolle.filter((doc) => portalDokumentBelongsToAkte(doc, boundAkte));
  }, [boundAkte, protokolle]);

  const portalAkteDocumentsMeta = useMemo((): PortalOpenDocumentMeta[] => {
    return akteDocumentsForBinding.slice(0, PORTAL_AKTE_INVENTORY_MAX_DOCS).map((doc) => ({
      documentId: `pb:${doc.id}`,
      title: doc.filename,
      nc_path: doc.nextcloud_path?.trim() || null,
      active: false,
    }));
  }, [akteDocumentsForBinding]);

  const focusBelongsToBoundAkte = useMemo(() => {
    if (!boundAkte) return true;
    const pbId = alphaDocSource?.pbRecordId?.trim() || '';
    const proto = pbId ? protokolle.find((row) => row.id === pbId) : undefined;
    if (proto) return portalDokumentBelongsToAkte(proto, boundAkte);
    if (!portalDocumentNcPath) return false;
    return portalDokumentBelongsToAkte({ nextcloud_path: portalDocumentNcPath }, boundAkte);
  }, [alphaDocSource?.pbRecordId, boundAkte, portalDocumentNcPath, protokolle]);

  const portalArkoOpenDocumentsMeta = useMemo(() => {
    if (!boundAkte) return portalOpenDocumentsMeta;
    const editorForAkte = portalOpenDocumentsMeta.filter((meta) => {
      const pbId = pbRecordIdFromPortalDocumentId(meta.documentId);
      const proto = pbId ? protokolle.find((row) => row.id === pbId) : undefined;
      return portalOpenMetaBelongsToAkte(meta, proto, boundAkte);
    });
    const seenIds = new Set(editorForAkte.map((meta) => meta.documentId));
    const seenPaths = new Set(
      editorForAkte
        .map((meta) => (meta.nc_path || '').replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase())
        .filter(Boolean),
    );
    const extra = portalAkteDocumentsMeta.filter((meta) => {
      if (seenIds.has(meta.documentId)) return false;
      const path = (meta.nc_path || '').replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase();
      if (path && seenPaths.has(path)) return false;
      return true;
    });
    return [...editorForAkte, ...extra];
  }, [boundAkte, portalAkteDocumentsMeta, portalOpenDocumentsMeta, protokolle]);

  const portalArkoDocumentContextForChat = useMemo(() => {
    if (!boundAkte) return portalArkoDocumentContext;
    const selectedExtracts: Array<{ title: string; extract: string }> = [];
    const selectedWithoutTextTitles: string[] = [];
    const seen = new Set<string>();
    for (const rawId of activeThreadSelectedDocIds) {
      if (selectedExtracts.length >= PORTAL_SYNTHESIS_MAX_SELECTED_DOCS) break;
      const id = rawId.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const cached = docContextCache[id];
      const extract = cached?.documentContext?.trim() || '';
      const meta = portalArkoOpenDocumentsMeta.find((row) => row.documentId === id);
      const title = (cached?.title || meta?.title || '').trim() || id;
      if (!hasUsableAkteExtract(extract)) {
        selectedWithoutTextTitles.push(title);
        continue;
      }
      selectedExtracts.push({
        title,
        extract,
      });
    }
    return buildAkteInventoryDocumentContext({
      akteTitle: boundAkte.title,
      documents: akteDocumentsForBinding.map((doc) => ({ title: doc.filename })),
      focusTitle: focusBelongsToBoundAkte ? portalDocumentDisplayName : null,
      focusExtract: boundAkteFocusExtractIfSelected({
        selectedIds: activeThreadSelectedDocIds,
        focusDocumentId: activeDocumentId,
        focusBelongsToAkte: focusBelongsToBoundAkte,
        extract: capAkteExtractDocumentContext(portalArkoDocumentContext),
      }),
      selectedExtracts,
      selectedWithoutTextTitles,
    });
  }, [
    activeDocumentId,
    activeThreadSelectedDocIds,
    akteDocumentsForBinding,
    boundAkte,
    docContextCache,
    focusBelongsToBoundAkte,
    portalArkoDocumentContext,
    portalArkoOpenDocumentsMeta,
    portalDocumentDisplayName,
  ]);

  const portalArkoAvailableDocuments = useMemo((): PortalSelectedDocumentPayload[] => {
    if (!boundAkte) return portalAvailableDocuments;
    return portalArkoOpenDocumentsMeta.map((meta) =>
      portalSelectedDocumentFromMeta(meta, docContextCache[meta.documentId]),
    );
  }, [boundAkte, docContextCache, portalArkoOpenDocumentsMeta, portalAvailableDocuments]);

  const portalActiveDocumentExtract = useMemo(() => {
    if (!activeDocumentId) return null;
    if (
      plainTextDocumentChrome
      && nativeTextUi.kind === 'ready'
      && nativeTextUi.documentId === activeDocumentId
      && nativeTextUi.text.trim()
    ) {
      return { text: nativeTextUi.text };
    }
    if (
      onlyofficeExtractTextUi.kind === 'ready'
      && onlyofficeExtractTextUi.documentId === activeDocumentId
      && onlyofficeExtractTextUi.text.trim()
    ) {
      return {
        text: onlyofficeExtractTextUi.text,
        pages: onlyofficeExtractTextUi.pdfPages,
        rawBytes: onlyofficeExtractTextUi.rawBytes,
        truncated: onlyofficeExtractTextUi.truncated,
        source: onlyofficeExtractTextUi.source,
      };
    }
    const cached = docContextCache[activeDocumentId];
    const full = cached?.fullExtractText?.trim();
    if (!full) return null;
    return {
      text: full,
      pages: cached?.extractPages ?? undefined,
      rawBytes: cached?.extractRawChars ?? undefined,
      truncated: cached?.extractTruncated,
      source: cached?.extractSource ?? undefined,
    };
  }, [
    activeDocumentId,
    plainTextDocumentChrome,
    nativeTextUi,
    onlyofficeExtractTextUi,
    docContextCache,
  ]);

  const boundAkteOptions = useMemo(
    () => portalAktenList.map((row) => toPortalBoundAkte(row)),
    [portalAktenList],
  );

  const arkoActiveDocumentId = boundAkte && !focusBelongsToBoundAkte ? null : activeDocumentId;

  // Aktenraum v2.8: angehakte Akten-Docs extrahieren ohne OnlyOffice-Tab.
  useEffect(() => {
    const selected = activeThreadSelectedDocIds;
    const abortMap = akteLazyExtractAbortRef.current;
    const inFlight = akteLazyExtractInFlightRef.current;
    const watchdogs = akteLazyExtractWatchdogRef.current;
    const clearWatchdog = (id: string) => {
      const timer = watchdogs.get(id);
      if (timer) {
        window.clearTimeout(timer);
        watchdogs.delete(id);
      }
    };

    for (const [id, controller] of abortMap) {
      if (!selected.includes(id)) {
        controller.abort();
        abortMap.delete(id);
        inFlight.delete(id);
        clearWatchdog(id);
      }
    }

    const selectedSet = new Set(selected);
    const stalePlaceholderIds = Object.keys(docContextCache).filter((id) => {
      if (selectedSet.has(id)) return false;
      const ctx = docContextCache[id]?.documentContext;
      return (
        isFailedAkteLazyExtractContext(ctx)
        || isPendingAkteLazyExtractContext(ctx)
        || isRecognizingAkteLazyExtractContext(ctx)
      );
    });
    const staleRecognizingIds = selected.filter((id) =>
      isStaleRecognizingAkteLazyExtractContext(docContextCache[id]?.documentContext),
    );
    if (stalePlaceholderIds.length > 0 || staleRecognizingIds.length > 0) {
      setDocContextCache((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const id of stalePlaceholderIds) {
          if (next[id]) {
            delete next[id];
            changed = true;
          }
        }
        for (const id of staleRecognizingIds) {
          const ctx = next[id]?.documentContext;
          const expired = expireStaleRecognizingAkteDocumentContext(ctx);
          if (expired && expired !== ctx && next[id]) {
            next[id] = { ...next[id], documentContext: expired };
            changed = true;
            clearWatchdog(id);
          }
        }
        return changed ? next : prev;
      });
      if (staleRecognizingIds.length > 0) return;
    }

    if (!boundAkte?.id) {
      if (akteLazyExtractBusyIds.length > 0) setAkteLazyExtractBusyIds([]);
      return;
    }

    const fingerprintsByDocumentId: Record<string, string> = {};
    for (const id of selected) {
      const pbId = pbRecordIdFromPortalDocumentId(id);
      const row = pbId ? protokolle.find((doc) => doc.id === pbId) : undefined;
      if (!row) continue;
      fingerprintsByDocumentId[id] = buildAkteDocumentRevFingerprint({
        updated: row.updated,
        fileSize: row.file_size,
        ncPath: row.nextcloud_path,
        lastRevisionId: row.last_revision_id,
      });
    }
    const persistedStore = loadAkteExtractCacheStore(customer.id);
    const hydrated = hydrateAkteExtractCacheFromPersisted({
      cache: docContextCache,
      persisted: persistedStore.byId,
      fingerprintsByDocumentId,
      selectedIds: selected,
    });
    if (hydrated.staleIds.length > 0) {
      dropAkteExtractCacheEntries(customer.id, hydrated.staleIds);
    }
    if (hydrated.changed) {
      setDocContextCache(hydrated.next);
    }
    const extractCache = hydrated.changed ? hydrated.next : docContextCache;

    const needed = akteSelectedIdsNeedingExtract({
      selectedIds: selected,
      cache: extractCache,
      inFlightIds: inFlight,
      maxSelected: PORTAL_SYNTHESIS_MAX_SELECTED_DOCS,
    });

    const busyNext = selected.filter((id) => {
      const ctx = extractCache[id]?.documentContext;
      if (hasUsableAkteExtract(ctx) || isFailedAkteLazyExtractContext(ctx)) return false;
      if (inFlight.has(id) || needed.includes(id)) return true;
      return (
        isRecognizingAkteLazyExtractContext(ctx)
        && !isStaleRecognizingAkteLazyExtractContext(ctx)
      );
    });
    setAkteLazyExtractBusyIds((prev) => {
      if (prev.length === busyNext.length && prev.every((id, i) => id === busyNext[i])) {
        return prev;
      }
      return busyNext;
    });

    if (!portalAuthzSidecarExpected() || needed.length === 0) return;
    // Hub-Liste noch nicht da: kein Fail-Cache (sonst kein Retry) und kein OCR-Start.
    if (protokolle.length === 0) return;
    // Ein Extract nach dem anderen — parallele OCR füllt pdf2text-Queue und der Proxy schließt.
    if (inFlight.size > 0) return;

    const documentId = needed[0];
    {
      const pbId = pbRecordIdFromPortalDocumentId(documentId);
      const doc = pbId ? protokolle.find((row) => row.id === pbId) : undefined;
      const title = doc?.filename?.trim() || documentId;
      const ncPath = doc?.nextcloud_path?.trim() || null;
      const revFp = fingerprintsByDocumentId[documentId] || buildAkteDocumentRevFingerprint({
        updated: doc?.updated,
        fileSize: doc?.file_size,
        ncPath,
        lastRevisionId: doc?.last_revision_id,
      });
      const writeCache = (
        documentContext: string,
        fullExtractText?: string | null,
        meta?: { pages?: number; rawBytes?: number; truncated?: boolean; source?: string },
      ) => {
        const usable = hasUsableAkteExtract(documentContext);
        setDocContextCache((prev) => ({
          ...prev,
          [documentId]: {
            documentContext,
            documentExtractFingerprint: usable ? revFp : null,
            title,
            nc_path: ncPath,
            fullExtractText: usable
              ? preferLongerAkteExtract(prev[documentId]?.fullExtractText, fullExtractText)
              : null,
            extractPages: meta?.pages || prev[documentId]?.extractPages || null,
            extractRawChars: meta?.rawBytes || prev[documentId]?.extractRawChars || null,
            extractTruncated: meta?.truncated ?? prev[documentId]?.extractTruncated,
            extractSource: meta?.source || prev[documentId]?.extractSource || null,
          },
        }));
        if (usable && isAkteDocumentRevFingerprintReady(revFp)) {
          saveUsableAkteExtractToCache(customer.id, {
            documentId,
            fingerprint: revFp,
            documentContext,
            title,
            nc_path: ncPath,
            fullExtractText: fullExtractText?.trim() || undefined,
            extractPages: meta?.pages,
            extractRawChars: meta?.rawBytes,
            extractTruncated: meta?.truncated,
            extractSource: meta?.source,
          });
        }
      };
      const finish = () => {
        inFlight.delete(documentId);
        abortMap.delete(documentId);
        setAkteLazyExtractBusyIds((prev) => prev.filter((id) => id !== documentId));
      };

      if (!doc?.nc_url?.trim()) {
        writeCache(buildFailedAkteLazyExtractContext({
          title,
          nc_path: ncPath,
          reason: 'keine Datei-URL',
        }));
      } else {
      const ext =
        fileExtensionFromName(doc.filename) ||
        fileExtensionFromName(doc.nextcloud_path || '') ||
        fileExtensionFromName(doc.nc_url);
      const isPlain = isPortalNativePlainTextExtension(ext);
      const extractFileType = resolvePortalExtractFileType({
        displayName: doc.filename,
        nextcloudPath: doc.nextcloud_path,
        documentUrl: doc.nc_url,
        mimeType: doc.mime_type,
      });
      const extractUrlRaw = isPlain ? PORTAL_PLAINTEXT_FETCH_URL : PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL;
      if (!extractUrlRaw || (!isPlain && !extractFileType)) {
        writeCache(buildFailedAkteLazyExtractContext({
          title,
          nc_path: ncPath,
          reason: 'Extract nicht konfiguriert',
        }));
      } else {
      const rateKey = portalOnlyofficeRateLimitKey('extract', documentId);
      if (portalOnlyofficeRateLimitBlocked(rateKey) || portalOnlyofficeRateLimitInsufficientBlocked(rateKey)) {
        writeCache(buildFailedAkteLazyExtractContext({
          title,
          nc_path: ncPath,
          reason: 'Rate-Limit',
        }));
      } else {
      inFlight.add(documentId);
      writeCache(buildPendingAkteLazyExtractContext({ title, nc_path: ncPath }));
      const controller = new AbortController();
      abortMap.set(documentId, controller);
      const extractUrl = resolveOnlyofficeIssueJwtUrl(extractUrlRaw);
      const displayNameForExtract =
        !fileExtensionFromName(doc.filename) && extractFileType
          ? `${doc.filename}.${extractFileType}`
          : doc.filename;
      const reqBody: Record<string, string> = {
        document_url: doc.nc_url,
        display_name: displayNameForExtract,
        customerId: customer.id,
        prefer_local: '1',
        ...portalTenantN8nExtras(customer.status, customer.email),
      };
      if (!isPlain && extractFileType) reqBody.filetype = extractFileType;
      if (ncPath) reqBody.nextcloud_path = normalizePortalWebdavPathFromPb(ncPath);
      if (doc.mime_type) reqBody.mime_type = doc.mime_type;

      void (async () => {
        let handoffToJob = false;
        try {
          const result = await fetchAkteDocumentExtractText({
            fetchFn: fetch,
            url: extractUrl,
            body: reqBody,
            headers: portalAuthzHeaders(extractUrl),
            signal: controller.signal,
            timeoutMs: PORTAL_AKTE_LAZY_EXTRACT_TIMEOUT_MS,
          });
          if (controller.signal.aborted) return;
          if (isPendingAkteExtractHttpResult(result)) {
            handoffToJob = true;
            writeCache(buildRecognizingAkteLazyExtractContext({ title, nc_path: ncPath }));
            inFlight.delete(documentId);
            const pollController = new AbortController();
            abortMap.set(documentId, pollController);
            let pollTimedOut = false;
            const watchdog = window.setTimeout(() => {
              pollTimedOut = true;
              writeCache(buildFailedAkteLazyExtractContext({
                title,
                nc_path: ncPath,
                reason: 'Scan ohne auslesbaren Text',
              }));
              pollController.abort();
            }, PORTAL_AKTE_OCR_POLL_MAX_MS);
            watchdogs.set(documentId, watchdog);
            void (async () => {
              try {
                const settled = await waitForAkteDocumentExtractJob({
                  fetchFn: fetch,
                  url: extractUrl,
                  jobId: result.jobId,
                  customerId: customer.id,
                  extraBody: portalTenantN8nExtras(customer.status, customer.email),
                  headers: portalAuthzHeaders(extractUrl),
                  signal: pollController.signal,
                  maxMs: PORTAL_AKTE_OCR_POLL_MAX_MS,
                });
                if (pollController.signal.aborted) return;
                if (isAkteLazyExtractHttpFailure(settled)) {
                  if (settled.status === 429) portalOnlyofficeRateLimitMark429(rateKey);
                  if (settled.status === 422 || isSoftAkteExtractHttpStatus(settled.status)) {
                    portalOnlyofficeRateLimitMarkInsufficient(rateKey);
                  }
                  writeCache(buildFailedAkteLazyExtractContext({
                    title,
                    nc_path: ncPath,
                    reason: settled.error || 'Scan ohne auslesbaren Text',
                  }));
                  return;
                }
                const text = decodePortalPlainTextUtf8(
                  portalPlaintextBase64ToArrayBuffer(settled.textBase64),
                );
                if (!isMeaningfulPortalExtractText(text)) {
                  portalOnlyofficeRateLimitMarkInsufficient(rateKey);
                  writeCache(buildFailedAkteLazyExtractContext({
                    title,
                    nc_path: ncPath,
                    reason: 'Scan ohne auslesbaren Text',
                  }));
                  return;
                }
                writeCache(buildAkteLazyExtractDocumentContext({
                  title,
                  nc_path: ncPath,
                  extractText: text,
                  viaOnlyOffice: !isPlain,
                  extractSource: settled.source,
                }), text, {
                  pages: settled.ok ? settled.pdfPages : undefined,
                  rawBytes: settled.ok ? settled.rawBytes : undefined,
                  truncated: settled.ok ? settled.truncated : undefined,
                  source: settled.ok ? settled.source : undefined,
                });
              } catch (e) {
                if (pollTimedOut || pollController.signal.aborted) return;
                if (e instanceof DOMException && e.name === 'AbortError') return;
                if (e instanceof Error && e.name === 'AbortError') return;
                writeCache(buildFailedAkteLazyExtractContext({
                  title,
                  nc_path: ncPath,
                  reason: 'Scan ohne auslesbaren Text',
                }));
              } finally {
                clearWatchdog(documentId);
                abortMap.delete(documentId);
              }
            })();
            return;
          }
          if (isAkteLazyExtractHttpFailure(result)) {
            if (result.status === 429) portalOnlyofficeRateLimitMark429(rateKey);
            if (result.status === 422) portalOnlyofficeRateLimitMarkInsufficient(rateKey);
            writeCache(buildFailedAkteLazyExtractContext({
              title,
              nc_path: ncPath,
              reason: result.error || String(result.status),
            }));
            return;
          }
          const text = decodePortalPlainTextUtf8(
            portalPlaintextBase64ToArrayBuffer(result.textBase64),
          );
          if (!isMeaningfulPortalExtractText(text)) {
            portalOnlyofficeRateLimitMarkInsufficient(rateKey);
            writeCache(buildFailedAkteLazyExtractContext({
              title,
              nc_path: ncPath,
              reason: 'zu wenig Text',
            }));
            return;
          }
          writeCache(buildAkteLazyExtractDocumentContext({
            title,
            nc_path: ncPath,
            extractText: text,
            viaOnlyOffice: !isPlain,
            extractSource: result.source,
          }), text, {
            pages: result.ok ? result.pdfPages : undefined,
            rawBytes: result.ok ? result.rawBytes : undefined,
            truncated: result.ok ? result.truncated : undefined,
            source: result.ok ? result.source : undefined,
          });
        } catch (e) {
          if (controller.signal.aborted) return;
          if (e instanceof DOMException && e.name === 'AbortError') return;
          writeCache(buildFailedAkteLazyExtractContext({
            title,
            nc_path: ncPath,
            reason: 'Verbindung geschlossen',
          }));
        } finally {
          if (!handoffToJob) finish();
        }
      })();
      }
      }
      }
    }

    return () => {
      /* Aborts nur bei Unselect / Unmount des Bindings — nicht bei jedem Cache-Tick. */
    };
  }, [
    activeThreadSelectedDocIds,
    boundAkte?.id,
    customer.email,
    customer.id,
    customer.status,
    docContextCache,
    protokolle,
  ]);

  const handleArkoHubModeSelect = useCallback((_mode: ArkoHubMode) => {
    setView('chat');
  }, []);

  // ── Vertragsgenerator (Stream C/D): erzeugtes Dokument im Editor öffnen ──────
  // Gleicher Editor-Pfad wie nach Upload (setAlphaDocSource + setAlphaDocReady),
  // danach Wechsel in die `document_editor`-View. Wiederverwendet den
  // gemeinsamen n8n-JSON-Parser (PORTAL_DOCUMENT_UPLOAD-Format).

  const loadGeneratedDocumentSource = useCallback(
    (data: Record<string, unknown>, res: Response) => {
      const { source } = buildAlphaDocSourceFromN8nJson(data, res, customer.id, 'portal-contract');
      pinAlphaDocSource(source);
      return source;
    },
    [customer.id, pinAlphaDocSource],
  );

  // Wizard (Stream C): erzeugtes Dokument laden UND direkt in die Editor-View wechseln.
  const openGeneratedDocumentInEditor = useCallback(
    (data: Record<string, unknown>, res: Response) => {
      loadGeneratedDocumentSource(data, res);
      setDokumenteAreaTab('editor');
      setView('document_editor');
    },
    [loadGeneratedDocumentSource],
  );

  // ARKO-Reiter (Stream D): Slot-Filling-Flow ruft denselben Generate-Endpoint.
  // Bei Erfolg wird das Dokument für den Editor-Reiter geladen (kein erzwungener
  // View-Wechsel — der Chat bleibt offen) und der Editor-Link in den Chat gegeben.
  const generateContractFromChat = useCallback(
    async (args: {
      templateType: string;
      fields: Record<string, unknown>;
      displayName: string;
    }): Promise<{ ok: boolean; message: string }> => {
      if (!ARKO_CONTRACT_GENERATE_URL) {
        return { ok: false, message: 'Der Vertragsgenerator ist in diesem Build nicht konfiguriert.' };
      }
      if (!hasPortalAuthzToken()) {
        return { ok: false, message: 'Bitte neu anmelden — es fehlt ein gültiges Portal-Token.' };
      }
      try {
        const url = resolveOnlyofficeIssueJwtUrl(ARKO_CONTRACT_GENERATE_URL);
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...portalAuthzHeaders(url),
          },
          credentials: 'omit',
          body: JSON.stringify({
            templateType: args.templateType,
            fields: args.fields,
            customerId: customer.id,
            display_name: args.displayName,
            ...portalTenantN8nExtras(customer.status, customer.email),
          }),
        });
        let data: Record<string, unknown> = {};
        try {
          data = (await res.json()) as Record<string, unknown>;
        } catch {
          /* ignore */
        }
        const docUrl = typeof data.document_url === 'string' ? data.document_url.trim() : '';
        if (!res.ok || !docUrl) {
          throw new Error(formatPortalUploadError(res, data, 'upload'));
        }
        loadGeneratedDocumentSource(data, res);
        const dn = typeof data.display_name === 'string' && data.display_name.trim() ? data.display_name.trim() : args.displayName;
        return {
          ok: true,
          message:
            `Ihr Vertrags-Entwurf „${dn}" ist fertig. Sie finden ihn im Reiter „Dokument (Alpha)" zum Bearbeiten. ` +
            `Direktlink: ${docUrl}`,
        };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [customer.id, loadGeneratedDocumentSource],
  );

  // ARKO V2 Stream 3: Schreiben-/Schriftsatz-Entwurf als PDF (Gotenberg → Ablage).
  // Share-URL nur intern als document_url für den Portal-Viewer — nie im Chat-Text.
  // Co-Brand: kanzlei aus CustomerRecord (PB-Session); Anschrift fehlt im Client-State.
  const exportSchreibenPdf = useCallback(
    async (args: {
      text: string;
      displayName: string;
    }): Promise<{
      ok: boolean;
      message: string;
      portalDocument?: PortalDocumentOpenFields;
    }> => {
      if (!ARKO_SCHREIBEN_PDF_URL) {
        return { ok: false, message: 'Der PDF-Export ist in diesem Build nicht konfiguriert.' };
      }
      if (!hasPortalAuthzToken()) {
        return { ok: false, message: 'Bitte neu anmelden — es fehlt ein gültiges Portal-Token.' };
      }
      try {
        const kanzlei = typeof customer.kanzlei === 'string' ? customer.kanzlei.trim() : '';
        const res = await fetch(ARKO_SCHREIBEN_PDF_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...portalAuthzHeaders(ARKO_SCHREIBEN_PDF_URL),
          },
          credentials: 'omit',
          body: JSON.stringify({
            text: args.text,
            customerId: customer.id,
            display_name: args.displayName,
            ...(kanzlei ? { kanzlei } : {}),
            ...portalTenantN8nExtras(customer.status, customer.email),
          }),
        });
        let data: Record<string, unknown> = {};
        try {
          data = (await res.json()) as Record<string, unknown>;
        } catch {
          /* ignore */
        }
        const pdfUrl = typeof data.pdf_url === 'string' ? data.pdf_url.trim() : '';
        if (!res.ok || !pdfUrl) {
          throw new Error(formatPortalUploadError(res, data, 'upload'));
        }
        const dnRaw =
          (typeof data.display_name === 'string' && data.display_name.trim()) ||
          args.displayName ||
          'Schreiben.pdf';
        const displayName = dnRaw.toLowerCase().endsWith('.pdf') ? dnRaw : `${dnRaw}.pdf`;
        const nextcloud_path =
          typeof data.nextcloud_path === 'string' && data.nextcloud_path.trim()
            ? data.nextcloud_path.trim()
            : undefined;
        const pbRecordId =
          (typeof data.pb_record_id === 'string' && data.pb_record_id.trim()) ||
          (typeof data.pb_id === 'string' && data.pb_id.trim()) ||
          (typeof data.record_id === 'string' && data.record_id.trim()) ||
          undefined;
        const portalDocument: PortalDocumentOpenFields = {
          display_name: displayName,
          document_url: pdfUrl,
          ...(nextcloud_path ? { nextcloud_path } : {}),
          ...(pbRecordId ? { pb_record_id: pbRecordId } : {}),
        };
        const row = protokollFromIngestFields(portalDocument);
        if (row) setProtokolle((prev) => mergeProtokollIntoList(prev, row));
        return {
          ok: true,
          portalDocument,
          message: 'PDF erstellt und in Ihrer Akte abgelegt.',
        };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [customer.id, customer.kanzlei, customer.status, customer.email],
  );

  const refreshEditorAfterSynthesisAppend = useCallback(
    (cacheBust: number, sourceOverride?: AlphaOnlyofficeDocumentSource) => {
      const doc = sourceOverride ?? alphaDocSource;
      if (!doc) return;
      let cleanUrl = doc.url;
      try {
        const u = new URL(doc.url);
        u.searchParams.delete('v');
        cleanUrl = u.toString();
      } catch {
        /* unveraendert */
      }
      const pbId = doc.pbRecordId || documentTabId(doc);
      const refreshed: AlphaOnlyofficeDocumentSource = {
        ...doc,
        url: bustPortalDocumentDownloadUrl(cleanUrl, cacheBust),
        key: sanitizeOnlyofficeDocumentKey(`portal-doc-${pbId}-${cacheBust}`),
      };
      activateOpenDocument(refreshed, { pin: true });
      setOnlyofficeExtractRevision((r) => r + 1);
    },
    [activateOpenDocument, alphaDocSource],
  );

  const buildSynthesisServerAppendConfig = useCallback(
    (source: AlphaOnlyofficeDocumentSource) => {
      if (!PORTAL_SYNTHESIS_APPEND_URL) return undefined;
      const nextcloudPath =
        source.nextcloudWebdavPath?.trim() ||
        (source.nextcloudPathFromPb
          ? normalizePortalWebdavPathFromPb(source.nextcloudPathFromPb)
          : undefined) ||
        inferNextcloudPortalDocumentsPath(
          customer.id,
          source.portalDocumentDisplayName ?? source.title ?? '',
          mandantenRoot,
          customer.kanzlei_id,
        );
      if (!nextcloudPath) return undefined;
      return {
        appendUrl: resolveOnlyofficeIssueJwtUrl(PORTAL_SYNTHESIS_APPEND_URL),
        customerId: customer.id,
        nextcloudPath,
        documentUrl: source.url,
        displayName:
          source.portalDocumentDisplayName?.trim() || source.title?.trim() || undefined,
        tenantExtras: getPortalTenantN8nExtras(customer.status, customer.email),
      };
    },
    [customer.id, customer.kanzlei_id, customer.status, customer.email, mandantenRoot],
  );

  const ensureBoundAkteUploadFolder = useCallback(async (): Promise<string> => {
    if (!boundAkte?.id) {
      return documentHubCurrentFolder.trim()
        || portalDocumentRelativeFolder(alphaDocSource?.nextcloudPathFromPb);
    }
    const folder = resolveAkteFolder(boundAkte);
    if (!folder) {
      return documentHubCurrentFolder.trim()
        || portalDocumentRelativeFolder(alphaDocSource?.nextcloudPathFromPb);
    }
    try {
      await createPortalFolderViaApi({
        customerId: customer.id,
        folderPath: folder,
        customerStatus: customer.status,
        customerEmail: customer.email,
      });
    } catch {
      /* Ordner kann bereits existieren */
    }
    setDocumentHubCurrentFolder(folder);
    if (!akteExistingFolder(boundAkte)) {
      const token = getPortalPbToken();
      if (token) {
        const result = await updatePortalAkteNcPath(token, boundAkte.id, folder);
        if (result.ok) {
          handleBoundAkteChange(result.data);
          setPortalAktenList((prev) => prev.map((row) => (row.id === result.data.id ? result.data : row)));
        }
      }
    }
    return folder;
  }, [
    alphaDocSource?.nextcloudPathFromPb,
    boundAkte,
    customer.email,
    customer.id,
    customer.status,
    documentHubCurrentFolder,
    handleBoundAkteChange,
  ]);

  const applySynthesisToDocument = useCallback(
    async (args: {
      text: string;
      target: 'existing' | 'new';
    }): Promise<{ ok: boolean; message: string }> => {
      if (import.meta.env.DEV) console.debug('[synthesis-apply]', 'start', args.target);

      if (args.target === 'existing') {
        if (!hasActivePortalDocument || !alphaDocSource) {
          return { ok: false, message: 'Kein Dokument im Editor geöffnet.' };
        }
        const focusTitle =
          portalDocumentDisplayName?.trim() ||
          alphaDocSource.title?.trim() ||
          'Dokument';
        const result = await applySynthesisWithTarget({
          text: args.text,
          focusTitle,
          target: 'existing',
          serverAppend: buildSynthesisServerAppendConfig(alphaDocSource),
        });
        if (result.ok && result.cacheBust != null) {
          refreshEditorAfterSynthesisAppend(result.cacheBust);
        }
        if (import.meta.env.DEV) {
          console.debug('[synthesis-apply]', result.ok ? 'insert-complete' : 'insert-unavailable');
        }
        return { ok: result.ok, message: result.message };
      }

      if (!PORTAL_DOCUMENT_UPLOAD_URL) {
        return {
          ok: false,
          message: 'Ein neues Dokument kann in diesem Portal-Build derzeit nicht angelegt werden.',
        };
      }

      try {
        if (import.meta.env.DEV) console.debug('[synthesis-apply]', 'create-document');
        const now = new Date();
        const stamp = now
          .toLocaleString('de-AT', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
          .replace(/[,:]/g, '-')
          .replace(/\s+/g, '_');
        const filename = `Synthese ${stamp}.docx`;
        const fd = new FormData();
        fd.append('file', createBlankDocxFile(filename, { kanzleiName: customer.kanzlei }));
        fd.set('customerId', customer.id);
        const targetFolder = await ensureBoundAkteUploadFolder();
        if (targetFolder) fd.set('folderPath', targetFolder);
        const tenantExtras = getPortalTenantN8nExtras(customer.status, customer.email);
        if (tenantExtras.tenant) fd.set('tenant', tenantExtras.tenant);

        const uploadUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
        const res = await fetch(uploadUrl, {
          method: 'POST',
          body: fd,
          credentials: 'omit',
          headers: portalAuthzHeaders(uploadUrl),
        });
        let data: Record<string, unknown> = {};
        try {
          data = (await res.json()) as Record<string, unknown>;
        } catch {
          /* Fehler wird durch den gemeinsamen Parser benutzerfreundlich formatiert. */
        }
        const fields = parsePortalDocumentN8nResponse(data, res);
        const source = buildAlphaDocSourceFromPortalDocumentFields(
          customer.id,
          fields,
          customer.status,
          customer.email,
        );
        if (!source) {
          throw new Error('Das neue Dokument wurde gespeichert, konnte aber nicht geöffnet werden.');
        }
        if (import.meta.env.DEV) console.debug('[synthesis-apply]', 'document-created');

        const row = protokollFromIngestFields(fields);
        if (row) setProtokolle((prev) => mergeProtokollIntoList(prev, row));
        saveRecentPortalDocument(customer.id, {
          display_name: fields.display_name,
          pb_record_id: fields.pb_record_id,
          nextcloud_path: fields.nextcloud_path,
          document_url: fields.document_url,
          openedAt: new Date().toISOString(),
        }, boundAkte?.id);
        setRecentDocs(loadRecentPortalDocuments(customer.id, boundAkte?.id));

        const appendConfig = buildSynthesisServerAppendConfig(source);
        const result = await applySynthesisWithTarget({
          text: args.text,
          focusTitle: fields.display_name,
          target: 'new',
          serverAppend: appendConfig ? { ...appendConfig, createNew: true } : undefined,
        });

        const editorVisible = await openDocumentInSplit(source);
        if (!editorVisible) {
          return {
            ok: false,
            message:
              `„${fields.display_name}" wurde angelegt, aber der Dokument-Split konnte nicht sichtbar geöffnet werden. ` +
              'Bitte wählen Sie „Erneut versuchen“.',
          };
        }

        if (result.ok && result.cacheBust != null) {
          refreshEditorAfterSynthesisAppend(result.cacheBust, source);
        }
        if (import.meta.env.DEV) {
          console.debug('[synthesis-apply]', result.ok ? 'insert-complete' : 'insert-unavailable');
        }
        if (!result.ok) {
          return {
            ok: false,
            message:
              `„${fields.display_name}" wurde im Dokument-Split angelegt und geöffnet. ` +
              `${result.message} Bitte „Erneut versuchen“, sobald der Editor sichtbar ist.`,
          };
        }
        return {
          ok: true,
          message:
            `„${fields.display_name}" wurde als neues Dokument angelegt und der Synthese-Entwurf eingefügt. ` +
            'Bitte prüfen und speichern.',
        };
      } catch (err) {
        return {
          ok: false,
          message:
            err instanceof Error
              ? `Neues Dokument konnte nicht angelegt werden: ${err.message}`
              : 'Neues Dokument konnte nicht angelegt werden.',
        };
      }
    },
    [
      portalDocumentDisplayName,
      alphaDocSource,
      hasActivePortalDocument,
      customer.id,
      customer.status,
      customer.email,
      customer.kanzlei,
      documentHubCurrentFolder,
      ensureBoundAkteUploadFolder,
      openDocumentInSplit,
      buildSynthesisServerAppendConfig,
      refreshEditorAfterSynthesisAppend,
    ],
  );

  // ── Litigation Upload State ────────────────────────────────────────────────
  const LITIGATION_TEXT_MIN_CHARS = 50;
  const [litigationUploadMode, setLitigationUploadMode] = useState<'pdf' | 'text'>('pdf');
  const [files, setFiles]         = useState<File[]>([]);
  const [schriftsatzText, setSchriftsatzText] = useState('');
  const [optIn, setOptIn]         = useState(false);
  const [litigationValidationError, setLitigationValidationError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    type: 'success' | 'error';
    message?: string;
    pdfUrl?: string;
    summary?: LitigationSummary;
    /** ISO-Zeitpunkt; nach 30 Min. ab Analyse-Ende (Hinweis im Portal) */
    pdfLinkExpiresAt?: string;
    /** Kurzgrund wenn kein PDF/Link (n8n Node 05), ohne Secrets */
    pdfGenerationNote?: string;
  } | null>(null);

  // ── Passwort State ─────────────────────────────────────────────────────────
  const [pwOld, setPwOld]               = useState('');
  const [pwNew, setPwNew]               = useState('');
  const [pwConfirm, setPwConfirm]       = useState('');
  const [pwError, setPwError]           = useState('');
  const [pwSuccess, setPwSuccess]       = useState(false);
  const [pwLoading, setPwLoading]       = useState(false);
  const [showPwOld, setShowPwOld]       = useState(false);
  const [showPwNew, setShowPwNew]       = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);

  // ── Vertrags-QA State ──────────────────────────────────────────────────
  const [vqaPreisliste, setVqaPreisliste]           = useState<File | null>(null);
  const [vqaGruendungspaket, setVqaGruendungspaket] = useState<File | null>(null);
  const [vqaMarkeIp, setVqaMarkeIp]                 = useState<File | null>(null);
  const [vqaOptIn, setVqaOptIn]                     = useState(false);
  const [vqaUploading, setVqaUploading]             = useState(false);
  const [vqaResult, setVqaResult]                   = useState<VertragQAResult | null>(null);

  // ── Diktat-Upload State ────────────────────────────────────────────────────
  const [diktatFile, setDiktatFile]         = useState<File | null>(null);
  const [diktatUploading, setDiktatUploading] = useState(false);
  const [diktatResult, setDiktatResult]     = useState<{
    type: 'success' | 'error';
    pdfUrl?: string;
    message?: string;
    pb_record_id?: string;
    transcriptText?: string;
    auswertungText?: string;
    analysisBlockedReason?: string;
  } | null>(null);
  const [diktatAnalysisStart, setDiktatAnalysisStart] = useState<DiktatAnalysisStart | null>(null);
  const [diktatAnalysisQueueHint, setDiktatAnalysisQueueHint] = useState<string | null>(null);
  const [diktatArchive, setDiktatArchive] = useState<DiktatArchiveEntry[]>([]);
  const [selectedDiktatArchiveId, setSelectedDiktatArchiveId] = useState<string | null>(null);
  const [highlightPbRecordId, setHighlightPbRecordId] = useState<string | null>(null);
  useEffect(() => {
    const onSprung = (event: Event) => {
      const detail = (event as CustomEvent<GlockeSprung>).detail;
      if (!detail || typeof detail.source !== 'string') return;
      if (detail.view) setView(detail.view);
      if (detail.source === 'dokument' && detail.refId) {
        setHighlightPbRecordId(detail.refId);
      }
      if (detail.source === 'akte' && detail.refId) {
        const row = portalAktenList.find((akte) => akte.id === detail.refId);
        if (row) handleBoundAkteChange(row);
      }
    };
    window.addEventListener(GLOCKE_SPRUNG_EVENT, onSprung);
    return () => window.removeEventListener(GLOCKE_SPRUNG_EVENT, onSprung);
  }, [handleBoundAkteChange, portalAktenList]);
  const [diktatFormatBusy, setDiktatFormatBusy] = useState(false);
  const [diktatFormatError, setDiktatFormatError] = useState<string | null>(null);
  const [diktatIntoAkteBusy, setDiktatIntoAkteBusy] = useState(false);
  const [diktatIntoAkteNote, setDiktatIntoAkteNote] = useState<string | null>(null);
  /** Fail-closed: erst Probe HTTP 200. Nie hardcoded true. */
  const [homeserverReachable, setHomeserverReachable] = useState(false);

  // ── Passkey State ──────────────────────────────────────────────────────────
  const supportsPasskeys =
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined';
  const [passkeys, setPasskeys]         = useState<PasskeyInfo[]>([]);
  const [pkLoading, setPkLoading]       = useState(false);
  const [pkRegistering, setPkRegistering] = useState(false);
  const [pkDeviceName, setPkDeviceName] = useState('');
  const [pkError, setPkError]           = useState('');
  const [pkSuccess, setPkSuccess]       = useState('');
  const [idAustriaConfigured, setIdAustriaConfigured] = useState(false);
  const [idAustriaLinked, setIdAustriaLinked] = useState(false);
  const [idAustriaLoading, setIdAustriaLoading] = useState(false);
  const [idAustriaBusy, setIdAustriaBusy] = useState(false);
  const [idAustriaError, setIdAustriaError] = useState('');
  const [idAustriaMessage, setIdAustriaMessage] = useState('');

  // ── Feedback State ─────────────────────────────────────────────────────────
  const [fbKategorie, setFbKategorie]   = useState<FeedbackKategorie>('Anregung');
  const [fbSymptom, setFbSymptom]       = useState<FeedbackSymptom>('other');
  const [fbNachricht, setFbNachricht]   = useState('');
  const [fbLoading, setFbLoading]       = useState(false);
  const [fbSuccess, setFbSuccess]       = useState(false);
  const [fbError, setFbError]           = useState('');
  const [fbDiagCode, setFbDiagCode]     = useState<string | null>(null);

  // ── Handler: Passkeys ──────────────────────────────────────────────────────

  const loadPasskeys = useCallback(async () => {
    if (!supportsPasskeys) return;
    setPkLoading(true);
    try {
      const r = await fetch(`${WEBAUTHN_URL}/my-passkeys?customerId=${customer.id}`, {
        headers: portalAuthHeaders(),
      });
      if (r.ok) setPasskeys(await r.json());
    } catch { /* silent */ } finally { setPkLoading(false); }
  }, [customer.id, supportsPasskeys]);

  useEffect(() => { if (settingsOpen) loadPasskeys(); }, [settingsOpen, loadPasskeys]);

  const loadIdAustria = useCallback(async () => {
    setIdAustriaLoading(true);
    setIdAustriaError('');
    try {
      const [st, link] = await Promise.all([
        fetchIdAustriaStatus(),
        fetchIdAustriaLink(customer.id).catch(() => ({ linked: false as const })),
      ]);
      setIdAustriaConfigured(st.configured);
      setIdAustriaLinked(link.linked === true);
    } catch {
      setIdAustriaConfigured(false);
      setIdAustriaLinked(false);
    } finally {
      setIdAustriaLoading(false);
    }
  }, [customer.id]);

  useEffect(() => {
    if (settingsOpen) void loadIdAustria();
  }, [settingsOpen, loadIdAustria]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('ida_linked') === '1') {
      setSettingsOpen(true);
      setIdAustriaMessage('ID Austria ist mit diesem Konto verbunden.');
      try {
        applyPortalViewHistory('replace', viewRef.current, { stripSearch: true });
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    setRecentDocs(loadRecentPortalDocuments(customer.id, boundAkte?.id));
  }, [customer.id]);

  useEffect(() => {
    if (view === 'upload') {
      setDiktatTab('upload');
      setView('diktat');
    } else if (view === 'protokolle') {
      setView('dokumente');
    }
  }, [view]);

  const handleAddPasskey = async () => {
    setPkError(''); setPkSuccess('');
    setPkRegistering(true);
    try {
      const name = pkDeviceName.trim() ||
        (/iPhone|iPad/.test(navigator.userAgent) ? 'iPhone / iPad' :
         /Mac/.test(navigator.userAgent)         ? 'Mac Touch ID' :
         /Android/.test(navigator.userAgent)     ? 'Android'      : 'Dieses Gerät');
      await registerPasskey(customer.id, customer.email, customer.kanzlei, name);
      setPkSuccess('Passkey erfolgreich eingerichtet! Sie können sich ab sofort biometrisch anmelden.');
      setPkDeviceName('');
      await loadPasskeys();
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string };
      if (e.name === 'NotAllowedError') setPkError('Biometrie abgebrochen oder nicht erlaubt.');
      else setPkError(e.message || 'Registrierung fehlgeschlagen.');
    } finally { setPkRegistering(false); }
  };

  const handleDeletePasskey = async (pkId: string) => {
    if (!confirm('Diesen Passkey löschen?')) return;
    try {
      const r = await fetch(`${WEBAUTHN_URL}/passkey/${pkId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...portalAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      if (r.ok) await loadPasskeys();
    } catch { setPkError('Löschen fehlgeschlagen.'); }
  };

  const handleLinkIdAustria = async () => {
    setIdAustriaError('');
    setIdAustriaMessage('');
    setIdAustriaBusy(true);
    try {
      const url = await startIdAustriaLink(customer.id);
      window.location.assign(url);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setIdAustriaError(e.message || 'ID Austria konnte nicht gestartet werden.');
      setIdAustriaBusy(false);
    }
  };

  const handleUnlinkIdAustria = async () => {
    if (!confirm('ID Austria von diesem Konto trennen? Das Passwort bleibt der Fallback.')) return;
    setIdAustriaError('');
    setIdAustriaMessage('');
    setIdAustriaBusy(true);
    try {
      await unlinkIdAustria(customer.id);
      setIdAustriaLinked(false);
      setIdAustriaMessage('ID Austria getrennt. Anmeldung weiter mit Passwort oder Passkey.');
    } catch (err: unknown) {
      const e = err as { message?: string };
      setIdAustriaError(e.message || 'Trennen fehlgeschlagen.');
    } finally {
      setIdAustriaBusy(false);
    }
  };

  // ── Handler: Passwort ──────────────────────────────────────────────────────

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(''); setPwSuccess(false);
    if (pwNew !== pwConfirm) { setPwError('Neues Passwort und Bestätigung stimmen nicht überein.'); return; }
    if (pwNew.length < 8)    { setPwError('Neues Passwort mindestens 8 Zeichen.'); return; }
    setPwLoading(true);
    try {
      const token = getPortalPbToken();
      if (!token) { setPwError('Nicht angemeldet. Bitte erneut einloggen.'); return; }
      const pbBase = getPortalPocketBaseUrl();
      const res = await fetch(`${pbBase}/api/collections/customers/records/${customer.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ oldPassword: pwOld, password: pwNew, passwordConfirm: pwConfirm }),
      });
      const data = (await res.json()) as {
        message?: string;
        token?: string;
        record?: Record<string, unknown>;
      };
      if (!res.ok) { setPwError(data.message || 'Passwort konnte nicht geändert werden. Altes Passwort korrekt?'); return; }
      // PB invalidiert den alten Token — Session mit neuem Token fortsetzen (wie MustChangePasswordOverlay)
      let nextToken =
        typeof data.token === 'string' && data.token.trim() ? data.token.trim() : '';
      if (!nextToken) {
        const authRes = await fetch(`${pbBase}/api/collections/customers/auth-with-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identity: customer.email, password: pwNew }),
        });
        const authData = (await authRes.json()) as { token?: string; record?: Record<string, unknown>; message?: string };
        nextToken = typeof authData.token === 'string' ? authData.token.trim() : '';
        if (!authRes.ok || !nextToken) {
          setPwError(
            authData.message ||
              'Passwort geändert, aber Sitzung konnte nicht fortgesetzt werden. Bitte erneut anmelden.',
          );
          return;
        }
        if (authData.record?.id) {
          const refreshed = buildCustomerRecord(authData.record);
          setCustomer(refreshed);
          persistPortalCustomer(refreshed);
          onCustomerUpdate?.(refreshed);
        }
      }
      persistPortalPbSession(nextToken, pbBase);
      setPwSuccess(true); setPwOld(''); setPwNew(''); setPwConfirm('');
    } catch { setPwError('Verbindungsfehler. Bitte erneut versuchen.'); }
    finally   { setPwLoading(false); }
  };

  // ── Handler: Diktat-Upload (siehe unten nach openPortalDocumentInEditor) ────

  // ── Handler: Protokolle laden ──────────────────────────────────────────────

  const loadProtokolle = useCallback(async (): Promise<PortalProtokoll[]> => {
    const seq = ++protokolleLoadSeqRef.current;
    setProtokolleLoading(true);
    setProtokolleError('');
    try {
      const token = getPortalPbToken();
      if (!token) {
        if (seq === protokolleLoadSeqRef.current) {
          setProtokolle([]);
          setProtokolleError('Bitte melden Sie sich erneut an, um Ihre Dokumente zu laden.');
        }
        return [];
      }
      const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
      const listPath = `${pbBase}/api/collections/${PORTAL_PROTOKOLLE_COLLECTION}/records`;
      const pbFilter = buildPortalDocumentsPbFilter(customer.id, customer.kanzlei_id);
      const controller = new AbortController();
      const loadTimeout = window.setTimeout(() => controller.abort(), 20_000);
      const rows: PortalProtokoll[] = [];
      const seenIds = new Set<string>();
      let page = 1;
      let lastStatus = 0;
      try {
        while (true) {
          const qs = new URLSearchParams({
            sort: PORTAL_DOCUMENTS_PB_LIST_SORT,
            perPage: String(PORTAL_DOCUMENTS_PB_LIST_PER_PAGE),
            page: String(page),
          });
          if (pbFilter) qs.set('filter', pbFilter);
          let res: Response;
          try {
            res = await fetch(`${listPath}?${qs.toString()}`, {
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal,
            });
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
              if (seq === protokolleLoadSeqRef.current) {
                setProtokolleError('Zeitüberschreitung beim Laden — bitte „Aktualisieren“ erneut versuchen.');
              }
              return [];
            }
            throw err;
          }
          lastStatus = res.status;
          if (!res.ok) break;
          const data = (await res.json()) as {
            items?: Record<string, unknown>[];
            totalItems?: number;
            totalPages?: number;
          };
          const pageRows = (data.items ?? [])
            .filter(isActivePortalDocumentRecord)
            .map(mapPbRecordToProtokoll)
            .filter((row) => row.id);
          for (const row of pageRows) {
            if (seenIds.has(row.id)) continue;
            seenIds.add(row.id);
            rows.push(row);
          }
          if (
            !shouldFetchNextPortalDocumentPage({
              page,
              received: pageRows.length,
              perPage: PORTAL_DOCUMENTS_PB_LIST_PER_PAGE,
              totalItems: data.totalItems,
              totalPages: data.totalPages,
            })
          ) {
            break;
          }
          page += 1;
        }
      } finally {
        window.clearTimeout(loadTimeout);
      }
      if (lastStatus === 200) {
        if (seq === protokolleLoadSeqRef.current) {
          setProtokolle(rows);
          if (clearRecentPortalDocumentsWhenServerEmpty(customer.id, rows)) {
            setRecentDocs([]);
          }
        }
        return rows;
      }
      const res = { status: lastStatus };
      if (res.status === 404) {
        if (seq === protokolleLoadSeqRef.current) {
          setProtokolle([]);
          setProtokolleError(
            `Dokumentenindex nicht erreichbar (${pbBase}, Collection „${PORTAL_PROTOKOLLE_COLLECTION}"). Bitte mit ?forceLogin=1 neu anmelden.`,
          );
        }
        return [];
      }
      if (res.status === 401 || res.status === 403) {
        if (seq === protokolleLoadSeqRef.current) {
          setProtokolle([]);
          setProtokolleError('Sitzung abgelaufen — bitte erneut anmelden.');
        }
        return [];
      }
      if (seq === protokolleLoadSeqRef.current) {
        setProtokolleError('Fehler beim Laden Ihrer Dokumente.');
      }
      return [];
    } catch {
      if (seq === protokolleLoadSeqRef.current) {
        setProtokolleError('Verbindungsfehler beim Laden Ihrer Dokumente.');
      }
      return [];
    } finally {
      if (seq === protokolleLoadSeqRef.current) {
        setProtokolleLoading(false);
      }
    }
  }, [customer.id]);

  useEffect(() => {
    setDiktatArchive((prev) => hydrateDiktatArchiveFromProtokolle(prev, protokolle));
  }, [protokolle]);

  const mergeIngestedProtokoll = useCallback((fields: PortalDocumentOpenFields) => {
    const row = protokollFromIngestFields(fields);
    if (!row) return;
    setProtokolle((prev) => mergeProtokollIntoList(prev, row));
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Invite / Must-Change: kein auth-refresh mit Temp-Token — PB invalidiert ihn beim PW-Wechsel
      // und der in-flight POST liefert sonst Console-401 (ggf. Logout-Race). Refresh erst nach Overlay.
      if (mustChangePassword) return;

      const token = getPortalPbToken();
      if (!token) {
        if (!cancelled) await loadProtokolle();
        return;
      }
      const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
      try {
        const res = await fetch(`${pbBase}/api/collections/customers/auth-refresh`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        let refreshPayload: unknown = null;
        try {
          refreshPayload = await res.json();
        } catch {
          refreshPayload = null;
        }
        if (isPbAuthRefreshNonRenewable(res.status, refreshPayload)) {
          /* Passkey-Login (PB impersonate): Token gültig, nur nicht erneuerbar — Session behalten. */
        } else if (res.status === 401 || res.status === 403 || res.status === 404) {
          // Settings-PW-Wechsel o.ä.: Token rotiert während in-flight refresh → Session behalten
          const latest = localStorage.getItem('pb_token')?.trim();
          if (latest && latest !== token) {
            /* noop — neuer Token schon persistiert */
          } else {
            handleLogoutRef.current();
            return;
          }
        } else if (res.ok) {
          const data = refreshPayload as { token?: string; record?: Record<string, unknown> };
          if (data.token?.trim()) {
            persistPortalPbSession(data.token.trim(), pbBase);
          }
          if (data.record?.id) {
            const refreshed = buildCustomerRecord(data.record);
            setCustomer(refreshed);
            persistPortalCustomer(refreshed);
            onCustomerUpdateRef.current?.(refreshed);
          }
        }
      } catch {
        /* Offline — Session behalten, Liste trotzdem versuchen */
      }
      if (!cancelled) await loadProtokolle();
    })();
    return () => {
      cancelled = true;
    };
  }, [customer.id, loadProtokolle, mustChangePassword]);

  useEffect(() => {
    if (view === 'dokumente' || view === 'akten' || view === 'diktat' || (view === 'document_editor' && dokumenteAreaTab === 'hub')) {
      void loadProtokolle();
      setRecentDocs(loadRecentPortalDocuments(customer.id, boundAkte?.id));
    }
  }, [view, dokumenteAreaTab, loadProtokolle, customer.id]);

  const diktatExplorerPbIds = useMemo(() => {
    if (boundAkte?.id) return akteDocumentsForBinding.map((doc) => doc.id);
    return protokolle.map((doc) => doc.id);
  }, [akteDocumentsForBinding, boundAkte?.id, protokolle]);

  const diktatDokumenteCta = useMemo(
    () =>
      diktatDokumenteCtaKind({
        pbRecordId: diktatResult?.pb_record_id,
        explorerPbIds: diktatExplorerPbIds,
      }),
    [diktatExplorerPbIds, diktatResult?.pb_record_id],
  );

  const handleShowDiktatInDokumente = useCallback((pbRecordId?: string) => {
    if (
      diktatDokumenteCtaKind({
        pbRecordId,
        explorerPbIds: diktatExplorerPbIds,
      }) !== 'show_in_explorer'
    ) {
      return;
    }
    setHighlightPbRecordId(pbRecordId?.trim() || null);
    if (hasActivePortalDocument) {
      setView('document_editor');
      setDokumenteAreaTab('hub');
    } else {
      setView('dokumente');
    }
    void loadProtokolle();
  }, [diktatExplorerPbIds, hasActivePortalDocument, loadProtokolle]);

  /** Dokument-Hub → OnlyOffice der Kanzlei. Akten-Dokumente bleiben auf der Akten-Fläche. */
  const openPortalDocumentInEditor = useCallback(
    async (fields: {
      display_name: string;
      document_url: string;
      pb_record_id?: string;
      nextcloud_path?: string;
      updated?: string;
      last_revision_id?: number;
      last_save_callback_at?: string;
      akte_id?: string;
    }) => {
      const kanzleiDecision = decidePortalOnlyofficeSurface({
        entry: 'kanzlei',
        documentAkteId: fields.akte_id,
        openAkteId: null,
      });
      if (!kanzleiDecision.ok) return;
      mergeIngestedProtokoll(fields);
      const source = buildAlphaDocSourceFromPortalDocumentFields(
        customer.id,
        fields,
        customer.status,
        customer.email,
      );
      if (!source) return;
      saveRecentPortalDocument(customer.id, {
        display_name: fields.display_name,
        pb_record_id: fields.pb_record_id,
        nextcloud_path: fields.nextcloud_path,
        document_url: fields.document_url,
        openedAt: new Date().toISOString(),
      }, boundAkte?.id);
      setRecentDocs(loadRecentPortalDocuments(customer.id, boundAkte?.id));
      const visible = await openDocumentInSplit(source);
      if (!visible) {
        setDokumenteAreaTab('editor');
        setView('document_editor');
      }
    },
    [customer.id, customer.status, customer.email, mergeIngestedProtokoll, openDocumentInSplit],
  );

  /** Akte → eigene OnlyOffice-Instanz. Schreibt nicht in die Kanzlei-Fläche. */
  const openAkteOnlyoffice = useCallback(
    async (fields: PortalDocumentOpenFields) => {
      const akteId = fields.akte_id?.trim() || '';
      const decision = decidePortalOnlyofficeSurface({
        entry: 'akte',
        documentAkteId: akteId,
        openAkteId: akteId,
      });
      if (!decision.ok) return;
      mergeIngestedProtokoll(fields);
      const source = buildAlphaDocSourceFromPortalDocumentFields(
        customer.id,
        fields,
        customer.status,
        customer.email,
      );
      if (!source) return;
      const docId = documentTabId(source);
      setAkteOpenDocs((prev) => {
        const sameAkte = prev.length === 0 || akteEditorAkteId === akteId;
        const base = sameAkte ? prev : [];
        const idx = base.findIndex((row) => documentTabId(row) === docId);
        if (idx < 0) return [...base, source];
        const next = base.slice();
        next[idx] = source;
        return next;
      });
      setAkteEditorAkteId(akteId);
      setAkteDocSource(source);
      setAkteDocReady(true);
    },
    [akteEditorAkteId, customer.email, customer.id, customer.status, mergeIngestedProtokoll],
  );

  const selectAkteOfficeDoc = useCallback((id: string) => {
    const doc = akteOpenDocs.find((row) => documentTabId(row) === id);
    if (!doc) return;
    setAkteDocSource(doc);
    setAkteDocReady(true);
  }, [akteOpenDocs]);

  useEffect(() => {
    if (!akteEditorAkteId) return;
    if (boundAkte?.id && boundAkte.id !== akteEditorAkteId) closeAkteOnlyoffice();
  }, [akteEditorAkteId, boundAkte?.id, closeAkteOnlyoffice]);

  const openAkteDocumentFromChat = useCallback((documentId: string) => {
    const pbId = pbRecordIdFromPortalDocumentId(documentId);
    const doc = pbId ? protokolle.find((row) => row.id === pbId) : undefined;
    if (!doc?.nc_url) return;
    if (!canOpenDocumentInPortal(isPremium, doc.filename, doc.nextcloud_path, doc.nc_url)) {
      window.open(doc.nc_url, '_blank', 'noopener,noreferrer');
      return;
    }
    const fields = portalDocumentOpenFieldsFromProtokoll(doc);
    if (fields.akte_id?.trim()) {
      void openAkteOnlyoffice(fields);
      setView('akten');
      return;
    }
    void openPortalDocumentInEditor(fields);
  }, [isPremium, openAkteOnlyoffice, openPortalDocumentInEditor, protokolle]);

  const commitPortalDocumentRename = useCallback(
    (pbRecordId: string, displayName: string, nextcloudPath?: string) => {
      const nextPath = nextcloudPath?.trim() || undefined;
      setProtokolle((prev) =>
        applyPortalDocumentRenameToProtokolle(prev, {
          pb_record_id: pbRecordId,
          display_name: displayName,
          nextcloud_path: nextPath,
        }),
      );
      setOpenDocuments((prev) =>
        prev.map((src) =>
          src.pbRecordId === pbRecordId
            ? {
                ...src,
                title: displayName,
                portalDocumentDisplayName: displayName,
                ...(nextPath
                  ? { nextcloudPathFromPb: nextPath, nextcloudWebdavPath: nextPath }
                  : {}),
              }
            : src,
        ),
      );
      setAlphaDocSource((prev) =>
        prev?.pbRecordId === pbRecordId
          ? {
              ...prev,
              title: displayName,
              portalDocumentDisplayName: displayName,
              ...(nextPath
                ? { nextcloudPathFromPb: nextPath, nextcloudWebdavPath: nextPath }
                : {}),
            }
          : prev,
      );
      setDocContextCache((prev) => {
        const docId = `pb:${pbRecordId}`;
        const cached = prev[docId];
        if (!cached) return prev;
        return {
          ...prev,
          [docId]: {
            ...cached,
            title: displayName,
            nc_path: nextPath ?? cached.nc_path,
          },
        };
      });
      applyPortalDocumentRenameToRecent(customer.id, {
        pb_record_id: pbRecordId,
        display_name: displayName,
        nextcloud_path: nextPath,
      }, boundAkte?.id);
      persistPortalDocumentRenameShortcuts(customer.id, {
        pb_record_id: pbRecordId,
        title: displayName,
        nextcloud_path: nextPath,
      }, boundAkte?.id);
      setRecentDocs(loadRecentPortalDocuments(customer.id, boundAkte?.id));
    },
    [customer.id],
  );

  const renameAkteDocumentFromChat = useCallback(async (documentId: string, newName: string) => {
    const pbId = pbRecordIdFromPortalDocumentId(documentId);
    const doc = pbId ? protokolle.find((row) => row.id === pbId) : undefined;
    if (!doc?.nextcloud_path?.trim()) {
      throw new Error('Dokument hat keinen Nextcloud-Pfad — Umbenennen nicht möglich.');
    }
    const sanitized = sanitizePortalFileName(newName);
    if (!sanitized) {
      throw new Error('Dateiname fehlt oder ist ungültig.');
    }
    const saved = await renamePortalDocumentViaFolderApi({
      customerId: customer.id,
      customerStatus: customer.status,
      customerEmail: customer.email,
      pbRecordId: doc.id,
      sourcePath: doc.nextcloud_path,
      newName: sanitized,
    });
    commitPortalDocumentRename(doc.id, saved.display_name, saved.nextcloud_path ?? doc.nextcloud_path);
  }, [commitPortalDocumentRename, customer.email, customer.id, customer.status, protokolle]);

  /** Schreiben-Entwurf als DOCX in der Akte (blankDocx + Upload) und im Portal-Viewer öffnen. */
  const exportSchreibenDocx = useCallback(
    async (args: {
      text: string;
      displayName: string;
    }): Promise<{
      ok: boolean;
      message: string;
      portalDocument?: PortalDocumentOpenFields;
    }> => {
      if (!PORTAL_DOCUMENT_UPLOAD_URL) {
        return { ok: false, message: 'Der DOCX-Export ist in diesem Build nicht konfiguriert.' };
      }
      if (!hasPortalAuthzToken()) {
        return { ok: false, message: 'Bitte neu anmelden — es fehlt ein gültiges Portal-Token.' };
      }
      try {
        const filename = ensurePortalDocxFileName(args.displayName);
        const fd = new FormData();
        fd.append(
          'file',
          createBlankDocxFile(filename, {
            kanzleiName: customer.kanzlei,
            bodyText: args.text,
          }),
        );
        fd.set('customerId', customer.id);
        const targetFolder = await ensureBoundAkteUploadFolder();
        if (targetFolder) fd.set('folderPath', targetFolder);
        const akteId = boundAkte?.id?.trim() || '';
        if (akteId) fd.set('akte_id', akteId);
        const tenantExtras = getPortalTenantN8nExtras(customer.status, customer.email);
        if (tenantExtras.tenant) fd.set('tenant', tenantExtras.tenant);

        const uploadUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
        const res = await fetch(uploadUrl, {
          method: 'POST',
          body: fd,
          credentials: 'omit',
          headers: portalAuthzHeaders(uploadUrl),
        });
        let data: Record<string, unknown> = {};
        try {
          data = (await res.json()) as Record<string, unknown>;
        } catch {
          /* Parser unten */
        }
        const fields = parsePortalDocumentN8nResponse(data, res);
        const row = protokollFromIngestFields(fields);
        if (row) setProtokolle((prev) => mergeProtokollIntoList(prev, row));
        saveRecentPortalDocument(customer.id, {
          display_name: fields.display_name,
          pb_record_id: fields.pb_record_id,
          nextcloud_path: fields.nextcloud_path,
          document_url: fields.document_url,
          openedAt: new Date().toISOString(),
        }, boundAkte?.id);
        setRecentDocs(loadRecentPortalDocuments(customer.id, boundAkte?.id));
        const returnedAkte = String(fields.akte_id || '').trim();
        if (akteId && returnedAkte && returnedAkte !== akteId) {
          void loadProtokolle();
          return {
            ok: true,
            portalDocument: fields,
            message: 'Die Datei gehört nicht zu dieser Akte und wurde hier nicht geöffnet.',
          };
        }
        if (akteId) {
          const stamped = { ...fields, akte_id: returnedAkte || akteId };
          await openAkteOnlyoffice(stamped);
          setView('akten');
          setAkteSaveFocus({
            akteId,
            docId: stamped.pb_record_id || '',
            tick: Date.now(),
          });
        } else {
          await openPortalDocumentInEditor(fields);
        }
        void loadProtokolle();
        return {
          ok: true,
          portalDocument: akteId ? { ...fields, akte_id: returnedAkte || akteId } : fields,
          message: boundAkte?.title
            ? `DOCX in Akte „${boundAkte.title}“ abgelegt und im Portal geöffnet.`
            : 'DOCX in der Akte abgelegt und im Portal geöffnet.',
        };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [
      customer.email,
      customer.id,
      customer.kanzlei,
      customer.status,
      boundAkte?.id,
      boundAkte?.title,
      ensureBoundAkteUploadFolder,
      loadProtokolle,
      openAkteOnlyoffice,
      openPortalDocumentInEditor,
    ],
  );

  /** ARKO „Analysieren“: hochgeladene Dateien im Hub sichtbar machen und ersten Editor-Typ öffnen. */
  const handleAnalyzeDocumentsIngested = useCallback(
    async (fields: PortalDocumentOpenFields[]) => {
      if (!fields.length) return;
      const openable = fields.find((f) =>
        canOpenDocumentInPortal(isPremium, f.display_name, f.nextcloud_path, f.document_url),
      );
      if (openable) {
        await openPortalDocumentInEditor(openable);
        for (const f of fields) {
          if (f !== openable) mergeIngestedProtokoll(f);
        }
        return;
      }
      for (const f of fields) mergeIngestedProtokoll(f);
      setView('dokumente');
      setDokumenteAreaTab('hub');
    },
    [isPremium, mergeIngestedProtokoll, openPortalDocumentInEditor],
  );

  useEffect(() => {
    let cancelled = false;
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const hs = portalMatrixHomeserverUrl(hostname);
    void probeMatrixHomeserverReachable(hs).then((ok) => {
      if (!cancelled) setHomeserverReachable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const diktatElementLoad = useMemo(
    () =>
      assessElementDiktatLoad({
        matrixRoomId: customer.matrixRoomId,
        isLaborHost:
          typeof window !== 'undefined' && isLaborPortalHostname(window.location.hostname),
        homeserverReachable,
      }),
    [customer.matrixRoomId, homeserverReachable],
  );

  const handleDiktatUpload = useCallback(async (fileOverride?: File) => {
    const file = fileOverride instanceof File ? fileOverride : diktatFile;
    if (!file) return;
    setDiktatUploading(true);
    setDiktatResult(null);
    setDiktatIntoAkteNote(null);
    setDiktatAnalysisStart(null);
    setDiktatAnalysisQueueHint(null);
    try {
      if (file.size === 0) {
        setDiktatResult({
          type: 'success',
          message: DIKTAT_UPLOAD_NO_TEXT,
        });
        return;
      }
      const audioResult = await prepareAudioAnalyzeDocumentContext({
        file,
        customerId: customer.id,
        kanzlei: customer.kanzlei,
        email: customer.email,
        tenantExtras: portalTenantN8nExtras(customer.status, customer.email),
      });
      const diktat = audioResult.diktatResponse;
      const transcript = pickDiktatTranscript(diktat);
      const auswertungText =
        diktatSurfaceCopyBlocks({
          transcriptText: transcript,
          protokollText: diktat.protokollText,
        }).auswertung || undefined;
      const uploaded = audioResult.uploadedFields[0];
      const diktatNc =
        (typeof diktat.nextcloud_path === 'string' && diktat.nextcloud_path.trim()) ||
        (uploaded?.nextcloud_path &&
        (/\/protokolle\//i.test(uploaded.nextcloud_path) ||
          /\/(diktate|audio-analyse)\//i.test(uploaded.nextcloud_path))
          ? uploaded.nextcloud_path.trim()
          : '');
      const pdfUrl =
        typeof diktat.pdfUrl === 'string' && diktat.pdfUrl.trim() ? diktat.pdfUrl.trim() : undefined;
      const pbRecordId =
        diktat.pb_record_id?.trim() ||
        diktat.pb_id?.trim() ||
        (diktatNc ? uploaded?.pb_record_id?.trim() : undefined) ||
        undefined;

      const analysisDecision = diktatAnalysisStartDecision({
        transcript,
        hasAuthToken: hasPortalAuthzToken(),
        webhookUrl: portalArkoWebhook,
        fileReceived: true,
      });
      const analysisBlockedReason = analysisDecision.ok ? undefined : analysisDecision.reason;
      const explorerPbIds = boundAkte?.id
        ? akteDocumentsForBinding.map((doc) => doc.id)
        : protokolle.map((doc) => doc.id);
      const cta = diktatDokumenteCtaKind({ pbRecordId, explorerPbIds });
      const proto = pbRecordId ? protokolle.find((row) => row.id === pbRecordId) : undefined;
      const archiveEntry = buildDiktatArchiveEntry({
        sourceName: file.name,
        transcriptText: transcript,
        pb_record_id: pbRecordId,
        nextcloud_path: diktatNc || undefined,
        pdfUrl,
        filename:
          (typeof diktat.filename === 'string' && diktat.filename) ||
          uploaded?.display_name,
        auswertungText,
      });
      if (archiveEntry && diktatArchiveHasFileHandle(archiveEntry)) {
        const attached = attachDiktatPersistFromProtokoll(archiveEntry, proto);
        setDiktatArchive((prev) => mergeDiktatArchive(prev, attached));
        setSelectedDiktatArchiveId(attached.id);
        if (pdfUrl) {
          mergeIngestedProtokoll({
            display_name: attached.filename || attached.sourceName,
            document_url: pdfUrl,
            pb_record_id: attached.pb_record_id,
            nextcloud_path: attached.nextcloud_path,
          });
        }
      }

      const listed = Boolean(archiveEntry && diktatArchiveHasFileHandle(archiveEntry));
      setDiktatResult({
        type: 'success',
        pdfUrl,
        message: listed
          ? diktatSuccessArchiveMessage({
              transcript,
              cta,
              serverMessage: typeof diktat.message === 'string' ? diktat.message : undefined,
            })
          : (typeof diktat.message === 'string' && diktat.message.trim()) ||
            (transcript
              ? 'Transkription abgeschlossen. Protokoll nicht in PocketBase gespeichert.'
              : DIKTAT_UPLOAD_NO_TEXT),
        pb_record_id: pbRecordId,
        transcriptText: transcript || undefined,
        auswertungText,
        analysisBlockedReason,
      });

      if (analysisDecision.ok) {
        const documentContext = buildDiktatAnalysisDocumentContext({
          sourceName: file.name,
          transcriptText: transcript,
          protokollText: diktat.protokollText,
        });
        setDiktatAnalysisStart({
          documentId: pbRecordId || `diktat-${Date.now().toString(36)}`,
          documentName: file.name || 'Diktat',
          documentContext,
          extractRawChars: transcript.length,
        });
      }

      if (pbRecordId || diktatNc) {
        void loadProtokolle();
      }
    } catch (err) {
      setDiktatResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'Upload fehlgeschlagen',
      });
    } finally {
      setDiktatUploading(false);
      setDiktatFile(null);
    }
  }, [
    diktatFile,
    customer.id,
    customer.kanzlei,
    customer.email,
    customer.status,
    portalArkoWebhook,
    loadProtokolle,
    mergeIngestedProtokoll,
    boundAkte?.id,
    akteDocumentsForBinding,
    protokolle,
  ]);

  const handleDiktatDroppedAudio = useCallback(
    (file: File) => {
      setDiktatFile(file);
      setDiktatTab('upload');
      setDiktatResult(null);
      void handleDiktatUpload(file);
    },
    [handleDiktatUpload],
  );

  const handleDiktatOutputFormat = useCallback(
    async (format: DiktatOutputFormat, source: 'result' | string) => {
      setDiktatFormatError(null);
      const entry =
        source === 'result'
          ? diktatArchive.find((row) => row.id === selectedDiktatArchiveId) || diktatArchive[0]
          : diktatArchive.find((row) => row.id === source);
      let transcript = resolveDiktatOutputTranscript({
        resultTranscript: diktatResult?.transcriptText,
        entry,
        archive: diktatArchive,
      });
      if (!diktatUsableTranscript(transcript) && entry) {
        const kind = diktatFileKind(entry.filename || entry.sourceName, entry.nextcloud_path);
        if (kind === 'txt' && (entry.pdfUrl || entry.nextcloud_path)) {
          try {
            transcript = await fetchStoredDiktatTranscript({
              documentUrl: entry.pdfUrl,
              nextcloudPath: entry.nextcloud_path,
              customerId: customer.id,
              tenantExtras: portalTenantN8nExtras(customer.status, customer.email),
            });
          } catch {
            transcript = '';
          }
        }
      }
      const bodyText = buildAnalysisDocumentBody(
        entry?.sourceName || entry?.filename || 'Diktat',
        undefined,
        transcript,
      );
      const existingPdfUrl = (source === 'result' ? diktatResult?.pdfUrl : undefined) || entry?.pdfUrl;
      const existingIsPdf =
        diktatFileKind(entry?.filename || entry?.sourceName, entry?.nextcloud_path) === 'pdf';
      const existingPdf =
        existingIsPdf && existingPdfUrl
          ? {
              display_name: entry?.filename || entry?.sourceName || 'Protokoll.pdf',
              document_url: existingPdfUrl,
              pb_record_id: entry?.pb_record_id,
              nextcloud_path: entry?.nextcloud_path,
            }
          : null;
      setDiktatFormatBusy(true);
      try {
        const { fields } = await materializeDiktatOutputFormat({
          format,
          sourceName: entry?.sourceName || entry?.filename || 'Diktat',
          bodyText,
          customerId: customer.id,
          kanzlei: customer.kanzlei,
          email: customer.email,
          tenantExtras: portalTenantN8nExtras(customer.status, customer.email),
          existingPdf,
        });
        if (!fields.document_url) {
          throw new Error('Konvertierung fehlgeschlagen — keine Datei erzeugt.');
        }
        mergeIngestedProtokoll(fields);
        if (transcript) {
          const nextEntry = buildDiktatArchiveEntry({
            sourceName: fields.display_name,
            transcriptText: transcript,
            pb_record_id: fields.pb_record_id,
            nextcloud_path: fields.nextcloud_path,
            pdfUrl: fields.document_url,
            filename: fields.display_name,
            auswertungText: entry?.auswertungText,
          });
          if (nextEntry && diktatArchiveHasFileHandle(nextEntry)) {
            setDiktatArchive((prev) => mergeDiktatArchive(prev, nextEntry));
            setSelectedDiktatArchiveId(nextEntry.id);
          }
        }
        if (format === 'pdf' && source === 'result') {
          setDiktatResult((prev) =>
            prev?.type === 'success' ? { ...prev, pdfUrl: fields.document_url } : prev,
          );
        }
        await openPortalDocumentInEditor(fields);
        void loadProtokolle();
      } catch (err) {
        setDiktatFormatError(err instanceof Error ? err.message : String(err));
      } finally {
        setDiktatFormatBusy(false);
      }
    },
    [
      customer.email,
      customer.id,
      customer.kanzlei,
      customer.status,
      diktatArchive,
      diktatResult?.pdfUrl,
      diktatResult?.transcriptText,
      loadProtokolle,
      mergeIngestedProtokoll,
      openPortalDocumentInEditor,
      selectedDiktatArchiveId,
    ],
  );

  const diktatIntoAkteId = useMemo(
    () =>
      diktatIntoRememberedAkteId({
        rememberedAkteId: recallPortalAkteRahmenOpen(),
        akten: portalAktenList,
        listReady: portalAktenList.length > 0,
      }),
    [portalAktenList, view],
  );

  const handleDiktatIntoAkte = useCallback(async () => {
    const akteId = diktatIntoRememberedAkteId({
      rememberedAkteId: recallPortalAkteRahmenOpen(),
      akten: portalAktenList,
      listReady: portalAktenList.length > 0,
    });
    const rememberedFolder = diktatIntoRememberedAkteFolder({
      rememberedAkteId: akteId,
      akten: portalAktenList,
      listReady: portalAktenList.length > 0,
    });
    const targetFolder = portalAkteAssignTargetFolder(akteId);
    if (!akteId || !rememberedFolder || targetFolder !== rememberedFolder) return;
    const transcript = diktatUsableTranscript(diktatResult?.transcriptText || '');
    if (!transcript) {
      setDiktatIntoAkteNote('Ohne Transkript wird nichts in die Akte gelegt.');
      return;
    }
    setDiktatIntoAkteBusy(true);
    setDiktatIntoAkteNote(null);
    try {
      const { fields } = await materializeDiktatOutputFormat({
        format: 'txt',
        sourceName: 'Diktat',
        bodyText: transcript,
        customerId: customer.id,
        kanzlei: customer.kanzlei,
        email: customer.email,
        tenantExtras: portalTenantN8nExtras(customer.status, customer.email),
      });
      const pbId = fields.pb_record_id?.trim() || '';
      const sourcePath = fields.nextcloud_path?.trim() || '';
      if (!pbId || !sourcePath) {
        throw new Error('Transkript ist nicht als Dokument angekommen.');
      }
      await createPortalFolderViaApi({
        customerId: customer.id,
        folderPath: targetFolder,
        customerStatus: customer.status,
        customerEmail: customer.email,
      });
      const moved = await movePortalDocumentViaFolderApi({
        customerId: customer.id,
        customerStatus: customer.status,
        customerEmail: customer.email,
        pbRecordId: pbId,
        sourcePath,
        targetFolderPath: targetFolder,
      });
      if (moved.akte_id !== akteId) {
        throw new Error('Verschieben konnte der Ziel-Akte nicht bestätigt werden.');
      }
      const canonical = portalAkteNcPathFromMovedDocument(moved.nextcloud_path, akteId);
      if (!canonical) {
        throw new Error('Verschieben konnte der Ziel-Akte nicht bestätigt werden.');
      }
      const token = getPortalPbToken();
      if (token) {
        const saved = await updatePortalAkteNcPath(token, akteId, canonical);
        if (saved.ok === false) throw new Error(saved.error);
      }
      setDiktatIntoAkteNote('Transkript liegt in dieser Akte.');
      void loadProtokolle();
    } catch (err) {
      setDiktatIntoAkteNote(err instanceof Error ? err.message : String(err));
    } finally {
      setDiktatIntoAkteBusy(false);
    }
  }, [
    customer.email,
    customer.id,
    customer.kanzlei,
    customer.status,
    diktatResult?.transcriptText,
    loadProtokolle,
    portalAktenList,
  ]);

  // ── Handler: Litigation Upload ─────────────────────────────────────────────

  const handleLitigationUpload = async () => {
    setLitigationValidationError('');
    setUploadResult(null);

    if (!optIn) {
      setLitigationValidationError(
        'Bitte bestätigen Sie den Datenschutzhinweis (Opt-In), bevor die Analyse startet.',
      );
      return;
    }

    const trimmedText = schriftsatzText.trim();
    const hasPdf = files.length > 0;
    const hasText = trimmedText.length >= LITIGATION_TEXT_MIN_CHARS;

    if (litigationUploadMode === 'pdf' && !hasPdf) {
      setLitigationValidationError('Bitte mindestens eine PDF-Datei auswählen.');
      return;
    }
    if (litigationUploadMode === 'text' && !hasText) {
      setLitigationValidationError(
        `Bitte Schriftsatztext eingeben (mindestens ${LITIGATION_TEXT_MIN_CHARS} Zeichen).`,
      );
      return;
    }

    setUploading(true);
    const webhookUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_LITIGATION_UPLOAD_URL);
    try {
      if (!hasPortalAuthzToken()) {
        setLitigationValidationError(
          'Litigation-Upload: Bitte neu anmelden (kein gültiges Portal-Token).',
        );
        setUploading(false);
        return;
      }
      const litigationUploadHeaders: Record<string, string> = {
        ...portalAuthzHeaders(webhookUrl),
      };
      let res: Response;
      if (litigationUploadMode === 'text' && hasText) {
        res = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            ...litigationUploadHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            optIn: true,
            customerId: customer.id,
            kanzlei: customer.kanzlei,
            schriftsatzText: trimmedText,
          }),
          signal: AbortSignal.timeout(300000),
        });
      } else {
        const formData = new FormData();
        files.forEach((f, i) => formData.append(i === 0 ? 'file' : `file_${i}`, f));
        formData.append('fileCount', String(files.length));
        formData.append('customerId', customer.id);
        formData.append('kanzlei', customer.kanzlei);
        formData.append('optIn', 'true');
        res = await fetch(webhookUrl, {
          method: 'POST',
          headers: litigationUploadHeaders,
          body: formData,
          signal: AbortSignal.timeout(300000),
        });
      }

      if (res.ok) {
        try {
          const data = await res.json() as Record<string, unknown>;
          setUploadResult({
            type: 'success',
            pdfUrl: typeof data.pdfUrl === 'string' ? data.pdfUrl : undefined,
            summary: data.summary as LitigationSummary | undefined,
            pdfLinkExpiresAt: typeof data.pdfLinkExpiresAt === 'string' ? data.pdfLinkExpiresAt : undefined,
            pdfGenerationNote: typeof data.pdfGenerationNote === 'string' ? data.pdfGenerationNote : undefined,
          });
          setFiles([]);
          setSchriftsatzText('');
          setOptIn(false);
        } catch {
          setUploadResult({ type: 'success' });
          setFiles([]);
          setSchriftsatzText('');
          setOptIn(false);
        }
      } else {
        let errMsg = formatHttpStatusForUser(res.status, 'upload');
        try {
          const data = (await res.json()) as Record<string, unknown>;
          if (typeof data.message === 'string' && data.message.trim()) {
            errMsg = data.message.trim();
          } else {
            errMsg = formatPortalUploadError(res, data, 'upload');
          }
        } catch {
          logPortalError('Litigation upload', `HTTP ${res.status}`);
        }
        setUploadResult({ type: 'error', message: errMsg });
      }
    } catch (err) {
      logPortalError('Litigation upload', err);
      setUploadResult({ type: 'error', message: formatConnectionError() });
    } finally {
      setUploading(false);
    }
  };

  // ── Handler: Vertrags-QA Upload ───────────────────────────────────────────

  const handleVertragQAUpload = async () => {
    const hasAtLeastOne = vqaPreisliste || vqaGruendungspaket || vqaMarkeIp;
    if (!hasAtLeastOne || !vqaOptIn) return;
    setVqaUploading(true); setVqaResult(null);
    try {
      const formData = new FormData();
      if (vqaPreisliste)      formData.append('file_preisliste',      vqaPreisliste);
      if (vqaGruendungspaket) formData.append('file_gruendungspaket', vqaGruendungspaket);
      if (vqaMarkeIp)         formData.append('file_marke_ip',        vqaMarkeIp);
      formData.append('customerId', customer.id);
      formData.append('kanzlei', customer.kanzlei);
      formData.append('optIn', 'true');
      const res = await fetch('/webhook/vertrags-qa-upload', {
        method: 'POST',
        body: formData,
        signal: AbortSignal.timeout(300000),
      });
      if (res.ok) {
        try {
          const data = await res.json();
          setVqaResult({
            type: 'success',
            pdfUrl: data.pdfUrl,
            pdfLinkExpiresAt: data.pdfLinkExpiresAt,
            pdfGenerationNote: data.pdfGenerationNote,
            gesamtstatus: data.gesamtstatus,
            statistik: data.statistik,
            llmBeurteilung: data.llmResult?.gesamtbeurteilung,
            risikostufe: data.llmResult?.risikostufe,
          });
        } catch { setVqaResult({ type: 'success' }); }
      } else { setVqaResult({ type: 'error' }); }
    } catch { setVqaResult({ type: 'error' }); }
    finally {
      setVqaUploading(false);
      setVqaPreisliste(null); setVqaGruendungspaket(null); setVqaMarkeIp(null);
      setVqaOptIn(false);
    }
  };

  // ── Handler: Feedback absenden ─────────────────────────────────────────────

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFbLoading(true); setFbError('');
    try {
      const result = await submitCustomerFeedback({
        customerId: customer.id,
        kategorie: fbKategorie,
        nachricht: fbNachricht.trim(),
        client_submitted_at: nowClientSubmittedAtIso(),
        diag: {
          symptom: fbSymptom,
          ui_view: 'feedback',
          pane: 'sidebar_feedback',
          tab: 'feedback',
          doc_count: openDocuments.length,
          oo_open: false,
          akte_bound: Boolean(boundAkte?.id || portalDocumentAkteNcPath),
        },
      });
      setFbDiagCode(result.diagCode);
      setFbSuccess(true);
      setFbNachricht('');
    } catch (err) {
      setFbError(err instanceof Error ? err.message : 'Feedback konnte nicht gespeichert werden.');
    } finally {
      setFbLoading(false);
    }
  };

  const handleSlashFocus = useCallback(() => {
    if (isDokumenteEditorPaneVisible) {
      window.setTimeout(() => {
        document.querySelector<HTMLTextAreaElement>('[data-portal-arko-input]')?.focus();
      }, 0);
      return;
    }
    if (hasActivePortalDocument) {
      setView('document_editor');
      setDokumenteAreaTab('editor');
    } else {
      setView('dokumente');
      setDokumenteAreaTab('hub');
    }
    window.setTimeout(() => {
      document.querySelector<HTMLTextAreaElement>('[data-portal-arko-input]')?.focus();
    }, 120);
  }, [isDokumenteEditorPaneVisible, hasActivePortalDocument]);

  const handleRouteAudioToDiktat = useCallback((file: File) => {
    setDiktatFile(file);
    setDiktatTab('upload');
    setDiktatResult(null);
    setView('diktat');
  }, []);

  const handleRecentDocsRefresh = useCallback(() => {
    setRecentDocs(loadRecentPortalDocuments(customer.id, boundAkte?.id));
  }, [boundAkte?.id, customer.id]);

  useEffect(() => {
    setRecentDocs(loadRecentPortalDocuments(customer.id, boundAkte?.id));
  }, [boundAkte?.id, customer.id]);

  const portalHomeRecents = useMemo(
    () => buildPortalHomeRecents(customer.id, recentDocs, { lastArkoIntent }),
    [customer.id, recentDocs, lastArkoIntent],
  );

  const handleRecentItemClick = useCallback(
    (item: PortalHomeRecentItem) => {
      if (item.kind === 'document' && item.document) {
        const d = item.document;
        const document_url = resolveProtokollDocumentUrl(protokolle, {
          pb_record_id: d.pb_record_id,
          document_url: d.document_url,
        });
        if (
          isPremium &&
          isPortalDocxForEditor(d.display_name, d.nextcloud_path) &&
          document_url
        ) {
          openPortalDocumentInEditor({
            display_name: d.display_name,
            document_url,
            pb_record_id: d.pb_record_id,
            nextcloud_path: d.nextcloud_path,
          });
        } else {
          handlePortalNavigate('dokumente');
        }
        return;
      }
      if (item.kind === 'arko') {
        if (item.view === 'dokumente' && hasActivePortalDocument) {
          setView('document_editor');
          setDokumenteAreaTab('editor');
        } else {
          setView('chat');
        }
        return;
      }
      if (item.view) handlePortalNavigate(item.view);
    },
    [protokolle, isPremium, hasActivePortalDocument, openPortalDocumentInEditor, handlePortalNavigate],
  );

  // ── View: Feedback & Wünsche ───────────────────────────────────────────────

  const feedbackPanel = (
    <PortalLightSurface
      surface={portalFeedbackSurfaceClass}
      className="space-y-6 animate-fade-in max-w-2xl mx-auto"
      testId="portal-feedback-pane"
    >
      <div>
        <h1 className="text-2xl font-bold text-white">Feedback & Wünsche</h1>
        <p className="text-slate-400 mt-1">Teilen Sie uns mit, was Sie beschäftigt – wir lesen und beantworten alles persönlich.</p>
      </div>

      {fbSuccess ? (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 text-center space-y-3">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
          <h2 className="text-white font-bold text-lg">Vielen Dank!</h2>
          <p className="text-slate-400 text-sm">Ihre Nachricht ist bei uns angekommen. Andreas liest persönlich mit und meldet sich wenn nötig zurück.</p>
          <FeedbackDiagCodeLine diagCode={fbDiagCode} />
          <button
            onClick={() => {
              setFbSuccess(false);
              setFbDiagCode(null);
              setFbSymptom('other');
            }}
            className="mt-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            Weitere Nachricht senden
          </button>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5">
          {/* Kategorie */}
          <div>
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-3">Art der Nachricht</label>
            <div className="flex gap-2 flex-wrap">
              {FEEDBACK_KATEGORIEN.map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFbKategorie(k)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                    fbKategorie === k
                      ? k === 'Problem'
                        ? 'bg-red-500/20 border-red-500/40 text-red-300'
                        : k === 'Anregung'
                          ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                          : 'bg-slate-700 border-slate-600 text-white'
                      : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
                  }`}
                >
                  {k === 'Anregung' ? '💡 Anregung' : k === 'Verbesserung' ? '💬 Verbesserung' : '⚠️ Problem'}
                </button>
              ))}
            </div>
          </div>

          {/* Symptom */}
          <div>
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-3">
              Symptom
            </label>
            <div className="flex gap-2 flex-wrap">
              {FEEDBACK_SYMPTOMS.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setFbSymptom(item)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                    fbSymptom === item
                      ? 'bg-cyan-500/15 border-cyan-400/35 text-cyan-100'
                      : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
                  }`}
                >
                  {FEEDBACK_SYMPTOM_LABELS[item]}
                </button>
              ))}
            </div>
          </div>

          {/* Nachricht */}
          <div>
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">
              Ihre Nachricht (optional)
            </label>
            <textarea
              value={fbNachricht}
              onChange={e => setFbNachricht(e.target.value)}
              maxLength={1000}
              placeholder="Optionaler Hinweis — keine Mandantennamen oder Chat-Volltext."
              rows={6}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors resize-none leading-relaxed"
            />
          </div>

          {fbError && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {fbError}
            </div>
          )}

          <button
            onClick={handleFeedbackSubmit}
            disabled={fbLoading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
          >
            {fbLoading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet…</>
              : <><Send className="w-4 h-4" /> Nachricht senden</>}
          </button>

          <div className="flex items-start gap-2 bg-slate-800/40 rounded-xl px-3 py-2.5">
            <Shield className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <p className="text-slate-500 text-xs leading-relaxed">
              Ihre Nachricht wird vertraulich behandelt. Kein Call-Center – Andreas liest persönlich mit.
            </p>
          </div>
        </div>
      )}
    </PortalLightSurface>
  );

  // ── View: Dokument-Editor (OnlyOffice Embed – Shell) ─────────────────────────
  // DS-Basis: VITE_ONLYOFFICE_DOCUMENT_SERVER_URL · JWT: POST VITE_ONLYOFFICE_ISSUE_JWT_URL → token → DocsAPI (iframe intern)
  // Stabile Komponenten-Identitaet (Re-Init-Bug 07/2026): nicht inline pro Parent-Render neu definieren.

  const DocumentEditorAlphaView = useMemo(
    () =>
      function DocumentEditorAlphaViewStable({
        workModeOpen,
        onWorkModeChange,
        surface = 'kanzlei',
        akteTitle = '',
        akteId = '',
      }: {
        workModeOpen: boolean;
        onWorkModeChange: (open: boolean) => void;
        surface?: 'kanzlei' | 'akte';
        akteTitle?: string;
        akteId?: string;
      }) {
        const editorScope = portalDocumentEditorScopeRef.current;
        const alphaDocSource = surface === 'akte' ? editorScope.akteDocSource : editorScope.alphaDocSource;
        const alphaDocReady = surface === 'akte' ? editorScope.akteDocReady : editorScope.alphaDocReady;
        const onlyofficeEditorInstanceRef = surface === 'akte'
          ? editorScope.akteOnlyofficeEditorInstanceRef
          : editorScope.onlyofficeEditorInstanceRef;
        const activeDocumentId = surface === 'akte'
          ? (alphaDocSource ? documentTabId(alphaDocSource) : null)
          : editorScope.activeDocumentId;
        const plainTextDocumentChrome = surface === 'akte'
          ? alphaDocSource != null &&
            isPortalNativePlainTextExtension(resolveAlphaDocRoutingExtension(alphaDocSource))
          : editorScope.plainTextDocumentChrome;
        const {
          customer,
          nativeTextUi,
          pinAlphaDocSource,
          mandantenRoot,
          setOnlyofficeTabDirty,
          scheduleOnlyofficeExtractRefresh,
          portalArkoDocumentContext,
          portalParagraphIndexMap,
          handleMarkPortalDocumentAnalyzed,
          portalDocumentDisplayName,
          defaultDocumentContextEnabled,
          portalDocumentExtractFingerprint,
          onlyofficeExtractRevision,
          portalDocumentNcPath,
          portalDocumentAkteNcPath,
          portalArkoWebhook,
          portalArkoInitialBotMessage,
          portalWebhookExtraBody,
          isPremium,
          contractGeneratorEnabled,
          generateContractFromChat,
          exportSchreibenPdf,
          exportSchreibenDocx,
          applySynthesisToDocument,
          memcoreChat,
          portalOpenDocumentsMeta,
          portalAvailableDocuments,
          portalArkoDocumentContextForChat,
          portalArkoOpenDocumentsMeta,
          portalArkoAvailableDocuments,
          portalActiveDocumentExtract,
          arkoActiveDocumentId,
          boundAkte,
          boundAkteOptions,
          akteDocumentCount,
          handleBoundAkteChange,
          akteDocsPanelOpenNonce,
          onOpenAkteDocument,
          onRenameAkteDocument,
          canWriteAkteDocuments,
          onDocumentSelectionControllerChange,
          onSelectedDocumentIdsChange,
          extractingDocumentIds,
          onlyOfficeTheme,
          setOnlyOfficeTheme,
          registerPortalDocumentFromFields,
        } = editorScope;

        const alphaDocSourceKey = alphaDocSource?.key ?? null;
        const alphaDocSourceUrl = alphaDocSource?.url ?? null;

        const DOC_ARKO_SPLIT_STORAGE_KEY = `arkorbit_portal_arko_split_v1__${customer.id}`;
    const DOC_ARKO_SPLIT_LEGACY_KEY = 'portal_doc_arko_split_pct';
    const DOC_ARKO_PANEL_COLLAPSED_KEY = 'portal_doc_arko_panel_collapsed';
    const DOC_ARKO_SPLIT_DEFAULT = 58;
    const DOC_ARKO_LEFT_MIN_PX = 280;
    const DOC_ARKO_RIGHT_MIN_PX = 320;
    const DOC_ARKO_LEFT_MIN_PCT = 28;
    const DOC_ARKO_RIGHT_MIN_PCT = 30;

    const dsBase = ONLYOFFICE_DOCUMENT_SERVER_URL;
    const issueJwtRaw = ONLYOFFICE_ISSUE_JWT_URL;
    const bridgeToken = ONLYOFFICE_JWT_BRIDGE_TOKEN;
    const hasDsUrl = Boolean(dsBase);
    const hasIssueJwtUrl = Boolean(issueJwtRaw);
    const shouldOoEmbed = hasDsUrl && hasIssueJwtUrl;
    const canWriteDocuments = canWritePortalDocuments(customer);
    const canPortalDocumentUpload = Boolean(PORTAL_DOCUMENT_UPLOAD_URL) && canWriteDocuments;
    const ncShareAllowedHosts = useMemo(() => getPortalNextcloudShareAllowedHosts(), []);
    const canPortalNcShareImport = Boolean(
      PORTAL_NEXTCLOUD_SHARE_IMPORT_URL && ncShareAllowedHosts.size > 0 && canWriteDocuments,
    );
    const showDocIngestCard = canPortalDocumentUpload;

    type OoUi =
      | { kind: 'inactive' }
      | { kind: 'loading' }
      | { kind: 'iframe'; src: string }
      | { kind: 'docsapi' }
      | { kind: 'error'; message: string };

    const editorMountRef = useRef<HTMLDivElement>(null);
    const editorInstanceRef = useRef<OnlyofficeDocEditorHandle | null>(null);
    const placeholderIdRef = useRef(`oo-${Math.random().toString(36).slice(2, 11)}`);
    const ooWasDirtyRef = useRef(false);

    const [ooUi, setOoUi] = useState<OoUi>(() =>
      shouldOoEmbed ? { kind: 'loading' } : { kind: 'inactive' },
    );
    /** DocsAPI onAppReady oder iframe-Editor-URL — unsichtbares E2E-Signal, kein Pixel-OCR. */
    const [ooAppReady, setOoAppReady] = useState(false);

    // Hinweis: `nativeTextUi`, `setNativeTextUi`, `onlyofficeExtractTextUi` und
    // `setOnlyofficeExtractTextUi` werden seit 27.05.2026 im outer
    // `CustomerPortal`-Scope gehalten (Closure greift hier weiter, kein Refactor
    // der Setter-Aufrufe noetig). Grund: documentContext muss auch im normalen
    // ARKO-Chat-Tab (`view === 'chat'`) verfuegbar sein, nicht nur im
    // Document-Editor-Pane.

    const splitContainerRef = useRef<HTMLDivElement>(null);
    const [leftSplitPct, setLeftSplitPct] = useState(DOC_ARKO_SPLIT_DEFAULT);
    const [splitHydrated, setSplitHydrated] = useState(false);
    const [arkoPanelCollapsed, setArkoPanelCollapsed] = useState(false);

    const clampLeftSplitPct = useCallback((pct: number) => {
      const el = splitContainerRef.current;
      if (!el) {
        const maxOnlyPct = 100 - DOC_ARKO_RIGHT_MIN_PCT;
        return Math.min(maxOnlyPct, Math.max(DOC_ARKO_LEFT_MIN_PCT, pct));
      }
      const w = el.getBoundingClientRect().width;
      if (w <= 0) return pct;
      const minFromPx = (DOC_ARKO_LEFT_MIN_PX / w) * 100;
      const maxFromPx = ((w - DOC_ARKO_RIGHT_MIN_PX) / w) * 100;
      const minL = Math.max(DOC_ARKO_LEFT_MIN_PCT, minFromPx);
      const maxL = Math.min(100 - DOC_ARKO_RIGHT_MIN_PCT, maxFromPx);
      if (maxL < minL) return (minL + maxL) / 2;
      return Math.min(maxL, Math.max(minL, pct));
    }, []);

    useLayoutEffect(() => {
      try {
        let raw = localStorage.getItem(DOC_ARKO_SPLIT_STORAGE_KEY);
        if (raw == null) raw = localStorage.getItem(DOC_ARKO_SPLIT_LEGACY_KEY);
        if (raw != null) {
          const n = Number(raw);
          if (Number.isFinite(n)) setLeftSplitPct(n);
        } else if (typeof window !== 'undefined' && window.innerWidth < 1280) {
          setLeftSplitPct(68);
        }
        const collapsedRaw = localStorage.getItem(DOC_ARKO_PANEL_COLLAPSED_KEY);
        if (collapsedRaw === '1') setArkoPanelCollapsed(true);
      } catch {
        /* ignore */
      }
      setSplitHydrated(true);
    }, [DOC_ARKO_SPLIT_STORAGE_KEY]);

    useEffect(() => {
      if (!splitHydrated) return;
      try {
        localStorage.setItem(DOC_ARKO_PANEL_COLLAPSED_KEY, arkoPanelCollapsed ? '1' : '0');
      } catch {
        /* ignore */
      }
    }, [arkoPanelCollapsed, splitHydrated]);

    useLayoutEffect(() => {
      if (!splitHydrated) return;
      setLeftSplitPct(p => clampLeftSplitPct(p));
    }, [splitHydrated, clampLeftSplitPct]);

    const splitDraggingRef = useRef(false);
    const dragSplitRef = useRef<{ startX: number; startPct: number } | null>(null);
    const splitDragCleanupRef = useRef<((ev?: MouseEvent) => void) | null>(null);
    const splitDragRafRef = useRef<number | null>(null);
    const splitDragGuideRef = useRef<HTMLDivElement>(null);
    const splitDragShieldRef = useRef<HTMLDivElement>(null);
    const splitDragLastClientXRef = useRef<number | null>(null);

    useEffect(() => {
      if (!splitHydrated || typeof ResizeObserver === 'undefined') return;
      const el = splitContainerRef.current;
      if (!el) return;
      const ro = new ResizeObserver(() => {
        if (splitDraggingRef.current) return;
        setLeftSplitPct(p => clampLeftSplitPct(p));
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, [splitHydrated, clampLeftSplitPct]);

    const applyDocArkoSplitPct = useCallback((pct: number) => {
      splitContainerRef.current?.style.setProperty('--doc-arko-left-pct', `${pct}%`);
    }, []);

    const notifyOnlyOfficePaneResize = useCallback(() => {
      const inst = editorInstanceRef.current as { resizeEditor?: () => void } | null;
      inst?.resizeEditor?.();
      window.dispatchEvent(new Event('resize'));
    }, []);

    const commitDocArkoSplitPct = useCallback(
      (pct: number) => {
        const clamped = clampLeftSplitPct(pct);
        applyDocArkoSplitPct(clamped);
        setLeftSplitPct(clamped);
        try {
          localStorage.setItem(DOC_ARKO_SPLIT_STORAGE_KEY, String(clamped));
        } catch {
          /* ignore */
        }
        notifyOnlyOfficePaneResize();
      },
      [clampLeftSplitPct, applyDocArkoSplitPct, notifyOnlyOfficePaneResize, DOC_ARKO_SPLIT_STORAGE_KEY],
    );

    const positionSplitDragGuide = useCallback((clientX: number) => {
      const cont = splitContainerRef.current;
      const guide = splitDragGuideRef.current;
      if (!cont || !guide) return;
      const rect = cont.getBoundingClientRect();
      if (rect.width <= 0) return;
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      guide.style.left = `${x}px`;
    }, []);

    const endSplitDragVisuals = useCallback(() => {
      splitDraggingRef.current = false;
      const cont = splitContainerRef.current;
      cont?.classList.remove('portal-split-drag-active');
      const guide = splitDragGuideRef.current;
      const shield = splitDragShieldRef.current;
      if (guide) guide.style.display = 'none';
      if (shield) shield.style.display = 'none';
    }, []);

    useEffect(
      () => () => {
        if (splitDragRafRef.current != null) {
          cancelAnimationFrame(splitDragRafRef.current);
          splitDragRafRef.current = null;
        }
        splitDragCleanupRef.current?.();
        splitDragCleanupRef.current = null;
        endSplitDragVisuals();
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      },
      [endSplitDragVisuals],
    );

    const handleSplitDividerMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        e.preventDefault();
        splitDragCleanupRef.current?.();

        const cont = splitContainerRef.current;
        if (!cont) return;

        dragSplitRef.current = { startX: e.clientX, startPct: leftSplitPct };
        splitDragLastClientXRef.current = e.clientX;
        splitDraggingRef.current = true;
        cont.classList.add('portal-split-drag-active');

        const guide = splitDragGuideRef.current;
        const shield = splitDragShieldRef.current;
        if (guide) {
          guide.style.display = 'block';
          positionSplitDragGuide(e.clientX);
        }
        if (shield) shield.style.display = 'block';

        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';

        const onMove = (ev: MouseEvent) => {
          if (!dragSplitRef.current) return;
          splitDragLastClientXRef.current = ev.clientX;
          if (splitDragRafRef.current != null) return;
          splitDragRafRef.current = requestAnimationFrame(() => {
            splitDragRafRef.current = null;
            const x = splitDragLastClientXRef.current;
            if (x == null) return;
            positionSplitDragGuide(x);
          });
        };

        const onUp = (ev?: MouseEvent) => {
          if (splitDragRafRef.current != null) {
            cancelAnimationFrame(splitDragRafRef.current);
            splitDragRafRef.current = null;
          }

          const st = dragSplitRef.current;
          const container = splitContainerRef.current;
          let finalPct = leftSplitPct;
          if (st && container) {
            const w = container.getBoundingClientRect().width;
            if (w > 0) {
              const clientX = ev?.clientX ?? splitDragLastClientXRef.current ?? st.startX;
              const deltaPct = ((clientX - st.startX) / w) * 100;
              finalPct = clampLeftSplitPct(st.startPct + deltaPct);
            }
          }

          splitDragLastClientXRef.current = null;
          dragSplitRef.current = null;
          document.body.style.userSelect = '';
          document.body.style.cursor = '';
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          splitDragCleanupRef.current = null;
          endSplitDragVisuals();
          commitDocArkoSplitPct(finalPct);
        };

        splitDragCleanupRef.current = onUp;
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      },
      [leftSplitPct, clampLeftSplitPct, positionSplitDragGuide, endSplitDragVisuals, commitDocArkoSplitPct],
    );

    // `alphaDocSource` / `alphaDocReady` werden seit 27.05.2026 im outer
    // `CustomerPortal`-Scope gehalten (siehe Kommentar oben). Closure-Zugriff
    // auf `setAlphaDocSource` / `setAlphaDocReady` bleibt erhalten.
    const [docUploadBusy, setDocUploadBusy] = useState(false);
    const [docUploadMsg, setDocUploadMsg] = useState<string | null>(null);
    const [docIngestOpen, setDocIngestOpen] = useState(!alphaDocReady);
    const [ncShareUrlInput, setNcShareUrlInput] = useState('');
    const [ncShareBusy, setNcShareBusy] = useState(false);

    // Plaintext- und OnlyOffice-Text-Loader sowie der initiale PB-Pull leben
    // seit 27.05.2026 im outer `CustomerPortal`-Scope (siehe Kommentar oben).
    // Schreibende Setter (`setAlphaDocSource` / `setAlphaDocReady` /
    // `setNativeTextUi` / `setOnlyofficeExtractTextUi`) bleiben via Closure
    // im inneren View nutzbar — z. B. `ingestPortalDocumentFromN8nJson`.

    const ingestPortalDocumentFromN8nJson = useCallback(
      async (
        data: Record<string, unknown>,
        res: Response,
        _opts?: { documentKeyPrefix?: string },
      ) => {
        const fields = parsePortalDocumentN8nResponse(data, res);
        await registerPortalDocumentFromFields(fields);
        setDocIngestOpen(false);
        setDocUploadMsg(`„${fields.display_name}" wird geöffnet…`);
      },
      [registerPortalDocumentFromFields],
    );

    const handlePortalDocumentUpload = useCallback(
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!hasPortalAuthzToken()) {
          setDocUploadMsg('Upload ist derzeit nicht verfügbar — bitte neu anmelden.');
          return;
        }
        const form = e.currentTarget;
        const fd = new FormData(form);
        fd.set('customerId', customer.id);
        const tenantExtras = getPortalTenantN8nExtras(customer.status, customer.email);
        if (tenantExtras.tenant) {
          fd.set('tenant', tenantExtras.tenant);
        }
        setDocUploadBusy(true);
        setDocUploadMsg(null);
        try {
          const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
          const res = await fetch(url, {
            method: 'POST',
            body: fd,
            // Kein Session-Cookie zum n8n-Webhook: Auth nur über den PB-Bearer.
            // `include` würde CORS mit Access-Control-Allow-Credentials erzwingen (Labor: Preflight-Fehler).
            credentials: 'omit',
            headers: portalAuthzHeaders(url),
          });
          let data: Record<string, unknown> = {};
          try {
            data = (await res.json()) as Record<string, unknown>;
          } catch {
            /* ignore */
          }
          await ingestPortalDocumentFromN8nJson(data, res, { documentKeyPrefix: 'portal-upload' });
          form.reset();
        } catch (err) {
          const msg = err instanceof Error ? err.message : formatConnectionError();
          setDocUploadMsg(msg);
        } finally {
          setDocUploadBusy(false);
        }
      },
      [customer.id, customer.status, customer.email, ingestPortalDocumentFromN8nJson],
    );

    const handleNextcloudShareImport = useCallback(async () => {
      if (!hasPortalAuthzToken()) {
        setDocUploadMsg('Nextcloud-Import: Bitte neu anmelden (kein gültiges Portal-Token).');
        return;
      }
      const validated = validateNextcloudSharePasteUrl(ncShareUrlInput, ncShareAllowedHosts);
      if (validated.ok === false) {
        setDocUploadMsg(validated.error);
        return;
      }
      setNcShareBusy(true);
      setDocUploadMsg(null);
      try {
        const url = resolveOnlyofficeIssueJwtUrl(PORTAL_NEXTCLOUD_SHARE_IMPORT_URL);
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...portalAuthzHeaders(url),
          },
          credentials: 'omit',
          body: JSON.stringify({
            shareUrl: validated.href,
            customerId: customer.id,
            ...portalTenantN8nExtras(customer.status, customer.email),
          }),
        });
        let data: Record<string, unknown> = {};
        try {
          data = (await res.json()) as Record<string, unknown>;
        } catch {
          /* ignore */
        }
        await ingestPortalDocumentFromN8nJson(data, res, { documentKeyPrefix: 'portal-nc-share' });
        setNcShareUrlInput('');
      } catch (err) {
        const msg = err instanceof Error ? err.message : formatConnectionError();
        setDocUploadMsg(msg);
      } finally {
        setNcShareBusy(false);
      }
    }, [customer.id, ingestPortalDocumentFromN8nJson, ncShareAllowedHosts, ncShareUrlInput]);

    useEffect(() => {
      if (!splitHydrated) return;
      if (!alphaDocReady || !alphaDocSource) {
        setOoUi({ kind: 'loading' });
        return;
      }
      const plainExt = resolveAlphaDocRoutingExtension(alphaDocSource);
      if (isPortalNativePlainTextExtension(plainExt)) {
        closeOnlyofficeEditorInstance(editorInstanceRef.current);
        if (onlyofficeEditorInstanceRef.current === editorInstanceRef.current) {
          onlyofficeEditorInstanceRef.current = null;
        }
        editorInstanceRef.current = null;
        if (editorMountRef.current) {
          editorMountRef.current.innerHTML = '';
        }
        setOoUi({ kind: 'inactive' });
        return;
      }
      if (!hasDsUrl || !hasIssueJwtUrl || !dsBase || !issueJwtRaw) {
        setOoUi({ kind: 'inactive' });
        return;
      }

      const activeDocTabId = documentTabId(alphaDocSource);
      const jwtRateKey = portalOnlyofficeRateLimitKey('jwt', activeDocTabId);
      let cancelled = false;
      const abortController = new AbortController();

      const run = async () => {
        if (portalOnlyofficeRateLimitBlocked(jwtRateKey)) {
          const remainSec = Math.ceil(portalOnlyofficeRateLimitRemainingMs(jwtRateKey) / 1000);
          setOoUi({
            kind: 'error',
            message: `Zu viele Anfragen in kurzer Zeit. Bitte ${remainSec} Sekunden warten und erneut öffnen.`,
          });
          return;
        }

        setOoUi({ kind: 'loading' });
        const wasDirty = dirtyOnlyofficeTabsRef.current.has(activeDocTabId) || ooWasDirtyRef.current;
        closeOnlyofficeEditorInstance(editorInstanceRef.current, {
          preferDestroy: !wasDirty,
        });
        if (onlyofficeEditorInstanceRef.current === editorInstanceRef.current) {
          onlyofficeEditorInstanceRef.current = null;
        }
        if (onlyofficeAppReadyDocumentIdRef.current === activeDocTabId) {
          onlyofficeAppReadyDocumentIdRef.current = null;
        }
        editorInstanceRef.current = null;
        if (editorMountRef.current) {
          editorMountRef.current.innerHTML = '';
        }

        const ooFilenameHint = onlyofficeFilenameHintForMeta(alphaDocSource);
        const ooMeta = onlyofficeDocumentMetaFromUrl(alphaDocSource.url, ooFilenameHint);
        const portalOoUserName =
          ((customer.name && String(customer.name).trim()) || customer.kanzlei || customer.email || 'Mandant')
            .trim()
            .slice(0, 128) || 'Mandant';
        const portalOoUserId = sanitizeOnlyofficeDocumentKey(`portal-user-${customer.id}`);
        const cbRaw = ONLYOFFICE_CALLBACK_URL.trim();
        const callbackUrlResolved = cbRaw ? resolveOnlyofficeIssueJwtUrl(cbRaw) : '';
        const resolvedWebdavPath =
          alphaDocSource.nextcloudWebdavPath?.trim() ||
          (alphaDocSource.nextcloudPathFromPb
            ? normalizePortalWebdavPathFromPb(alphaDocSource.nextcloudPathFromPb)
            : undefined) ||
          inferNextcloudPortalDocumentsPath(
            customer.id,
            alphaDocSource.portalDocumentDisplayName ?? alphaDocSource.title ?? '',
            mandantenRoot,
            customer.kanzlei_id,
          );
        const userdataJson = buildOnlyofficeEditorUserdataJson({
          portalCustomerId: customer.id,
          kanzleiId: customer.kanzlei_id,
          nextcloudWebdavPath: resolvedWebdavPath,
          pbRecordId: alphaDocSource.pbRecordId,
          mandantenRoot,
        });
        const editorConfig: Record<string, unknown> = {
          mode: canWritePortalDocuments(customer) ? 'edit' : 'view',
          lang: 'de',
          region: 'de-DE',
          user: {
            id: portalOoUserId,
            name: portalOoUserName,
          },
          customization: buildOnlyofficeEditorCustomization(resolveOnlyofficeUiTheme(onlyOfficeTheme)),
        };
        if (callbackUrlResolved && canWritePortalDocuments(customer)) {
          editorConfig.callbackUrl = callbackUrlResolved;
        }
        if (userdataJson) {
          editorConfig.userdata = userdataJson;
        }
        const onlyofficeConfig: Record<string, unknown> = {
          documentType: ooMeta.documentType,
          document: {
            fileType: ooMeta.fileType,
            key: alphaDocSource.key,
            title: ooFilenameHint,
            url: alphaDocSource.url,
          },
          editorConfig,
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        // Traefik CEO-Basic nutzt Authorization: Basic — kein Bearer auf dieselbe Request,
        // sonst 401. Bridge-Token separat (n8n ONLYOFFICE_ISSUE_JWT liest X-Arkorbit-Onlyoffice-Bridge).
        if (bridgeToken) {
          headers['X-Arkorbit-Onlyoffice-Bridge'] = bridgeToken;
        }

        try {
          const issueUrl = resolveOnlyofficeIssueJwtUrl(issueJwtRaw);
          const res = await fetch(issueUrl, {
            method: 'POST',
            headers,
            // Nur JSON + ggf. X-Arkorbit-Onlyoffice-Bridge — keine Browser-Cookies an n8n nötig.
            credentials: 'omit',
            body: JSON.stringify({ onlyofficeConfig }),
            signal: abortController.signal,
          });

          let data: Record<string, unknown> = {};
          try {
            data = (await res.json()) as Record<string, unknown>;
          } catch {
            logPortalError('OnlyOffice JWT', res.status);
            throw new Error(formatOnlyofficeJwtErrorForUser(res));
          }

          const editorUrl =
            (typeof data.editorUrl === 'string' && data.editorUrl) ||
            (typeof data.htmlUrl === 'string' && data.htmlUrl) ||
            '';

          if (editorUrl) {
            if (!cancelled) {
              setOoUi({ kind: 'iframe', src: editorUrl });
              setOoAppReady(true);
            }
            return;
          }

          const ok = data.ok === true;
          const token = typeof data.token === 'string' ? data.token : '';
          const errStr = typeof data.error === 'string' ? data.error : '';

          if (!res.ok || !ok || !token) {
            if (res.status === 429) {
              portalOnlyofficeRateLimitMark429(jwtRateKey);
            }
            throw new Error(formatOnlyofficeJwtFetchError(res, errStr));
          }

          await loadOnlyOfficeDocsApiScript(dsBase);
          if (cancelled || abortController.signal.aborted) return;

          const mount = await waitForOnlyofficeEditorMount(() => editorMountRef.current);
          if (cancelled || abortController.signal.aborted) return;
          if (surface === 'akte') {
            const heightDeadline = Date.now() + 2000;
            while (mount.clientHeight < 280 && Date.now() < heightDeadline) {
              if (cancelled || abortController.signal.aborted) return;
              await new Promise<void>((resolve) => {
                requestAnimationFrame(() => resolve());
              });
            }
          }

          mount.innerHTML = '';
          const inner = document.createElement('div');
          inner.id = placeholderIdRef.current;
          inner.style.width = '100%';
          inner.style.height = '100%';
          inner.style.minHeight = '100%';
          mount.appendChild(inner);

          if (!window.DocsAPI?.DocEditor) {
            throw new Error('DocsAPI.DocEditor nicht verfügbar');
          }

          const fullConfig: Record<string, unknown> = {
            ...onlyofficeConfig,
            token,
            width: '100%',
            height: '100%',
            events: {
              // Erst jetzt darf der Synthese-Pfad createConnector verwenden.
              // Die DocEditor-Instanz existiert schon vorher, die App im iframe aber noch nicht.
              onAppReady: () => {
                window.setTimeout(() => {
                  if (!cancelled && editorInstanceRef.current) {
                    onlyofficeEditorInstanceRef.current = editorInstanceRef.current;
                    onlyofficeAppReadyDocumentIdRef.current = activeDocTabId;
                    setOoAppReady(true);
                    if (import.meta.env.DEV) {
                      console.debug('[portal-synthesis] OnlyOffice onAppReady');
                    }
                  }
                }, 0);
              },
              onRequestClose: () => {
                editorInstanceRef.current?.destroyEditor?.();
                if (onlyofficeEditorInstanceRef.current === editorInstanceRef.current) {
                  onlyofficeEditorInstanceRef.current = null;
                }
                if (onlyofficeAppReadyDocumentIdRef.current === activeDocTabId) {
                  onlyofficeAppReadyDocumentIdRef.current = null;
                }
                editorInstanceRef.current = null;
              },
              // Unsaved-Guard: data === true → Dokument hat ungespeicherte Änderungen.
              onDocumentStateChange: (event?: { data?: unknown }) => {
                const isDirty = event?.data === true;
                setOnlyofficeTabDirty(activeDocTabId, isDirty);
                if (ooWasDirtyRef.current && !isDirty) {
                  scheduleOnlyofficeExtractRefresh();
                }
                ooWasDirtyRef.current = isDirty;
              },
            },
          };

          const inst = new window.DocsAPI.DocEditor(placeholderIdRef.current, fullConfig);
          editorInstanceRef.current = inst;

          if (cancelled) {
            if (onlyofficeEditorInstanceRef.current === inst) {
              onlyofficeEditorInstanceRef.current = null;
            }
            closeOnlyofficeEditorInstance(inst, { preferDestroy: true });
            return;
          }

          setOoUi({ kind: 'docsapi' });
        } catch (e) {
          if (cancelled || abortController.signal.aborted) return;
          if (e instanceof DOMException && e.name === 'AbortError') return;
          const msg = e instanceof Error ? e.message : String(e);
          setOoUi({ kind: 'error', message: msg });
        }
      };

      setOoAppReady(false);
      setOoUi((prev) => (prev.kind === 'loading' ? prev : { kind: 'loading' }));
      const jwtDebounceTimer = window.setTimeout(() => {
        if (cancelled) return;
        void run();
      }, PORTAL_ONLYOFFICE_JWT_SWITCH_DEBOUNCE_MS);

      return () => {
        cancelled = true;
        abortController.abort();
        window.clearTimeout(jwtDebounceTimer);
        const wasDirty =
          dirtyOnlyofficeTabsRef.current.has(activeDocTabId) || ooWasDirtyRef.current;
        closeOnlyofficeEditorInstance(editorInstanceRef.current, {
          preferDestroy: !wasDirty,
        });
        if (onlyofficeEditorInstanceRef.current === editorInstanceRef.current) {
          onlyofficeEditorInstanceRef.current = null;
        }
        if (onlyofficeAppReadyDocumentIdRef.current === activeDocTabId) {
          onlyofficeAppReadyDocumentIdRef.current = null;
        }
        editorInstanceRef.current = null;
        // requestClose hat (sofern nötig) gespeichert → Flag leeren, sonst bliebe
        // ein inaktiver Tab dauerhaft „dirty" und würde fälschlich warnen.
        setOnlyofficeTabDirty(activeDocTabId, false);
        ooWasDirtyRef.current = false;
      };
    }, [
      splitHydrated,
      hasDsUrl,
      hasIssueJwtUrl,
      dsBase,
      issueJwtRaw,
      bridgeToken,
      customer.id,
      alphaDocReady,
      alphaDocSourceKey,
      alphaDocSourceUrl,
      ONLYOFFICE_CALLBACK_URL,
      setOnlyofficeTabDirty,
      scheduleOnlyofficeExtractRefresh,
      onlyOfficeTheme,
    ]);

    // `plainTextDocumentChrome` und `portalArkoDocumentContext` sind seit
    // 27.05.2026 ebenfalls im outer `CustomerPortal`-Scope (Closure).

    const onlyOfficePaneInner = (() => {
      if (plainTextDocumentChrome) {
        if (nativeTextUi.kind === 'loading' || nativeTextUi.kind === 'off') {
          return (
            <div className="flex flex-1 min-h-0 h-full flex-col items-center justify-center gap-3 bg-slate-950/60">
              <Loader2 className="w-9 h-9 animate-spin text-emerald-400" aria-hidden />
              <span className="text-slate-400 text-xs">Text wird geladen (UTF-8)…</span>
            </div>
          );
        }
        if (nativeTextUi.kind === 'error') {
          return (
            <div className="flex flex-1 min-h-0 h-full flex-col items-start justify-start gap-4 p-4 bg-slate-950/50 text-left">
              <div className="flex items-start gap-2 text-amber-300 text-sm">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
                <div>
                  <p className="font-semibold text-amber-200 m-0 mb-1">Klartext konnte nicht im Portal geladen werden</p>
                  <p className="text-slate-400 m-0 text-xs break-words">{nativeTextUi.message}</p>
                </div>
              </div>
              <a
                href={nativeTextUi.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium px-3 py-2"
              >
                <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                Original in neuem Tab öffnen
              </a>
            </div>
          );
        }
        return (
          <div className="flex min-h-0 h-full w-full flex-col gap-2 p-3 bg-slate-950">
            <p className="m-0 text-[11px] leading-snug text-slate-500 shrink-0">
              Leseansicht · zum Bearbeiten im Editor bitte Word-Format (.docx) nutzen
            </p>
            <textarea
              readOnly
              spellCheck={false}
              value={nativeTextUi.text}
              className="min-h-0 flex-1 w-full resize-none rounded-lg border border-slate-700/90 bg-slate-900/95 p-3 font-mono text-[13px] leading-relaxed text-slate-100 focus:outline-none focus:ring-1 focus:ring-emerald-600/40"
              aria-label={alphaDocSource?.title ?? 'Dokument'}
            />
          </div>
        );
      }

      if (!hasDsUrl) {
        return (
          <div className={`flex flex-1 min-h-0 h-full flex-col items-center justify-center px-6 py-10 text-center gap-4 ${PORTAL_SHELL.ooCanvas}/40`}>
            <FilePenLine className="w-12 h-12 text-slate-600" aria-hidden />
            <p className="text-slate-300 text-sm font-medium m-0 max-w-md leading-relaxed">
              Der Dokument-Editor ist derzeit nicht verfügbar. ARKO rechts können Sie trotzdem nutzen.
            </p>
            <a
              href="mailto:office@arkorbit.at?subject=Portal%20Dokument-Editor"
              className="text-blue-400 text-sm hover:text-blue-300 transition-colors"
            >
              Support kontaktieren
            </a>
          </div>
        );
      }

      if (!hasIssueJwtUrl) {
        return (
          <div className={`flex flex-1 min-h-0 h-full flex-col items-center justify-center px-6 py-10 text-center gap-3 ${PORTAL_SHELL.ooCanvas}/40`}>
            <AlertTriangle className="w-10 h-10 text-amber-500/90 flex-shrink-0" aria-hidden />
            <p className="text-slate-300 text-sm font-medium m-0 max-w-md leading-relaxed">
              Der Editor konnte nicht initialisiert werden. Bitte Seite neu laden oder uns kurz Bescheid geben.
            </p>
          </div>
        );
      }

      if (ooUi.kind === 'error') {
        return (
          <div className="flex flex-1 min-h-0 h-full flex-col items-start justify-start px-4 py-6 gap-3 bg-slate-950/50 text-left">
            <div className="flex items-start gap-2 text-red-300 text-sm leading-relaxed">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
              <div>
                <p className="font-semibold text-red-200 m-0 mb-1">OnlyOffice konnte nicht gestartet werden</p>
                <p className="text-slate-400 m-0 text-xs break-words">{ooUi.message}</p>
              </div>
            </div>
          </div>
        );
      }

      if (ooUi.kind === 'iframe') {
        return (
          <div className="relative h-full min-h-0 w-full min-w-0 flex-1 bg-slate-950">
            <iframe
              title="OnlyOffice Document Server (Alpha)"
              data-testid="portal-oo-iframe"
              src={ooUi.src}
              className="absolute inset-0 block !h-full !w-full !max-w-none border-0 bg-slate-950"
              referrerPolicy="no-referrer"
            />
          </div>
        );
      }

      const showSpinner = ooUi.kind === 'loading' || ooUi.kind === 'inactive';

      return (
        <div className="relative flex flex-1 min-h-0 h-full flex-col bg-slate-950">
          {showSpinner && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center z-10 gap-3 bg-slate-950/90"
              aria-busy="true"
              aria-live="polite"
            >
              <Loader2 className="w-9 h-9 animate-spin text-blue-400" aria-hidden />
              <span className="text-slate-500 text-xs">JWT und Editor werden geladen…</span>
            </div>
          )}
          <div
            ref={editorMountRef}
            className={`flex h-full min-h-0 w-full min-w-0 flex-1 flex-col basis-0 ${ooUi.kind === 'docsapi' ? '' : 'opacity-0'}`}
          />
        </div>
      );
    })();

    useEffect(() => {
      if (!workModeOpen && surface !== 'akte') return;
      const timer = window.setTimeout(() => notifyOnlyOfficePaneResize(), 150);
      return () => window.clearTimeout(timer);
    }, [workModeOpen, surface, notifyOnlyOfficePaneResize, alphaDocSourceKey, ooUi.kind]);

    const toggleWorkMode = useCallback(() => {
      onWorkModeChange(!workModeOpen);
    }, [workModeOpen, onWorkModeChange]);

    const workModeBtnClass =
      'portal-shell-doc-mode inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-slate-400 hover:text-blue-300 hover:bg-white/[0.06] transition-colors flex-shrink-0';

    if (surface === 'akte') {
      return (
        <div
          data-testid="portal-oo-akte"
          data-portal-oo-surface="akte"
          data-akte-id={akteId}
          data-akte-title={akteTitle}
          data-portal-document-split-id={activeDocumentId ?? ''}
          className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden"
        >
          <div className={`portal-onlyoffice-shell flex min-h-0 h-full w-full flex-1 flex-col overflow-hidden ${PORTAL_SHELL.ooCanvas}`}>
            {onlyOfficePaneInner}
          </div>
        </div>
      );
    }

    return (
      <div
        data-testid="portal-oo-split"
        data-portal-oo-surface="kanzlei"
        data-portal-document-split-id={activeDocumentId ?? ''}
        data-portal-document-editor-active="true"
        data-portal-oo-ready={
          ooUi.kind === 'iframe' || (ooUi.kind === 'docsapi' && ooAppReady) ? 'true' : undefined
        }
        tabIndex={-1}
        className={`portal-shell-doc flex min-h-0 w-full flex-1 animate-fade-in flex-col ${
          workModeOpen ? 'gap-0 h-full' : 'gap-2'
        }`}
      >
        {showDocIngestCard && !workModeOpen && (
          <div className={`portal-shell-doc-bar flex-shrink-0 ${PORTAL_SHELL.ingestBar}`}>
            <button
              type="button"
              onClick={() => setDocIngestOpen(v => !v)}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors"
              aria-expanded={docIngestOpen}
            >
              <Upload className="w-4 h-4 text-emerald-400 flex-shrink-0" aria-hidden />
              <span className="text-sm font-medium text-slate-200">Dokument laden</span>
              {alphaDocSource?.title && (
                <span className="text-xs text-slate-500 truncate hidden sm:inline ml-1">
                  · {alphaDocSource.title}
                </span>
              )}
              <ChevronRight
                className={`w-4 h-4 text-slate-500 ml-auto transition-transform ${docIngestOpen ? 'rotate-90' : ''}`}
                aria-hidden
              />
            </button>

            {docIngestOpen && (
              <div className="px-4 pb-4 pt-1 border-t border-white/[0.08] space-y-3">
                {canPortalDocumentUpload && (
                  <form onSubmit={handlePortalDocumentUpload} className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <input
                      type="file"
                      name="file"
                      accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.md,.markdown,.html,.htm,.csv,.xls,.xlsx,.ods,.ppt,.pptx,.odp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown,text/plain,text/html,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                      required
                      className="flex-1 min-w-0 text-xs text-slate-400 file:mr-2 file:rounded-lg file:border-0 file:bg-slate-700 file:px-3 file:py-1.5 file:text-slate-200 file:text-xs file:font-medium"
                    />
                    <button
                      type="submit"
                      disabled={docUploadBusy}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 whitespace-nowrap shrink-0"
                    >
                      {docUploadBusy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
                      Öffnen
                    </button>
                  </form>
                )}

                {docUploadMsg && (
                  <p className={`text-xs m-0 ${docUploadMsg.includes('wird geöffnet') || docUploadMsg.startsWith('Gespeichert') ? 'text-emerald-400' : 'text-amber-300'}`}>
                    {docUploadMsg}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div
          ref={splitContainerRef}
          style={{ '--doc-arko-left-pct': `${leftSplitPct}%` } as React.CSSProperties}
          className={`relative ${PORTAL_SHELL.splitPane} ${
            workModeOpen
              ? 'flex-1 min-h-0 rounded-none border-0 bg-slate-950 lg:min-h-0'
              : ''
          }`}
        >
          <div
            ref={splitDragShieldRef}
            className="portal-split-drag-shield absolute inset-0 z-[15] hidden cursor-col-resize lg:block"
            style={{ display: 'none' }}
            aria-hidden
          />
          <div
            ref={splitDragGuideRef}
            className="portal-split-drag-guide pointer-events-none absolute top-0 bottom-0 z-20 hidden w-0.5 -translate-x-1/2 bg-blue-500/75 shadow-[0_0_10px_rgba(59,130,246,0.45)] lg:block"
            style={{ display: 'none', left: 0 }}
            aria-hidden
          />
          <section
            className={`flex flex-col min-h-0 min-w-0 w-full border-b lg:border-b-0 lg:border-r-0 border-white/[0.06] ${
              arkoOpen && !arkoPanelCollapsed
                ? 'lg:w-[var(--doc-arko-left-pct)] lg:max-w-[var(--doc-arko-left-pct)] lg:flex-shrink-0'
                : 'lg:flex-1'
            }`}
            aria-label={plainTextDocumentChrome ? 'Dokument · Leseansicht' : 'Dokument · Editor'}
          >
            <div className={`portal-shell-doc-bar ${PORTAL_SHELL.paneHeader}`}>
              <FilePenLine className="w-3 h-3 text-blue-400 flex-shrink-0" aria-hidden />
              <span className="font-medium text-slate-300 truncate flex-1 min-w-0">
                {alphaDocSource?.title ?? 'Dokument'}
              </span>
              {!plainTextDocumentChrome && (
                <OnlyofficeThemeSwitch
                  value={onlyOfficeTheme}
                  onChange={setOnlyOfficeTheme}
                  className="hidden sm:flex"
                />
              )}
              <button
                type="button"
                onClick={toggleWorkMode}
                className={workModeBtnClass}
                title={workModeOpen ? 'Arbeitsmodus beenden (Esc)' : 'Arbeitsmodus — volle Bildschirmfläche'}
                aria-pressed={workModeOpen}
                aria-label={workModeOpen ? 'Arbeitsmodus beenden' : 'Arbeitsmodus aktivieren'}
              >
                {workModeOpen ? (
                  <>
                    <Minimize2 className="w-3 h-3" aria-hidden />
                    <span className="hidden sm:inline">Beenden</span>
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-3 h-3" aria-hidden />
                    <span className="hidden sm:inline">Arbeitsmodus</span>
                  </>
                )}
              </button>
            </div>
            <FristApprovalDocumentMirror
              nextcloudPath={alphaDocSource?.nextcloudPathFromPb}
              nextcloudWebdavPath={alphaDocSource?.nextcloudWebdavPath}
              pbRecordId={alphaDocSource?.pbRecordId}
              documentDisplayName={
                alphaDocSource?.portalDocumentDisplayName?.trim() ||
                alphaDocSource?.title?.trim() ||
                null
              }
            />
            <div className={`portal-onlyoffice-shell flex min-h-0 h-full w-full flex-1 flex-col overflow-hidden ${PORTAL_SHELL.ooCanvas}`}>
              {onlyOfficePaneInner}
            </div>
          </section>

          {arkoOpen && !arkoPanelCollapsed && (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Breite zwischen Dokument und ARKO anpassen"
              onMouseDown={handleSplitDividerMouseDown}
              className="hidden lg:flex w-1 min-w-[4px] flex-shrink-0 self-stretch cursor-col-resize relative z-10 bg-white/[0.04] hover:bg-blue-600/35 transition-colors"
            />
          )}

          {!arkoOpen ? null : arkoPanelCollapsed ? (
            <aside className={PORTAL_SHELL.arkoCollapsedStrip} aria-label="ARKO eingeklappt">
              <button
                type="button"
                onClick={() => setArkoPanelCollapsed(false)}
                className="p-1.5 rounded-md text-slate-400 hover:text-blue-300 hover:bg-white/[0.06] transition-colors"
                title="ARKO-Panel öffnen"
                aria-label="ARKO-Panel öffnen"
              >
                <PanelRightOpen className="w-4 h-4" aria-hidden />
              </button>
              <MessageSquare className="w-4 h-4 text-blue-400/70" aria-hidden />
            </aside>
          ) : (
            <section
              className={`portal-shell-doc-side portal-arko-pane flex min-h-0 min-w-0 w-full flex-col ${PORTAL_SHELL.ooCanvas}/30 lg:flex-1 lg:min-w-0`}
              aria-label="ARKO"
            >
              <div className={`portal-shell-doc-bar ${PORTAL_SHELL.paneHeader}`}>
                <MessageSquare className="w-3 h-3 text-blue-400 flex-shrink-0" aria-hidden />
                <span className="font-medium text-slate-200 truncate flex-1 min-w-0">ARKO</span>
                <button
                  type="button"
                  onClick={() => setArkoPanelCollapsed(true)}
                  className="hidden lg:inline-flex p-0.5 rounded text-slate-500 hover:text-slate-300 hover:bg-white/[0.06] transition-colors"
                  title="ARKO-Panel einklappen"
                  aria-label="ARKO-Panel einklappen"
                >
                  <PanelRightClose className="w-3.5 h-3.5" aria-hidden />
                </button>
              </div>
              <div className="flex flex-1 flex-col min-h-0 px-1 py-0.5">
                <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
                  <PortalArkoChatWorkspace
                    customerId={customer.id}
                    customerStatus={customer.status}
                    customerEmail={customer.email}
                    webhookUrl={portalArkoWebhook}
                    title="ARKO"
                    subtitle={`Kanzlei-Assistent · ${customer.kanzlei}`}
                    initialBotMessage={portalArkoInitialBotMessage}
                    accentColor="blue"
                    mode="anwalts_arko"
            judicatureOpen={judicatureOpen}
            backgroundAnalysisOpen={hintergrundOpen}
                    showQuickActions
                    showRechtsfrageQuickAction
                    followAppearance
                    hideChatHeader
                    compactChrome
                    layoutVariant="compact"
                    cursorCustomerName={customer.name}
                    cursorKanzlei={customer.kanzlei}
                    chatShellClassName="rounded-none border-0 shadow-none min-h-0 bg-transparent"
                    extraBody={portalWebhookExtraBody}
                    documentContext={portalArkoDocumentContextForChat}
                    documentDisplayName={portalDocumentDisplayName}
                    defaultDocumentContextEnabled={defaultDocumentContextEnabled}
                    documentExtractFingerprint={portalDocumentExtractFingerprint}
                    documentExtractRevision={onlyofficeExtractRevision}
                    quellDokumentNcPath={portalDocumentNcPath}
                    quellDokumentPbId={alphaDocSource?.pbRecordId ?? null}
                    akteNcPath={portalDocumentAkteNcPath}
                    boundAkte={boundAkte}
                    freeDocumentColumn={!boundAkte || !focusBelongsToBoundAkte}
                    akten={boundAkteOptions}
                    akteDocumentCount={akteDocumentCount}
                    onBoundAkteChange={handleBoundAkteChange}
                    akteDocsPanelOpenNonce={akteDocsPanelOpenNonce}
                    onOpenAkteDocument={onOpenAkteDocument}
                    onRenameAkteDocument={onRenameAkteDocument}
                    canWriteAkteDocuments={canWriteAkteDocuments}
                    activeDocumentId={arkoActiveDocumentId}
                    activeDocumentFullExtract={portalActiveDocumentExtract}
                    openDocumentsMeta={portalArkoOpenDocumentsMeta}
                    availableDocuments={portalArkoAvailableDocuments}
                    onDocumentSelectionControllerChange={onDocumentSelectionControllerChange}
                    onSelectedDocumentIdsChange={onSelectedDocumentIdsChange}
                    extractingDocumentIds={extractingDocumentIds}
                    enableContractIntent={isPremium && contractGeneratorEnabled}
                    onContractGenerate={generateContractFromChat}
                    onExportSchreibenPdf={ARKO_SCHREIBEN_PDF_URL ? exportSchreibenPdf : undefined}
                    onExportSchreibenDocx={PORTAL_DOCUMENT_UPLOAD_URL ? exportSchreibenDocx : undefined}
                    onOpenPortalDocument={registerPortalDocumentFromFields}
                    onApplySynthesisToDocument={applySynthesisToDocument}
                    synthesisHasOpenDocument={hasActivePortalDocument}
                    memcore={memcoreChat}
                    onAnalyzeDocumentsReady={handleAnalyzeDocumentsIngested}
                    paragraphIndexMap={portalParagraphIndexMap}
                    onMarkDocumentAnalyzed={handleMarkPortalDocumentAnalyzed}
                    authToken={getPortalPbToken()}
                  />
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    );
      },
    [],
  );

  // ── Haupt-Render ───────────────────────────────────────────────────────────

  portalDocumentEditorScopeRef.current = {
    customer,
    alphaDocSource,
    alphaDocReady,
    plainTextDocumentChrome,
    nativeTextUi,
    pinAlphaDocSource,
    mandantenRoot,
    setOnlyofficeTabDirty,
    scheduleOnlyofficeExtractRefresh,
    portalArkoDocumentContext,
    portalParagraphIndexMap,
    handleMarkPortalDocumentAnalyzed,
    portalDocumentDisplayName,
    defaultDocumentContextEnabled,
    portalDocumentExtractFingerprint,
    onlyofficeExtractRevision,
    portalDocumentNcPath,
    portalDocumentAkteNcPath,
    portalArkoWebhook,
    portalArkoInitialBotMessage,
    portalWebhookExtraBody,
    isPremium,
    contractGeneratorEnabled,
    generateContractFromChat,
    exportSchreibenPdf,
    exportSchreibenDocx,
    applySynthesisToDocument,
    onlyofficeEditorInstanceRef,
    akteDocSource,
    akteDocReady,
    akteOnlyofficeEditorInstanceRef,
    closeAkteOnlyoffice,
    memcoreChat,
    activeDocumentId,
    portalOpenDocumentsMeta,
    portalAvailableDocuments,
    portalArkoDocumentContextForChat,
    portalArkoOpenDocumentsMeta,
    portalArkoAvailableDocuments,
    portalActiveDocumentExtract,
    arkoActiveDocumentId,
    boundAkte,
    boundAkteOptions,
    akteDocumentCount: akteDocumentsForBinding.length,
    handleBoundAkteChange,
    akteDocsPanelOpenNonce,
    onOpenAkteDocument: openAkteDocumentFromChat,
    onRenameAkteDocument: Boolean(PORTAL_DOCUMENT_FOLDER_URL) ? renameAkteDocumentFromChat : undefined,
    canWriteAkteDocuments: canWritePortalDocuments(customer),
    onDocumentSelectionControllerChange: handleDocumentSelectionControllerChange,
    onSelectedDocumentIdsChange: handleSelectedDocumentIdsChange,
    extractingDocumentIds: akteLazyExtractBusyIds,
    onlyOfficeTheme,
    setOnlyOfficeTheme,
    registerPortalDocumentFromFields: openPortalDocumentInEditor,
  };

  const readAnalysisDocument = useCallback((documentId: string) => {
    const id = documentId.trim();
    if (!id) return null;
    return readableAnalysisDocument({
      documentId: id,
      live: docContextCache[id],
      persisted: loadAkteExtractCacheStore(customer.id).byId[id] ?? null,
    });
  }, [customer.id, docContextCache]);

  const portalCenterViews = (
    <>
      {(view === 'akten' || (view === 'chat' && arkoOpen)) ? (
        <AkteArkoChatMount view={view}>
          <PortalArkoChatPane customerId={customer.id}>
          <PortalArkoChatWorkspace
            customerId={customer.id}
            customerStatus={customer.status}
            customerEmail={customer.email}
            webhookUrl={portalArkoWebhook}
            title="ARKO"
            subtitle={`Kanzlei-Assistent · ${customer.kanzlei}`}
            initialBotMessage={portalArkoInitialBotMessage}
            accentColor="purple"
            mode="anwalts_arko"
            judicatureOpen={judicatureOpen}
            backgroundAnalysisOpen={hintergrundOpen}
            showQuickActions
            showRechtsfrageQuickAction
            layoutVariant="cursor"
            followAppearance={view === 'chat' || view === 'akten'}
            cursorCustomerName={customer.name}
            cursorKanzlei={customer.kanzlei}
            chatShellClassName="rounded-none border-0 shadow-none min-h-0 bg-transparent flex-1"
            extraBody={portalWebhookExtraBody}
            documentContext={portalArkoDocumentContextForChat}
            documentDisplayName={portalDocumentDisplayName}
            defaultDocumentContextEnabled={defaultDocumentContextEnabled}
            documentExtractFingerprint={portalDocumentExtractFingerprint}
            documentExtractRevision={onlyofficeExtractRevision}
            quellDokumentNcPath={portalDocumentNcPath}
            quellDokumentPbId={alphaDocSource?.pbRecordId ?? null}
            akteNcPath={portalDocumentAkteNcPath}
            boundAkte={boundAkte}
            akten={boundAkteOptions}
            akteDocumentCount={akteDocumentsForBinding.length}
            onBoundAkteChange={handleBoundAkteChange}
            akteDocsPanelOpenNonce={akteDocsPanelOpenNonce}
            onOpenAkteDocument={openAkteDocumentFromChat}
            onRenameAkteDocument={Boolean(PORTAL_DOCUMENT_FOLDER_URL) ? renameAkteDocumentFromChat : undefined}
            canWriteAkteDocuments={canWritePortalDocuments(customer)}
            activeDocumentId={arkoActiveDocumentId}
            activeDocumentFullExtract={portalActiveDocumentExtract}
            openDocumentsMeta={portalArkoOpenDocumentsMeta}
            availableDocuments={portalArkoAvailableDocuments}
            onDocumentSelectionControllerChange={handleDocumentSelectionControllerChange}
            onSelectedDocumentIdsChange={handleSelectedDocumentIdsChange}
            extractingDocumentIds={akteLazyExtractBusyIds}
            enableContractIntent={isPremium && contractGeneratorEnabled}
            onContractGenerate={generateContractFromChat}
            onExportSchreibenPdf={ARKO_SCHREIBEN_PDF_URL ? exportSchreibenPdf : undefined}
            onExportSchreibenDocx={PORTAL_DOCUMENT_UPLOAD_URL ? exportSchreibenDocx : undefined}
            onOpenPortalDocument={openPortalDocumentInEditor}
            onApplySynthesisToDocument={applySynthesisToDocument}
            synthesisHasOpenDocument={hasActivePortalDocument}
            memcore={memcoreChat}
            onActiveIntentChange={setLastArkoIntent}
            onAnalyzeDocumentsReady={handleAnalyzeDocumentsIngested}
            paragraphIndexMap={portalParagraphIndexMap}
            onMarkDocumentAnalyzed={handleMarkPortalDocumentAnalyzed}
            authToken={getPortalPbToken()}
            chatHome={view === 'akten' ? 'akte' : 'haupt'}
          />
          </PortalArkoChatPane>
        </AkteArkoChatMount>
      ) : null}

      {view === 'home' && (
        <div className="flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden">
        <HomeView
          customerName={customer.name}
          kanzlei={customer.kanzlei}
          isPremium={isPremium}
          matrixRoomId={customer.matrixRoomId}
          elementSeat={customer.elementSeat === true}
          recentItems={portalHomeRecents}
          onNavigate={handlePortalNavigate}
          onRecentItemClick={handleRecentItemClick}
          onArkoModeSelect={handleArkoHubModeSelect}
          arkoOpen={arkoOpen}
          diktatOpen={diktatOpen}
        />
        </div>
      )}

      {view === 'kalender' && (
        <div className="w-full min-w-0 max-w-none flex flex-1 flex-col min-h-0 h-full overflow-hidden">
          <KalenderView
            customerId={customer.id}
            kanzleiId={customer.kanzlei_id}
            authToken={getPortalPbToken()}
            akten={portalAktenList.map((akte) => ({ id: akte.id, title: akte.title }))}
            defaultAkteId={boundAkte?.id ?? null}
          />
        </div>
      )}

      {view === 'suche' && sucheOpen && (
        <SuchePaneBoundary extraBody={portalWebhookExtraBody}>
        <SucheView webhookUrl={portalArkoWebhook} extraBody={portalWebhookExtraBody} seat={bausteinSeat}
        />
        </SuchePaneBoundary>
      )}

      {view === 'diktat' && (
        diktatOpen && (
          <div className="w-full min-w-0 max-w-none flex flex-1 flex-col min-h-0 h-full overflow-hidden">
          <DiktatPaneBoundary>
          <DiktatView
            activeTab={diktatTab}
            onTabChange={setDiktatTab}
            onOpenChat={() => {
              if (arkoOpen) setView('chat');
            }}
            diktatFile={diktatFile}
            onDiktatFileChange={setDiktatFile}
            diktatUploading={diktatUploading}
            onDiktatUpload={handleDiktatUpload}
            onDiktatDroppedAudio={handleDiktatDroppedAudio}
            diktatResult={
              diktatResult && diktatAnalysisQueueHint
                ? { ...diktatResult, analysisBlockedReason: diktatAnalysisQueueHint }
                : diktatResult
            }
            onDiktatResultClear={() => {
              setDiktatResult(null);
              setDiktatAnalysisQueueHint(null);
              setSelectedDiktatArchiveId(null);
              setDiktatIntoAkteNote(null);
            }}
            onShowInDokumente={handleShowDiktatInDokumente}
            dokumenteCta={diktatDokumenteCta}
            archive={diktatArchive}
            selectedArchiveId={selectedDiktatArchiveId}
            onSelectArchive={(id) => {
              const entry = diktatArchive.find((row) => row.id === id);
              if (!entry) return;
              setSelectedDiktatArchiveId(id);
              setDiktatResult({
                type: 'success',
                pdfUrl: entry.pdfUrl,
                message: entry.transcriptText.trim()
                  ? 'Transkript aus Diktate.'
                  : 'Datei aus PocketBase — Transkript nur in der Sitzung sichtbar.',
                pb_record_id: entry.pb_record_id,
                transcriptText: entry.transcriptText || undefined,
                auswertungText: entry.auswertungText,
              });
              setDiktatAnalysisQueueHint(null);
            }}
            protokolle={protokolle}
            akten={portalAktenList}
            canWriteDocuments={canWritePortalDocuments(customer)}
            isPremium={isPremium}
            authToken={getPortalPbToken()}
            customerId={customer.id}
            kanzleiId={customer.kanzlei_id || ''}
            customerStatus={customer.status}
            customerEmail={customer.email}
            protokolleLoading={protokolleLoading}
            onRefreshDocuments={() => {
              void loadProtokolle();
            }}
            onOpenDocument={openPortalDocumentInEditor}
            onChooseOutputFormat={handleDiktatOutputFormat}
            formatBusy={diktatFormatBusy}
            formatError={diktatFormatError}
            intoAkteId={diktatIntoAkteId}
            onIntoAkte={() => {
              void handleDiktatIntoAkte();
            }}
            intoAkteBusy={diktatIntoAkteBusy}
            intoAkteNote={diktatIntoAkteNote}
            matrixRoomId={customer.matrixRoomId}
            elementLoad={diktatElementLoad}
            analysisSlot={
              <DiktatAnalysisIsland
                customerId={customer.id}
                analysisOpen={hintergrundOpen}
                start={diktatAnalysisStart}
                onConsumed={() => setDiktatAnalysisStart(null)}
                onQueueFull={setDiktatAnalysisQueueHint}
              />
            }
          />
          </DiktatPaneBoundary>
          </div>
        )
      )}

        {view === 'matrix' && customer.elementSeat === true && (
          <div className="w-full min-w-0 max-w-none flex flex-1 flex-col min-h-0 h-full overflow-hidden">
            <MatrixView
              matrixRoomId={customer.matrixRoomId}
              seatMxid={customer.matrixUserId}
              seatId={customer.id}
              kanzleiId={customer.kanzlei_id}
              canManageRooms={teamAccessEnabled}
              onOpenDiktatAnleitung={() => {
                setDiktatTab('anleitung');
                setView('diktat');
              }}
            />
          </div>
        )}

            {isDokumenteWorkspaceVisible && (
          <div
            className={
              isDokumenteEditorPaneVisible
                ? docWorkModeOpen
                  ? 'flex flex-1 flex-col min-h-0'
                  : 'flex flex-1 flex-col min-h-0 gap-3'
                : 'w-full min-w-0 max-w-none space-y-3'
            }
          >
            {hasActivePortalDocument && !docWorkModeOpen && (
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <DokumenteAreaTabs
                  activeTab={dokumenteAreaTab}
                  onTabChange={setDokumenteAreaTab}
                  documentTitle={portalDocumentTabTitle}
                />
                {isDokumenteEditorPaneVisible && (
                  <OnlyofficeThemeSwitch value={onlyOfficeTheme} onChange={setOnlyOfficeTheme} />
                )}
              </div>
            )}
            {openDocumentsLimitMsg && isDokumenteWorkspaceVisible && (
              <p className="text-xs text-amber-300 m-0 px-1" role="alert">
                {openDocumentsLimitMsg}
              </p>
            )}
            {isDokumenteEditorPaneVisible && !docWorkModeOpen && openDocuments.length > 0 && (
              <DocumentTabsBar
                documents={documentTabsItems}
                onSelect={selectDocumentTab}
                onClose={closeDocumentTab}
                onAddNew={() => setDokumenteAreaTab('hub')}
                addNewDisabled={openDocuments.length >= PORTAL_MAX_OPEN_DOCUMENTS}
                addNewTitle={
                  openDocuments.length >= PORTAL_MAX_OPEN_DOCUMENTS
                    ? `Maximal ${PORTAL_MAX_OPEN_DOCUMENTS} Dokumente gleichzeitig geöffnet`
                    : 'Weiteres Dokument öffnen'
                }
                showSynthesisSelect={openDocuments.length >= 2}
                onToggleSynthesis={toggleSynthesisDocument}
                synthesisSelectDisabled={synthesisSelectAtMax}
              />
            )}
            {isDokumenteHubMode && (
              <DokumenteView
                customerId={customer.id}
                kanzleiId={customer.kanzlei_id}
                portalRole={customer.portal_role}
                isPrimary={customer.is_primary}
                kanzleiName={customer.kanzlei}
                customerStatus={customer.status}
                customerEmail={customer.email}
                isPremium={isPremium}
                canWriteDocuments={canWritePortalDocuments(customer)}
                protokolle={protokolle}
                protokolleLoading={protokolleLoading}
                protokolleError={protokolleError}
                recentDocs={recentDocs}
                initialFolder={documentHubCurrentFolder}
                onCurrentFolderChange={setDocumentHubCurrentFolder}
                onRefresh={() => {
                  void loadProtokolle();
                }}
                onDocumentIngested={mergeIngestedProtokoll}
                onDocumentRenamed={({ pb_record_id, display_name, nextcloud_path }) => {
                  commitPortalDocumentRename(pb_record_id, display_name, nextcloud_path);
                }}
                onOpenDocument={openPortalDocumentInEditor}
                onRouteAudioToDiktat={handleRouteAudioToDiktat}
                onRecentDocsChange={handleRecentDocsRefresh}
                highlightRecordId={highlightPbRecordId}
                onHighlightClear={() => setHighlightPbRecordId(null)}
                authToken={getPortalPbToken()}
                completedByEmail={customer.email}
                boundAkteId={boundAkte?.id ?? null}
                onBoundAkteChange={handleBoundAkteChange}
                onAktenListChange={handleAktenListChange}
                onOpenAkte={openAkteInHub}
                onOpenAkteInChat={openAkteInArkoChat}
                contractGeneratorAvailable={isPremium && contractGeneratorEnabled}
                hubMode="documents"
                onGoToAkten={() => setView('akten')}
                matrixRoomId={customer.matrixRoomId}
                elementSeat={customer.elementSeat === true}
                seatMxid={customer.matrixUserId || ''}
                arkoBooked={arkoBooked}
                onBindAkteForArko={handleBoundAkteChange}
                onRahmenActiveChange={setAktenRahmenFill}
                readAnalysisDocument={readAnalysisDocument}
                bausteinKategorien={customer.baustein_kategorien}
                onOpenArkoChat={() => setView('chat')}
              />
            )}
            {view === 'document_editor' && isPremium && (
              <div
                className={
                  isDokumenteEditorPaneVisible
                    ? 'flex flex-1 flex-col min-h-0 min-w-0'
                    : 'hidden'
                }
                aria-hidden={!isDokumenteEditorPaneVisible}
              >
                <DocumentEditorAlphaView
                  workModeOpen={docWorkModeOpen}
                  onWorkModeChange={setDocWorkModeOpen}
                />
              </div>
            )}
          </div>
        )}
        {view === 'akten' && (
          <DokumenteView
            customerId={customer.id}
            kanzleiId={customer.kanzlei_id}
            kanzleiName={customer.kanzlei}
            customerStatus={customer.status}
            customerEmail={customer.email}
            isPremium={isPremium}
            canWriteDocuments={canWritePortalDocuments(customer)}
            protokolle={protokolle}
            protokolleLoading={protokolleLoading}
            protokolleError={protokolleError}
            recentDocs={recentDocs}
            initialFolder={documentHubCurrentFolder}
            onCurrentFolderChange={setDocumentHubCurrentFolder}
            onRefresh={() => {
              void loadProtokolle();
            }}
            onDocumentIngested={mergeIngestedProtokoll}
            onDocumentRenamed={({ pb_record_id, display_name, nextcloud_path }) => {
              commitPortalDocumentRename(pb_record_id, display_name, nextcloud_path);
            }}
            onOpenDocument={openPortalDocumentInEditor}
            onOpenAkteOnlyoffice={openAkteOnlyoffice}
            onCloseAkteOnlyoffice={closeAkteOnlyoffice}
            akteOnlyoffice={
              akteDocSource && akteEditorAkteId ? (
                <DocumentEditorAlphaView
                  surface="akte"
                  akteTitle={boundAkte?.id === akteEditorAkteId ? (boundAkte.title || '') : ''}
                  akteId={akteEditorAkteId}
                  workModeOpen={false}
                  onWorkModeChange={() => {}}
                />
              ) : null
            }
            akteOfficeDocs={akteOpenDocs.map((doc) => ({
              id: documentTabId(doc),
              title:
                doc.portalDocumentDisplayName?.trim() ||
                doc.title?.trim() ||
                'Dokument',
            }))}
            akteOfficeActiveId={akteDocSource ? documentTabId(akteDocSource) : ''}
            onSelectAkteOfficeDoc={selectAkteOfficeDoc}
            aktenHomeTick={aktenHomeTick}
            akteSaveFocus={akteSaveFocus}
            onRouteAudioToDiktat={handleRouteAudioToDiktat}
            onRecentDocsChange={handleRecentDocsRefresh}
            highlightRecordId={highlightPbRecordId}
            onHighlightClear={() => setHighlightPbRecordId(null)}
            authToken={getPortalPbToken()}
            completedByEmail={customer.email}
            boundAkteId={boundAkte?.id ?? null}
            onBoundAkteChange={handleBoundAkteChange}
            onAktenListChange={handleAktenListChange}
            onOpenAkte={openAkteInHub}
            onOpenAkteInChat={openAkteInArkoChat}
            contractGeneratorAvailable={isPremium && contractGeneratorEnabled}
            hubMode="akten"
            seedAkten={portalAktenList}
            readAnalysisDocument={readAnalysisDocument}
            onGoToAkten={() => setView('akten')}
            matrixRoomId={customer.matrixRoomId}
            elementSeat={customer.elementSeat === true}
            seatMxid={customer.matrixUserId || ''}
            arkoBooked={arkoBooked}
            onBindAkteForArko={handleBoundAkteChange}
            onRahmenActiveChange={setAktenRahmenFill}
          />
        )}
        {view === 'pdf_tools' && (
          <div className="w-full min-w-0 max-w-none flex flex-1 flex-col min-h-0 h-full overflow-hidden">
            <PdfToolsView />
          </div>
        )}
        {view === 'bausteine' && (
          <div className="flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden">
          <BausteinView
            seat={bausteinSeat}
            onBestellen={(name) => { void bestelleBaustein(name); }}
            onKuendigen={(name, zugestimmt) => { void kuendigeBaustein(name, zugestimmt); }}
            bestellName={bausteinBestellName}
            bestellHinweis={bausteinBestellHinweis}
          />
          </div>
        )}
        {view === 'feedback' && feedbackPanel}
        {view === 'tasten' && (
          <PortalLightSurface
            surface={portalTastenSurfaceClass}
            className="space-y-6 animate-fade-in max-w-2xl mx-auto"
            testId="portal-tasten-pane"
          >
            <div>
              <h1 className="text-2xl font-bold text-white">Tasten</h1>
              <p className="text-slate-400 mt-1">Grundfunktionen dieses Sitzes. Die Belegung bleibt in diesem Browser, nur die Tasten.</p>
            </div>
            <PortalShortcutBelegung seatId={customer.id} />
          </PortalLightSurface>
        )}

        {view === 'kanzlei_zugaenge' && teamAccessEnabled && (
          <div className="flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden">
          <KanzleiZugaengeView
            kanzlei={customer.kanzlei}
            kanzleiId={customer.kanzlei_id}
            customerStatus={customer.status}
            tier={customer.tier}
            authToken={getPortalPbToken()}
            includedSeats={customer.included_seats}
            maxSeats={customer.max_seats}
            onSelfElementSeat={(enabled, matrixUserId) => {
              setCustomer((prev) => {
                const next = {
                  ...prev,
                  elementSeat: enabled,
                  ...(enabled && matrixUserId ? { matrixUserId } : {}),
                };
                persistPortalCustomer(next);
                onCustomerUpdate?.(next);
                return next;
              });
              if (!enabled) {
                setView((current) => (current === 'matrix' ? 'home' : current));
              }
            }}
          />
          </div>
        )}
        {view === 'litigation' && fallanalyseOpen && (
          <div className="flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden">
          <LitigationView
            textMinChars={LITIGATION_TEXT_MIN_CHARS}
            uploadMode={litigationUploadMode}
            onUploadModeChange={setLitigationUploadMode}
            files={files}
            onFilesChange={setFiles}
            schriftsatzText={schriftsatzText}
            onSchriftsatzTextChange={setSchriftsatzText}
            optIn={optIn}
            onOptInChange={setOptIn}
            validationError={litigationValidationError}
            onValidationErrorClear={() => setLitigationValidationError('')}
            uploading={uploading}
            uploadResult={uploadResult}
            onUpload={() => void handleLitigationUpload()}
            customerId={customer.id}
            customerStatus={customer.status}
            customerEmail={customer.email}
            onDocumentReady={openPortalDocumentInEditor}
          />
          </div>
        )}
        {view === 'vertrags_qa' && isPremium && (
          <VertragsQAView
            preisliste={vqaPreisliste}
            onPreislisteChange={setVqaPreisliste}
            gruendungspaket={vqaGruendungspaket}
            onGruendungspaketChange={setVqaGruendungspaket}
            markeIp={vqaMarkeIp}
            onMarkeIpChange={setVqaMarkeIp}
            optIn={vqaOptIn}
            onOptInChange={setVqaOptIn}
            uploading={vqaUploading}
            result={vqaResult}
            onResultClear={() => setVqaResult(null)}
            onUpload={() => void handleVertragQAUpload()}
          />
        )}
        {view === 'contract_generator' && isPremium && contractGeneratorEnabled && (
          <div className="flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden">
          <ContractGeneratorView
            customerId={customer.id}
            customerStatus={customer.status}
            customerEmail={customer.email}
            initialTemplateId={contractCreatorTemplateId}
            onDocumentReady={openGeneratedDocumentInEditor}
            mehrReiter
          />
          </div>
        )}
        {view === 'gedaechtnis' && memcoreEnabled && (
          <MemoryView
            ctx={memcoreCtx}
            curatorUrl={memcoreCuratorUrl}
            onStartOnboarding={() => setMemOnboardingOpen(true)}
          />
        )}

    </>
  );

  const mainMaxWidthClass =
    docWorkModeOpen
      ? 'w-full max-w-none flex flex-1 flex-col min-h-0'
      : isDokumenteEditorPaneVisible
        ? 'max-w-[min(100%,1440px)] flex flex-1 flex-col min-h-0'
        : isDokumenteHubMode || view === 'akten' || view === 'kalender' || view === 'diktat' || view === 'suche' || view === 'pdf_tools' || view === 'matrix' || view === 'gedaechtnis' || view === 'kanzlei_zugaenge' || view === 'litigation' || view === 'contract_generator' || view === 'bausteine'
          ? 'w-full max-w-none min-w-0 flex flex-1 flex-col min-h-0'
          : view === 'chat'
            ? 'w-full max-w-none min-w-0 flex flex-1 flex-col min-h-0'
            : view === 'home'
              ? 'h-full w-full max-w-none min-w-0 flex flex-1 flex-col min-h-0'
              : 'max-w-3xl';

  return (
    <FristApprovalProvider
      customerId={customer.id}
      customerEmail={customer.email}
      customerStatus={customer.status}
    >
    <PortalBackgroundJobsProvider
      customerId={customer.id}
      webhookUrl={portalArkoWebhook}
      extraBody={portalWebhookExtraBody}
      mode="anwalts_arko"
      timeoutMs={PORTAL_ARKO_BACKGROUND_ANALYSIS_TIMEOUT_MS}
    >
    <>
      {mustChangePassword && (
        <MustChangePasswordOverlay
          customer={customer}
          onComplete={(updated) => {
            setCustomer(updated);
            onCustomerUpdate?.(updated);
          }}
          onSessionLost={handleLogout}
        />
      )}
    <PortalShell
      customer={customer}
      elementSeat={customer.elementSeat === true}
      bausteinSeat={bausteinSeat}
      documentCount={protokolle.length}
      view={view}
      isPremium={isPremium}
      contractGeneratorEnabled={contractGeneratorEnabled}
      memcoreEnabled={memcoreEnabled}
      teamAccessEnabled={teamAccessEnabled}
      mobileOpen={mobileOpen}
      onMobileOpenChange={setMobileOpen}
      settingsOpen={settingsOpen}
      onSettingsOpenChange={setSettingsOpen}
      onLogout={handleLogout}
      onNavigate={handlePortalNavigate}
      onSlashFocus={handleSlashFocus}
      fillHeight={isDokumenteEditorPaneVisible || view === 'chat' || view === 'diktat' || view === 'pdf_tools' || view === 'kalender' || view === 'matrix' || view === 'suche' || view === 'gedaechtnis' || view === 'kanzlei_zugaenge' || view === 'litigation' || view === 'contract_generator' || view === 'bausteine' || view === 'home' || (view === 'akten' && aktenRahmenFill)}
      minimalChrome={docWorkModeOpen}
      customerId={customer.id}
      kanzleiId={customer.kanzlei_id}
      merkzettelEnabled={!mustChangePassword}
      settingsPanel={
        <SettingsPanel
          onClose={() => setSettingsOpen(false)}
          onLogout={handleLogout}
          isPremium={isPremium}
          supportsPasskeys={supportsPasskeys}
          passkeys={passkeys}
          pkLoading={pkLoading}
          pkRegistering={pkRegistering}
          pkDeviceName={pkDeviceName}
          onPkDeviceNameChange={setPkDeviceName}
          pkError={pkError}
          pkSuccess={pkSuccess}
          onAddPasskey={handleAddPasskey}
          onDeletePasskey={handleDeletePasskey}
          idAustriaConfigured={idAustriaConfigured}
          idAustriaLinked={idAustriaLinked}
          idAustriaLoading={idAustriaLoading}
          idAustriaBusy={idAustriaBusy}
          idAustriaError={idAustriaError}
          idAustriaMessage={idAustriaMessage}
          onLinkIdAustria={handleLinkIdAustria}
          onUnlinkIdAustria={handleUnlinkIdAustria}
          pwOld={pwOld}
          pwNew={pwNew}
          pwConfirm={pwConfirm}
          onPwOldChange={setPwOld}
          onPwNewChange={setPwNew}
          onPwConfirmChange={setPwConfirm}
          showPwOld={showPwOld}
          showPwNew={showPwNew}
          showPwConfirm={showPwConfirm}
          onToggleShowPwOld={() => setShowPwOld((v) => !v)}
          onToggleShowPwNew={() => setShowPwNew((v) => !v)}
          onToggleShowPwConfirm={() => setShowPwConfirm((v) => !v)}
          pwError={pwError}
          pwSuccess={pwSuccess}
          pwLoading={pwLoading}
          onPasswordChange={handlePasswordChange}
        />
      }
    >
      <AkteArkoHostProvider>
      <div className={`mx-auto w-full min-h-0 ${mainMaxWidthClass} ${aktenRahmenFill && view === 'akten' ? 'h-full' : ''}`}>
        {portalCenterViews}
      </div>
      </AkteArkoHostProvider>

      {memOnboardingOpen && memcoreEnabled && (
        <MemoryOnboardingDialog
          ctx={memcoreCtx}
          curatorUrl={memcoreCuratorUrl}
          onSaved={() => setMemcoreOnboardingDone(customer.id)}
          onClose={(done) => {
            setMemOnboardingOpen(false);
            if (done) setMemcoreOnboardingDone(customer.id);
          }}
        />
      )}
    </PortalShell>
    </>
    </PortalBackgroundJobsProvider>
    </FristApprovalProvider>
  );
};

export default CustomerPortal;
